import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// ── Supabase Storage ─────────────────────────────────────────────────────────
// All data stored in Supabase, scoped per user. Private and secure.

async function load(key, fallback, userId) {
  if (!userId) return fallback;
  try {
    const { data, error } = await supabase
      .from('user_data')
      .select('value')
      .eq('user_id', userId)
      .eq('key', key)
      .single();
    if (error || !data) return fallback;
    return JSON.parse(data.value);
  } catch { return fallback; }
}

async function save(key, val, userId) {
  if (!userId) return;
  try {
    await supabase
      .from('user_data')
      .upsert({ user_id: userId, key, value: JSON.stringify(val), updated_at: new Date().toISOString() },
               { onConflict: 'user_id,key' });
  } catch (e) { console.error('Save error:', e); }
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2); }
function todayKey() { return new Date().toISOString().slice(0, 10); }
function fmt(d) { return new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
function fmtDate(s) { return new Date(s + "T12:00:00").toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }); }
function minsBetween(a, b) { return Math.max(0, Math.round((new Date(b) - new Date(a)) / 60000)); }

async function checkAndGenerateRecurringTasks(templates, currentTasks, userId) {
  if (!templates.length) return currentTasks;
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const dayOfWeek = today.getDay();
  const dayOfMonth = today.getDate();
  const month = today.getMonth() + 1;
  let newTasks = [...currentTasks];
  let changed = false;
  for (const tmpl of templates) {
    const hasUndone = currentTasks.some(t => t.recurringId === tmpl.id && !t.done);
    if (hasUndone) continue;
    const doneToday = currentTasks.some(t => t.recurringId === tmpl.id && t.done && t.doneAt?.startsWith(todayStr));
    if (doneToday) continue;
    let should = false;
    if (tmpl.frequency === "daily") should = true;
    else if (tmpl.frequency === "weekly") should = (tmpl.dayOfWeek === dayOfWeek);
    else if (tmpl.frequency === "monthly") should = (tmpl.dayOfMonth === dayOfMonth);
    else if (tmpl.frequency === "yearly") should = (tmpl.month === month && tmpl.dayOfMonth === dayOfMonth);
    if (!should) continue;
    newTasks = [{
      id: uid(), text: tmpl.text, tag: tmpl.tag, area: tmpl.area,
      urgent: false, when: "today", done: false, doneAt: null,
      estimatedMins: tmpl.estimatedMins || undefined,
      recurringId: tmpl.id, isRecurring: true, frequency: tmpl.frequency,
      order: Date.now(), createdAt: new Date().toISOString(),
    }, ...newTasks];
    changed = true;
  }
  if (changed) await save("tasks", newTasks, userId);
  return newTasks;
}

// ── Life Areas ───────────────────────────────────────────────────────────────
const AREAS = [
  { id: "business",  label: "Business",        color: "#3b82f6", icon: "🏭", desc: "Manufacturing + SaaS" },
  { id: "financial", label: "Financial",        color: "#8b5cf6", icon: "💰", desc: "Income, savings, investments" },
  { id: "potential", label: "Potential",        color: "#f59e0b", icon: "⚡", desc: "Closing the gap, growth" },
  { id: "habits",    label: "Habits",           color: "#10b981", icon: "🔄", desc: "Daily systems & routines" },
  { id: "health",    label: "Health & Energy",  color: "#ef4444", icon: "❤️", desc: "Body, mind, energy" },
  { id: "family",    label: "Family & Kids",    color: "#f472b6", icon: "👨‍👧", desc: "Quality time, teaching, presence" },
  { id: "home",      label: "Home & Food",      color: "#fb923c", icon: "🏠", desc: "Meal planning, household systems" },
  { id: "personal",  label: "Personal",         color: "#a3e635", icon: "🌱", desc: "Hobbies, learning, veg garden" },
];
const AREA_MAP = Object.fromEntries(AREAS.map(a => [a.id, a]));

const HORIZONS = { "7d": "1 Week", "30d": "1 Month", "90d": "3 Months", "1y": "1 Year", "3y": "3 Years", "custom": "Custom..." };

// ── Shared Styles ────────────────────────────────────────────────────────────
const card = { background: "#0f172a", border: "1px solid #1e293b", borderRadius: 14, padding: "18px 20px" };
const inp = {
  width: "100%", background: "#1e293b", border: "1px solid #334155",
  borderRadius: 8, padding: "9px 12px", color: "#f1f5f9", fontSize: 13,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};
const btn = (bg, txt) => ({
  background: bg, border: "none", borderRadius: 8, padding: "9px 18px",
  color: txt || "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer",
  fontFamily: "inherit", whiteSpace: "nowrap", transition: "opacity 0.15s",
});

// ═══════════════════════════════════════════════════════════════════════════════
// GOALS
// ═══════════════════════════════════════════════════════════════════════════════
function getCountdown(g) {
  if (!g.deadline) return null;
  // Compare dates only (ignore time) to avoid timezone issues
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(g.deadline + "T00:00:00");
  end.setHours(0, 0, 0, 0);
  const diff = end - today;
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: "#ef4444", urgent: true };
  if (days === 0) return { label: "Due today!", color: "#ef4444", urgent: true };
  if (days <= 7) return { label: `${days}d left`, color: "#f97316", urgent: true };
  if (days <= 30) return { label: `${days}d left`, color: "#f59e0b", urgent: false };
  if (days <= 365) {
    const months = Math.round(days / 30);
    return { label: `${months}mo left`, color: "#10b981", urgent: false };
  }
  const years = Math.floor(days / 365);
  const remMonths = Math.round((days % 365) / 30);
  return { label: remMonths > 0 ? `${years}yr ${remMonths}mo` : `${years}yr left`, color: "#3b82f6", urgent: false };
}

// ── Goal Dashboard helpers ────────────────────────────────────────────────────
// Whole days between two dates (date-only, timezone-safe). Returns >= 0.
function daysBetween(a, b) {
  if (!a || !b) return 0;
  const d1 = new Date(a); const d2 = new Date(b);
  if (isNaN(d1) || isNaN(d2)) return 0;
  d1.setHours(0, 0, 0, 0); d2.setHours(0, 0, 0, 0);
  return Math.round((d2 - d1) / 86400000);
}

// Most recent activity date for a goal. Milestones carry no timestamp, so the
// best signal available is the newest progress-log entry; fall back to createdAt.
function lastActivityDate(g) {
  let latest = null;
  (g.progressLog || []).forEach(e => {
    if (!e.date) return;
    const d = new Date(e.date);
    if (isNaN(d)) return;
    if (!latest || d > latest) latest = d;
  });
  if (!latest && g.createdAt) { const c = new Date(g.createdAt); if (!isNaN(c)) latest = c; }
  return latest;
}

// Compute health, expected progress, milestone stats and staleness for a goal.
function analyzeGoal(g) {
  const today = new Date();
  const milestones = g.milestones || [];
  const milestonesTotal = milestones.length;
  const milestonesDone = milestones.filter(m => m.done).length;
  const autoProgress = milestonesTotal > 0 ? Math.round((milestonesDone / milestonesTotal) * 100) : null;
  const progress = typeof g.progress === "number" ? g.progress : 0;

  const lastActive = lastActivityDate(g);
  const daysSinceActivity = lastActive ? daysBetween(lastActive, today) : (g.createdAt ? daysBetween(g.createdAt, today) : 0);

  // Expected progress given elapsed time toward the deadline.
  let expected = null;
  if (g.deadline && g.createdAt) {
    const totalDays = daysBetween(g.createdAt, g.deadline);
    const daysPassed = daysBetween(g.createdAt, today);
    if (totalDays > 0) expected = Math.max(0, Math.min(100, Math.round((daysPassed / totalDays) * 100)));
  }

  // Deadline passed and not finished → Overdue (takes priority).
  let overdue = false;
  if (g.deadline) {
    const end = new Date(g.deadline + "T00:00:00");
    const t = new Date(); t.setHours(0, 0, 0, 0);
    if (!isNaN(end) && end < t && progress < 100) overdue = true;
  }

  let health;
  if (overdue) {
    health = { key: "Overdue", icon: "⚫", color: "#64748b" };
  } else if (progress === 0 || daysSinceActivity > 14) {
    health = { key: "Stalled", icon: "🔴", color: "#ef4444" };
  } else if (expected != null) {
    const behind = expected - progress;
    if (behind <= 10) health = { key: "On Track", icon: "🟢", color: "#10b981" };
    else if (behind <= 30) health = { key: "Falling Behind", icon: "🟡", color: "#f59e0b" };
    else health = { key: "Stalled", icon: "🔴", color: "#ef4444" };
  } else {
    // No deadline — judge on progress alone.
    health = progress > 0 ? { key: "On Track", icon: "🟢", color: "#10b981" }
                          : { key: "Stalled", icon: "🔴", color: "#ef4444" };
  }

  const stalled = health.key === "Stalled" || daysSinceActivity > 14;
  return { health, expected, autoProgress, milestonesDone, milestonesTotal, daysSinceActivity, stalled, progress };
}

// ── Circular SVG progress ring ────────────────────────────────────────────────
function ProgressRing({ progress, color, size = 80 }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, progress || 0));
  const strokeDashoffset = circumference - (clamped / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" style={{ display: "block" }}>
      <circle cx="40" cy="40" r={radius} fill="none" stroke="#1e293b" strokeWidth="8" />
      <circle cx="40" cy="40" r={radius} fill="none" stroke={color} strokeWidth="8"
        strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
        transform="rotate(-90 40 40)" style={{ transition: "stroke-dashoffset 0.5s ease" }} />
      <text x="40" y="37" textAnchor="middle" dominantBaseline="middle" fontSize="18" fontWeight="700" fill="#f1f5f9">{clamped}%</text>
      <text x="40" y="52" textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#64748b">complete</text>
    </svg>
  );
}

// ── Dashboard goal card ───────────────────────────────────────────────────────
function DashGoalCard({ g, onOpen, onCoach, emphasize }) {
  const area = AREA_MAP[g.area] || AREAS[0];
  const a = analyzeGoal(g);
  const cd = getCountdown(g);
  const milePct = a.milestonesTotal > 0 ? Math.round((a.milestonesDone / a.milestonesTotal) * 100) : 0;

  // Colour the "last updated" line by staleness.
  const lastColor = a.daysSinceActivity > 14 ? "#ef4444" : a.daysSinceActivity > 7 ? "#f59e0b" : "#10b981";
  const lastLabel = a.daysSinceActivity === 0 ? "Last updated today"
    : `Last updated ${a.daysSinceActivity} day${a.daysSinceActivity === 1 ? "" : "s"} ago`;

  const showBoth = a.autoProgress != null && a.autoProgress !== a.progress;

  return (
    <div onClick={() => onOpen(g.id)}
      style={{
        ...card, cursor: "pointer", padding: emphasize ? "18px 20px" : "16px 18px",
        borderLeft: emphasize ? "4px solid #ef4444" : `4px solid ${area.color}`,
        display: "flex", flexDirection: "column", gap: 12,
        transition: "transform 0.12s, border-color 0.12s",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}>

      {/* Top row: area + goal text + countdown */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", minWidth: 0 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: area.color, flexShrink: 0, marginTop: 5 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: area.color, fontWeight: 700, marginBottom: 3 }}>{area.icon} {area.label}</div>
            <div style={{
              fontSize: 14, fontWeight: 700, color: "#f1f5f9", lineHeight: 1.35,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>{g.text}</div>
          </div>
        </div>
        {cd && (
          <span style={{
            fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 20, flexShrink: 0,
            background: cd.color + "22", color: cd.color, whiteSpace: "nowrap",
          }}>⏱ {cd.label}</span>
        )}
      </div>

      {/* Progress ring + health */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <ProgressRing progress={a.progress} color={area.color} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: a.health.color }}>
          {a.health.icon} {a.health.key}
        </div>
        {showBoth && (
          <div style={{ fontSize: 10, color: "#64748b" }}>
            Manual: {a.progress}% · Milestones: {a.autoProgress}%
          </div>
        )}
      </div>

      {/* Milestone mini bar */}
      {a.milestonesTotal > 0 && (
        <div>
          <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>Milestones: {a.milestonesDone}/{a.milestonesTotal}</div>
          <div style={{ height: 4, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: milePct + "%", height: "100%", background: area.color, borderRadius: 3, transition: "width 0.4s" }} />
          </div>
        </div>
      )}

      {/* Last activity */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 10, color: lastColor }}>{lastLabel}</span>
        {a.health.key === "Stalled" && (
          <button onClick={e => { e.stopPropagation(); onCoach(g.id); }}
            style={{ ...btn("#ef444422", "#ef4444"), padding: "4px 10px", fontSize: 11, border: "1px solid #ef444455" }}>
            ◎ Coach Me
          </button>
        )}
      </div>
    </div>
  );
}

// ── Goal Dashboard ────────────────────────────────────────────────────────────
function GoalDashboard({ goals, onOpen, onCoach }) {
  const analyzed = goals.map(g => ({ g, a: analyzeGoal(g) }));
  const stalledGoals = analyzed.filter(x => x.a.stalled);
  const onTrack = analyzed.filter(x => x.a.health.key === "On Track").length;
  const falling = analyzed.filter(x => x.a.health.key === "Falling Behind").length;
  const stalledCount = stalledGoals.length;

  // Most stalled = longest without activity.
  const mostStalled = stalledGoals.slice().sort((p, q) => q.a.daysSinceActivity - p.a.daysSinceActivity)[0];

  // Area breakdown — only areas that actually have goals.
  const areaStats = AREAS.map(area => {
    const inArea = goals.filter(g => g.area === area.id);
    if (inArea.length === 0) return null;
    const avg = Math.round(inArea.reduce((s, g) => s + (g.progress || 0), 0) / inArea.length);
    return { area, count: inArea.length, avg };
  }).filter(Boolean);

  if (goals.length === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Summary bar */}
      <div style={{ ...card, padding: "14px 18px" }}>
        <div style={{ fontSize: 13, color: "#94a3b8", display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "center" }}>
          <span style={{ fontWeight: 700, color: "#f1f5f9" }}>{goals.length} goal{goals.length === 1 ? "" : "s"}</span>
          <span style={{ color: "#334155" }}>·</span>
          <span style={{ color: "#10b981", fontWeight: 700 }}>{onTrack} on track</span>
          <span style={{ color: "#334155" }}>·</span>
          <span style={{ color: "#f59e0b", fontWeight: 700 }}>{falling} falling behind</span>
          <span style={{ color: "#334155" }}>·</span>
          <span style={{ color: "#ef4444", fontWeight: 700 }}>{stalledCount} stalled</span>
        </div>
        {stalledCount > 0 && mostStalled && (
          <button onClick={() => onCoach(mostStalled.g.id)}
            style={{ ...btn("#ef444422", "#ef4444"), marginTop: 12, border: "1px solid #ef444455", width: "100%", textAlign: "left" }}>
            🔴 {stalledCount} goal{stalledCount === 1 ? " has" : "s have"} stalled. Tap to coach →
          </button>
        )}
      </div>

      {/* Needs attention */}
      {stalledGoals.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#ef4444", letterSpacing: "0.08em", marginBottom: 10 }}>⚠ NEEDS ATTENTION</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {stalledGoals.map(({ g }) => (
              <DashGoalCard key={g.id} g={g} onOpen={onOpen} onCoach={onCoach} emphasize />
            ))}
          </div>
        </div>
      )}

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {analyzed.map(({ g }) => (
          <DashGoalCard key={g.id} g={g} onOpen={onOpen} onCoach={onCoach} />
        ))}
      </div>

      {/* Area breakdown */}
      {areaStats.length > 0 && (
        <div style={{ ...card }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 14 }}>AREA BREAKDOWN</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {areaStats.map(({ area, count, avg }) => (
              <div key={area.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 130, flexShrink: 0, fontSize: 12, color: "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {area.icon} {area.label}
                </div>
                <div style={{ flex: 1, height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: avg + "%", height: "100%", background: area.color, borderRadius: 4, transition: "width 0.4s" }} />
                </div>
                <div style={{ width: 38, flexShrink: 0, textAlign: "right", fontSize: 12, color: area.color, fontWeight: 700 }}>{avg}%</div>
                <div style={{ width: 54, flexShrink: 0, textAlign: "right", fontSize: 10, color: "#64748b" }}>{count} goal{count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GoalCard({ g, goals, setGoals, userId, dayPlans, setDayPlans, tasks, setTasks, autoExpand, autoCoach }) {
  const area = AREA_MAP[g.area] || AREAS[0];
  const [expanded, setExpanded] = useState(false);
  const [aiFeedback, setAiFeedback] = useState(g.aiFeedback || "");
  const [aiFeedbackLoading, setAiFeedbackLoading] = useState(false);
  const [milestoneText, setMilestoneText] = useState("");
  const [logDate, setLogDate] = useState(todayKey());
  const [logValue, setLogValue] = useState("");
  const [logNote, setLogNote] = useState("");
  const goalsRef = useRef(goals);
  const [saveStatus, setSaveStatus] = useState("");
  const [assumptionReview, setAssumptionReview] = useState(g.assumptionReview || "");
  const [assumptionReviewLoading, setAssumptionReviewLoading] = useState(false);
  const [aiPlanLoading, setAiPlanLoading] = useState(false);
  const [aiPlanMsg, setAiPlanMsg] = useState("");
  const [nextAction, setNextAction] = useState(g.nextAction || null);
  const [nextActionLoading, setNextActionLoading] = useState(false);
  const [scheduleAdded, setScheduleAdded] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachMessages, setCoachMessages] = useState([]);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachInput, setCoachInput] = useState("");
  const [coachEnding, setCoachEnding] = useState(false);
  const [coachSummary, setCoachSummary] = useState("");
  const [showPrevSession, setShowPrevSession] = useState(false);
  const [taskSavedMap, setTaskSavedMap] = useState({});
  const chatScrollRef = useRef(null);
  const cardRef = useRef(null);
  useEffect(() => { goalsRef.current = goals; }, [goals]);
  useEffect(() => { setAiFeedback(g.aiFeedback || ""); }, [g.id, g.aiFeedback]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAssumptionReview(g.assumptionReview || ""); }, [g.id, g.assumptionReview]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setNextAction(g.nextAction || null); }, [g.id, g.nextAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to dashboard "open"/"coach" requests: expand, scroll into view, and
  // optionally kick off a coaching session.
  useEffect(() => {
    if (autoExpand || autoCoach) {
      setExpanded(true);
      if (cardRef.current) cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (autoCoach && !coachOpen) openCoach();
  }, [autoExpand, autoCoach]); // eslint-disable-line react-hooks/exhaustive-deps

  async function updateGoal(patch) {
    const latest = goalsRef.current;
    const u = latest.map(x => x.id === g.id ? { ...x, ...patch } : x);
    setGoals(u);
    setSaveStatus("saving");
    await save("goals", u, userId);
    setSaveStatus("saved");
    setTimeout(() => setSaveStatus(""), 2000);
  }

  async function del() {
    const u = goals.filter(x => x.id !== g.id);
    setGoals(u); await save("goals", u, userId);
  }

  async function addMilestone() {
    if (!milestoneText.trim()) return;
    const ms = [...(g.milestones || []), { id: uid(), text: milestoneText.trim(), done: false }];
    await updateGoal({ milestones: ms });
    setMilestoneText("");
  }

  async function toggleMilestone(mid) {
    const ms = (g.milestones || []).map(m => m.id === mid ? { ...m, done: !m.done } : m);
    await updateGoal({ milestones: ms });
  }

  async function delMilestone(mid) {
    const ms = (g.milestones || []).filter(m => m.id !== mid);
    await updateGoal({ milestones: ms });
  }

  async function getAiFeedback() {
    setAiFeedbackLoading(true); setAiFeedback("");
    const prompt = `You are a sharp strategic advisor. This person owns a manufacturing company and an early-stage SaaS, is based in Israel, is vegan, has kids, and wants to close the gap between his potential and his real-life results.

Here is one of their goals:
- Goal: ${g.text}
- Area: ${area.label}
- Timeframe: ${HORIZONS[g.horizon]}
- Why it matters to them: ${g.why || "not specified"}
- How they'll measure success: ${g.success || "not specified"}
- Their strategy: ${g.strategy || "not written yet"}
- Their milestones: ${(g.milestones || []).map(m => m.text).join(", ") || "none yet"}

Give honest, specific feedback structured as:

**IS THIS GOAL REALISTIC?**
Direct assessment. Given the timeframe and their situation, is this achievable?

**WHAT'S STRONG IN THEIR APPROACH**
What's good about their strategy or milestones (if they have any).

**WHAT'S MISSING OR WRONG**
The gaps, blind spots, or mistakes in their plan. Be specific.

**THE FASTEST PATH TO THIS GOAL**
3-4 concrete steps, ordered by priority. What should they do first?

**THE #1 RISK**
What is most likely to stop them from achieving this?

Keep it under 400 words. Be direct, not gentle.`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const d = await res.json();
      if (d.error) {
        setAiFeedback("API error: " + d.error.message);
        setAiFeedbackLoading(false);
        return;
      }
      const text = d.content?.map(b => b.text || "").join("") || "No response.";
      setAiFeedback(text);
      await updateGoal({ aiFeedback: text, aiFeedbackDate: new Date().toISOString() });
    } catch (e) {
      setAiFeedback("Error: " + (e.message || "Please try again."));
    }
    setAiFeedbackLoading(false);
  }

  async function getAssumptionReview() {
    setAssumptionReviewLoading(true); setAssumptionReview("");
    const prompt = `You are a sharp strategic advisor reviewing whether a person's action plan is producing the results they expected.

GOAL: ${g.text}
TARGET: ${g.success || "not specified"}
DEADLINE: ${g.deadline || "not set"}
CURRENT PROGRESS: ${g.progress}%
TIME REMAINING: ${getCountdown(g)?.label || "unknown"}

THEIR ASSUMPTIONS (what they believed would work):
${(g.assumptions || []).map(a => `- Action: ${a.action}\n  Frequency: ${a.frequency}\n  Expected: ${a.expectedResult}\n  Scheduled: ${a.timeBlock || "not scheduled"}`).join('\n') || "No assumptions defined yet."}

ACTUAL RESULTS BY MONTH:
${(g.resultsLog || []).map(r => `${r.month}: ${r.notes || "(no notes)"} (execution score: ${r.score}/10)\n${(r.results || []).map(res => { const assumption = (g.assumptions || []).find(a => a.id === res.assumptionId); return assumption ? `  - ${assumption.action}: expected ${assumption.expectedResult}, actual: ${res.actual}` : ""; }).filter(Boolean).join('\n')}`).join('\n\n') || "No results logged yet."}

Analyze this and respond with:

**ARE THE ASSUMPTIONS WORKING?**
For each assumption, assess: is the expected result matching actual results? Be specific with numbers.

**WHERE IS THE FUNNEL BREAKING?**
At which step are results falling short? Is it volume of actions, conversion rate, or wrong actions entirely?

**WHAT NEEDS TO BE ADJUSTED?**
Specific recommendation for each assumption that is not working. Give concrete numbers.

**WHAT IS WORKING?**
What should stay the same or be doubled down on?

**REVISED ACTION PLAN**
A suggested updated set of assumptions based on what you now know from actual results.

**THE HARD TRUTH**
One honest assessment of whether this goal is still achievable by the deadline given current performance.

Keep it under 500 words. Be specific. Use the actual numbers.`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1500, messages: [{ role: "user", content: prompt }] }),
      });
      const d = await res.json();
      const text = d.content?.map(b => b.text || "").join("") || "No response.";
      setAssumptionReview(text);
      await updateGoal({ assumptionReview: text, assumptionReviewDate: new Date().toISOString() });
    } catch (e) {
      setAssumptionReview("Error: " + (e.message || "Please try again."));
    }
    setAssumptionReviewLoading(false);
  }

  async function getAIPlan() {
    setAiPlanLoading(true); setAiPlanMsg("");
    const prompt = `You are a strategic advisor helping someone build an action plan for a goal.

GOAL: ${g.text}
AREA: ${area.label}
DEADLINE: ${g.deadline || "not set"} (${getCountdown(g)?.label || "unknown"})
WHY IT MATTERS: ${g.why || "not specified"}
SUCCESS MEASURE: ${g.success || "not specified"}
THEIR STRATEGY SO FAR: ${g.strategy || "not written yet"}

Build a concrete action plan with assumptions. For each key action needed to achieve this goal, define:
1. What is the action?
2. How often should it be done?
3. What result should each cycle of action produce?
4. When in the week should it be scheduled?

Respond in this exact JSON format with no other text:
{
  "assumptions": [
    {
      "action": "action description",
      "frequency": "how often",
      "expectedResult": "what this should produce",
      "timeBlock": "suggested time slot"
    }
  ],
  "keyMetric": "the one number to track",
  "firstStep": "do this first",
  "biggestRisk": "this could stop you"
}`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1200, messages: [{ role: "user", content: prompt }] }),
      });
      const d = await res.json();
      const text = d.content?.map(b => b.text || "").join("") || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("No JSON");
      const parsed = JSON.parse(jsonMatch[0]);
      const newAssumptions = (parsed.assumptions || []).map(a => ({ id: uid(), action: a.action || "", frequency: a.frequency || "", expectedResult: a.expectedResult || "", timeBlock: a.timeBlock || "" }));
      await updateGoal({ assumptions: newAssumptions });
      setAiPlanMsg("success");
    } catch {
      setAiPlanMsg("error");
    }
    setAiPlanLoading(false);
  }

  async function getNextAction() {
    setNextActionLoading(true);
    const lastMilestone = (g.milestones || []).filter(m => m.done).slice(-1)[0]?.text || "none";
    const prompt = `You are a practical life coach. The user has this goal:
Goal: ${g.text}
Area: ${area.label}
Progress: ${g.progress}%
Deadline: ${g.deadline || "not set"} (${getCountdown(g)?.label || "no deadline"})
Strategy: ${g.strategy || "not written yet"}
Actions: ${(g.assumptions || []).map(a => `${a.action} (${a.frequency})`).join(", ") || "none defined"}
Last milestone done: ${lastMilestone}

They need ONE specific next physical action to move this goal forward THIS WEEK.

Rules for your suggestion:
- Must be a physical action (buy, call, write, visit, build, cook, send)
- Must be completable in under 60 minutes
- Must be specific — include what, where if relevant
- Suggest the best day and time based on the goal area:
  Health/habits → morning this week
  Business → during work hours
  Home/food → weekend
  Family → evening or weekend
- End with: "Add to schedule?" as a prompt

Respond in this exact JSON format only, using double quotes:
{
  "action": "specific action description",
  "duration": "20 minutes",
  "suggestedDay": "Saturday",
  "suggestedTime": "10:00",
  "area": "${g.area}",
  "reason": "one sentence why this is the right first step"
}`;
    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 400, messages: [{ role: "user", content: prompt }] }),
      });
      const d = await res.json();
      const text = d.content?.map(b => b.text || "").join("") || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error("No JSON in response");
      const parsed = JSON.parse(jsonMatch[0]);
      const actionData = { ...parsed, generatedAt: new Date().toISOString() };
      setNextAction(actionData);
      await updateGoal({ nextAction: actionData });
    } catch (e) {
      console.error("Next action error:", e);
    }
    setNextActionLoading(false);
  }

  async function addNextActionToSchedule(na) {
    const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
    const startTime = na.suggestedTime || "09:00";
    const durationMins = parseInt(na.duration) || 30;
    const [h, m] = startTime.split(":").map(Number);
    const endMins = Math.min(h * 60 + m + durationMins, 23 * 60);
    const endTime = `${String(Math.floor(endMins / 60)).padStart(2, "0")}:${String(endMins % 60).padStart(2, "0")}`;
    const block = { id: uid(), startTime, endTime, taskName: na.action, area: g.area };
    const currentPlan = (dayPlans || {})[tomorrowStr] || {};
    const updatedBlocks = [...(currentPlan.timeBlocks || []), block].sort((a, b) => a.startTime.localeCompare(b.startTime));
    const updated = { ...(dayPlans || {}), [tomorrowStr]: { ...currentPlan, timeBlocks: updatedBlocks } };
    if (setDayPlans) setDayPlans(updated);
    await save("dayplans", updated, userId);
    setScheduleAdded(true);
    setTimeout(() => setScheduleAdded(false), 3000);
  }

  function timeAgo(isoStr) {
    if (!isoStr) return "";
    const mins = Math.round((new Date() - new Date(isoStr)) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 1440)}d ago`;
  }

  function buildCoachSystemPrompt() {
    return `You are a personal life coach inside alicanb.
You are having a focused coaching conversation about ONE specific goal with this user.

ABOUT THE USER:
- Owns two businesses (manufacturing + SaaS) in Israel
- Has kids, limited free time (~1-2 hours per day)
- Wants to close the gap between potential and results
- Has a tendency to delay — be aware of this pattern

THE GOAL YOU ARE COACHING:
- Goal: ${g.text}
- Area: ${area.label}
- Progress: ${g.progress}%
- Deadline: ${getCountdown(g)?.label || "no deadline set"}
- Why it matters: ${g.why || "not specified"}
- Strategy: ${g.strategy || "not written yet"}
- Actions: ${(g.assumptions || []).map(a => `${a.action} → expected: ${a.expectedResult}`).join(", ") || "none defined"}
- Milestones done: ${(g.milestones || []).filter(m => m.done).map(m => m.text).join(", ") || "none"}
- Milestones pending: ${(g.milestones || []).filter(m => !m.done).map(m => m.text).join(", ") || "none"}

COACHING RULES:
1. Ask ONE question at a time — never multiple questions
2. Listen to the answer before suggesting anything
3. Identify the REAL obstacle — it is rarely what the user says first. Dig one level deeper.
4. When you understand the obstacle, suggest ONE specific next physical action:
   - Must be completable this week
   - Must be specific (buy X, call Y, write Z)
   - Include when: day and time
5. Ask for a commitment: 'Will you do this by [day]?'
6. If they say yes — acknowledge it and end with: 'I will ask you about this next time.'
7. If they say no or maybe — explore why. Adjust the suggestion to be smaller or easier.
8. Never give a list of suggestions. One at a time.
9. Keep responses under 80 words. This is a conversation, not a report.
10. End each response with either a question OR a clear commitment request. Never just a statement.

OPENING MESSAGE (first message only):
Look at the goal progress and time remaining.
If progress is 0% or very low relative to deadline:
Start with: 'Let's talk about [goal name]. You have [X days] left and you are at [Y]%. What is actually getting in the way?'
If progress is reasonable:
Start with: 'Good progress on [goal name] — you are at [Y]%. What is the next milestone you want to hit, and what would it take to get there this week?'`;
  }

  function hasCommitment(text) {
    return /will you do this|by (monday|tuesday|wednesday|thursday|friday|saturday|sunday)|commit to|by tomorrow|by tonight|i will ask you/i.test(text);
  }

  function extractTaskText(msgContent) {
    const willYouIdx = msgContent.toLowerCase().indexOf("will you");
    const source = willYouIdx > 20 ? msgContent.slice(0, willYouIdx).trim() : msgContent;
    const sentences = source.split(/[.!?\n]/).map(s => s.trim()).filter(s => s.length > 10);
    return (sentences[sentences.length - 1] || msgContent).slice(0, 200);
  }

  async function openCoach() {
    if (coachOpen) { setCoachOpen(false); return; }
    const session = g.coachingSession;
    const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    if (session?.messages?.length && session.lastActiveAt && new Date(session.lastActiveAt) >= sevenDaysAgo) {
      setCoachMessages(session.messages);
      setCoachSummary(session.summary || "");
      setCoachOpen(true);
    } else {
      setCoachMessages([]);
      setCoachSummary("");
      setTaskSavedMap({});
      setCoachOpen(true);
      await triggerCoachOpening([]);
    }
  }

  async function triggerCoachOpening(currentMessages) {
    setCoachLoading(true);
    const hiddenStart = { role: "user", content: "Let's start.", timestamp: new Date().toISOString(), hidden: true };
    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 300,
          system: buildCoachSystemPrompt(),
          messages: [{ role: "user", content: "Let's start." }],
        }),
      });
      const d = await res.json();
      const aiText = d.content?.map(b => b.text || "").join("") || "Let's talk about your goal. What's actually getting in the way?";
      const aiMsg = { role: "assistant", content: aiText, timestamp: new Date().toISOString() };
      const newMessages = [hiddenStart, aiMsg];
      setCoachMessages(newMessages);
      const session = {
        messages: newMessages,
        startedAt: new Date().toISOString(),
        lastActiveAt: new Date().toISOString(),
        goalSnapshot: { text: g.text, progress: g.progress, deadline: g.deadline },
        summary: null,
      };
      await updateGoal({ coachingSession: session });
      setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 80);
    } catch {
      const fallback = { role: "assistant", content: "Let's talk about your goal. What's actually getting in the way of progress right now?", timestamp: new Date().toISOString() };
      setCoachMessages([hiddenStart, fallback]);
    }
    setCoachLoading(false);
  }

  async function sendCoachMessage() {
    if (!coachInput.trim() || coachLoading) return;
    const userMsg = { role: "user", content: coachInput.trim(), timestamp: new Date().toISOString() };
    const updatedMessages = [...coachMessages, userMsg];
    setCoachMessages(updatedMessages);
    setCoachInput("");
    setCoachLoading(true);
    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 300,
          system: buildCoachSystemPrompt(),
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const d = await res.json();
      const aiText = d.content?.map(b => b.text || "").join("") || "I hear you. Tell me more.";
      const aiMsg = { role: "assistant", content: aiText, timestamp: new Date().toISOString() };
      const finalMessages = [...updatedMessages, aiMsg];
      setCoachMessages(finalMessages);
      const updatedSession = { ...(g.coachingSession || {}), messages: finalMessages, lastActiveAt: new Date().toISOString() };
      await updateGoal({ coachingSession: updatedSession });
      setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 80);
    } catch {
      setCoachMessages(m => [...m, { role: "assistant", content: "Something went wrong. Please try again.", timestamp: new Date().toISOString() }]);
    }
    setCoachLoading(false);
  }

  async function endCoachSession() {
    if (coachEnding || coachMessages.length < 2) return;
    setCoachEnding(true);
    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 200,
          system: buildCoachSystemPrompt(),
          messages: [
            ...coachMessages.map(m => ({ role: m.role, content: m.content })),
            { role: "user", content: "Summarize this coaching session in exactly 3 bullet points:\n1. The main obstacle identified\n2. The committed next action with day/time\n3. One thing to watch for\n\nKeep it under 60 words total." },
          ],
        }),
      });
      const d = await res.json();
      const summaryText = d.content?.map(b => b.text || "").join("") || "Session complete.";
      setCoachSummary(summaryText);
      const updatedSession = { ...(g.coachingSession || {}), messages: coachMessages, lastActiveAt: new Date().toISOString(), summary: summaryText };
      await updateGoal({ coachingSession: updatedSession });
      setTimeout(() => { if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight; }, 80);
    } catch {
      setCoachSummary("Session saved.");
    }
    setCoachEnding(false);
  }

  async function saveAsTask(msgContent, msgIndex) {
    const taskText = extractTaskText(msgContent);
    const newTask = {
      id: uid(), text: taskText, when: "today",
      tag: area.id === "business" ? "work" : "personal",
      urgent: false, linkedGoal: g.id,
      order: Date.now(), createdAt: new Date().toISOString(),
      done: false, doneAt: null,
    };
    const updated = [...(tasks || []), newTask];
    setTasks(updated);
    await save("tasks", updated, userId);
    setTaskSavedMap(m => ({ ...m, [msgIndex]: true }));
  }

  function renderMd(text) {
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
      if (line.startsWith("**") && line.endsWith("**"))
        return <div key={i} style={{ fontWeight: 700, color: "#f59e0b", marginTop: 14, marginBottom: 4, fontSize: 11, letterSpacing: "0.05em" }}>{line.replace(/\*\*/g, "")}</div>;
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return <div key={i} style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7 }}>
        {parts.map((p, j) => j % 2 === 1 ? <strong key={j} style={{ color: "#cbd5e1" }}>{p}</strong> : p)}
      </div>;
    });
  }

  return (
    <div ref={cardRef} style={{ ...card, borderLeft: `3px solid ${area.color}` }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ flex: 1, paddingRight: 12, cursor: "pointer" }} onClick={() => setExpanded(e => !e)}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, background: area.color + "22", color: area.color, padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>
              {area.icon} {area.label}
            </span>
            <span style={{ fontSize: 11, color: "#334155" }}>{g.horizon === "custom" ? (g.customHorizon || "Custom") : (HORIZONS[g.horizon] || g.horizon)}</span>
            {saveStatus && (
              <span style={{ fontSize: 10, color: saveStatus === "saved" ? "#10b981" : "#f59e0b", marginLeft: 8 }}>
                {saveStatus === "saving" ? "saving..." : "✓ saved"}
              </span>
            )}
            {(() => {
              const cd = getCountdown(g);
              return cd ? (
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
                  background: cd.color + "22", color: cd.color,
                  animation: cd.urgent ? "pulse-badge 2s ease-in-out infinite" : "none",
                }}>
                  ⏱ {cd.label}
                </span>
              ) : null;
            })()}
            <span style={{ fontSize: 11, color: "#334155", marginLeft: "auto" }}>{expanded ? "▴" : "▾"}</span>
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#f1f5f9", marginBottom: 4 }}>{g.text}</div>
          {g.why && <div style={{ fontSize: 12, color: "#475569", fontStyle: "italic", marginBottom: 2 }}>Why: {g.why}</div>}
          {g.success && <div style={{ fontSize: 12, color: "#475569" }}>✓ {g.success}</div>}
          {!g.why && !g.success && !expanded && <div style={{ fontSize: 11, color: "#334155", marginTop: 2 }}>Tap to add details →</div>}
        </div>
        <button onClick={del} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 18, flexShrink: 0 }}
          onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
      </div>

      {/* Progress bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: expanded ? 16 : 0 }}>
        <div style={{ flex: 1, height: 5, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: g.progress + "%", height: "100%", background: area.color, borderRadius: 3, transition: "width 0.4s" }} />
        </div>
        <span style={{ fontSize: 12, color: area.color, fontWeight: 700, minWidth: 34 }}>{g.progress}%</span>
        <input type="range" min={0} max={100} value={g.progress}
          onChange={e => updateGoal({ progress: +e.target.value })} style={{ width: 80, accentColor: area.color }} />
      </div>

      {/* Goal completion celebration */}
      {g.progress === 100 && (
        <div style={{ margin: "10px 0", background: area.color + "15", border: `1px solid ${area.color}44`, borderRadius: 10, padding: "14px", textAlign: "center" }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>🎉</div>
          <div style={{ fontSize: 14, color: area.color, fontWeight: 700, marginBottom: 6 }}>Goal Achieved!</div>
          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
            You set out to: <em style={{ color: "#94a3b8" }}>"{g.text.slice(0, 60)}"</em><br/>
            And you did it. This is what closing the gap looks like. 🌱
          </div>
          <div style={{ fontSize: 11, color: "#334155", marginTop: 8, fontStyle: "italic" }}>
            Consider sharing your story — it could change someone else's life.
          </div>
        </div>
      )}

      {/* Expanded section */}
      {expanded && (
        <div style={{ borderTop: "1px solid #1e293b", paddingTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Editable goal title */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>GOAL</div>
            <input value={g.text || ""} onChange={e => updateGoal({ text: e.target.value })}
              placeholder="What do you want to achieve?" style={inp} />
          </div>

          {/* Area + Timeframe editable */}
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>AREA</div>
              <select value={g.area || "business"} onChange={e => updateGoal({ area: e.target.value })} style={inp}>
                {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>TIMEFRAME</div>
              <select value={g.horizon === "custom" || !HORIZONS[g.horizon] ? "custom" : g.horizon}
                onChange={e => updateGoal({ horizon: e.target.value })} style={inp}>
                {Object.entries(HORIZONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Deadline date picker */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>TARGET DATE — when exactly?</div>
            <input type="date" value={g.deadline || ""}
              onChange={e => updateGoal({ deadline: e.target.value })}
              style={{ ...inp, colorScheme: "dark" }} />
            {g.deadline && (
              <div style={{ fontSize: 11, color: getCountdown(g)?.color || "#64748b", marginTop: 4 }}>
                {getCountdown(g)?.label} — {new Date(g.deadline).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
            )}
          </div>

          {/* Custom timeframe input */}
          {(g.horizon === "custom" || !HORIZONS[g.horizon]) && (
            <div>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>YOUR TIMEFRAME</div>
              <input value={g.customHorizon || ""}
                onChange={e => updateGoal({ customHorizon: e.target.value, horizon: "custom" })}
                placeholder="e.g. End of summer, Before my 40th birthday, 6 months..." style={inp} />
            </div>
          )}

          {/* Why */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>WHY DOES THIS MATTER?</div>
            <textarea value={g.why || ""} onChange={e => updateGoal({ why: e.target.value })}
              placeholder="Be honest with yourself. Why do you really want this?"
              rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>

          {/* Success measure */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>HOW WILL YOU KNOW YOU ACHIEVED IT?</div>
            <input value={g.success || ""} onChange={e => updateGoal({ success: e.target.value })}
              placeholder="Make it measurable — a number, a date, a clear outcome" style={inp} />
          </div>

          {/* Strategy */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>YOUR STRATEGY — how will you get there?</div>
            <textarea value={g.strategy || ""} onChange={e => updateGoal({ strategy: e.target.value })}
              placeholder="Describe your plan step by step. The more specific, the better the AI feedback."
              rows={3} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} />
          </div>

          {/* Assumptions & Action Plan */}
          <div style={{ borderTop: "1px solid #1e293b", paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: area.color, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>◎ ASSUMPTIONS & ACTION PLAN</div>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>What actions do you believe will produce this goal? Track them, then compare against real results.</div>

            {/* AI Planning Helper — only when no assumptions exist */}
            {(g.assumptions || []).length === 0 && (
              <div style={{ marginBottom: 12 }}>
                <button onClick={getAIPlan} disabled={aiPlanLoading} style={{ ...btn("#8b5cf6"), width: "100%", opacity: aiPlanLoading ? 0.7 : 1 }}>
                  {aiPlanLoading ? "Building your plan…" : "✦ AI Help Me Plan This Goal"}
                </button>
                {aiPlanMsg === "success" && <div style={{ fontSize: 12, color: "#10b981", textAlign: "center", marginTop: 8 }}>✓ Plan generated — review and adjust the assumptions below</div>}
                {aiPlanMsg === "error" && <div style={{ fontSize: 12, color: "#ef4444", textAlign: "center", marginTop: 8 }}>Could not generate plan. Try again or add assumptions manually.</div>}
              </div>
            )}

            {/* Assumption entries */}
            {(g.assumptions || []).map((a, idx) => (
              <div key={a.id} style={{ background: "#080f1e", border: `1px solid ${area.color}33`, borderLeft: `3px solid ${area.color}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 10, color: area.color, fontWeight: 700, letterSpacing: "0.06em" }}>ASSUMPTION {idx + 1}</span>
                  <button onClick={() => updateGoal({ assumptions: (g.assumptions || []).filter(x => x.id !== a.id) })}
                    style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 16 }}
                    onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
                </div>
                {[
                  { field: "action", label: "ACTION", placeholder: "e.g. Cold call potential customers" },
                  { field: "frequency", label: "FREQUENCY", placeholder: "e.g. 200 per month or 3x per week" },
                  { field: "expectedResult", label: "EXPECTED RESULT", placeholder: "e.g. 10 send drawings, 1 becomes customer" },
                  { field: "timeBlock", label: "TIME BLOCK (optional)", placeholder: "e.g. Tuesday 09:00-10:00, Wednesday 09:00-10:00" },
                ].map(({ field, label, placeholder }) => (
                  <div key={field} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 4 }}>{label}</div>
                    <input value={a[field] || ""} onChange={e => updateGoal({ assumptions: (g.assumptions || []).map(x => x.id === a.id ? { ...x, [field]: e.target.value } : x) })}
                      placeholder={placeholder} style={{ ...inp, fontSize: 12 }} />
                  </div>
                ))}
              </div>
            ))}

            <button onClick={() => updateGoal({ assumptions: [...(g.assumptions || []), { id: uid(), action: "", frequency: "", expectedResult: "", timeBlock: "" }] })}
              style={{ ...btn("#1e293b", "#64748b"), width: "100%", fontSize: 12 }}>
              + Add Assumption
            </button>
            {(g.assumptions || []).length > 0 && aiPlanMsg === "success" && (
              <div style={{ fontSize: 12, color: "#10b981", textAlign: "center", marginTop: 8 }}>✓ Plan generated — review and adjust the assumptions above</div>
            )}
            {(g.assumptions || []).length > 0 && aiPlanMsg === "error" && (
              <div style={{ fontSize: 12, color: "#ef4444", textAlign: "center", marginTop: 8 }}>Could not generate plan. Try again or edit assumptions manually.</div>
            )}
          </div>

          {/* Results Log */}
          <div style={{ borderTop: "1px solid #1e293b", paddingTop: 14 }}>
            <div style={{ fontSize: 11, color: area.color, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 12 }}>📊 RESULTS LOG</div>

            <button onClick={() => {
              const thisMonth = new Date().toISOString().slice(0, 7);
              const newEntry = { id: uid(), month: thisMonth, results: (g.assumptions || []).map(a => ({ assumptionId: a.id, actual: "" })), notes: "", score: 5 };
              updateGoal({ resultsLog: [newEntry, ...(g.resultsLog || [])] });
            }} style={{ ...btn("#334155", "#94a3b8"), width: "100%", fontSize: 12, marginBottom: 12 }}>
              + Log This Month
            </button>

            {(g.resultsLog || []).map(r => (
              <div key={r.id} style={{ background: "#080f1e", border: "1px solid #1e293b", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <input type="month" value={r.month}
                    onChange={e => updateGoal({ resultsLog: (g.resultsLog || []).map(x => x.id === r.id ? { ...x, month: e.target.value } : x) })}
                    style={{ ...inp, width: "auto", colorScheme: "dark", fontWeight: 700, color: area.color, fontSize: 13 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 11, color: "#475569" }}>Execution: <span style={{ color: r.score >= 8 ? "#10b981" : r.score >= 5 ? "#f59e0b" : "#ef4444", fontWeight: 700 }}>{r.score}/10</span></span>
                    <button onClick={() => updateGoal({ resultsLog: (g.resultsLog || []).filter(x => x.id !== r.id) })}
                      style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 16 }}
                      onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
                  </div>
                </div>

                <input type="range" min={1} max={10} value={r.score}
                  onChange={e => updateGoal({ resultsLog: (g.resultsLog || []).map(x => x.id === r.id ? { ...x, score: +e.target.value } : x) })}
                  style={{ width: "100%", accentColor: area.color, marginBottom: 12 }} />

                {(g.assumptions || []).length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>ACTUAL RESULTS PER ASSUMPTION</div>
                    {(g.assumptions || []).map(a => {
                      const result = (r.results || []).find(res => res.assumptionId === a.id);
                      return (
                        <div key={a.id} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>{a.action || "Unnamed action"}</div>
                          <input value={result?.actual || ""}
                            onChange={e => {
                              const results = [...(r.results || [])];
                              const idx = results.findIndex(res => res.assumptionId === a.id);
                              if (idx >= 0) results[idx] = { ...results[idx], actual: e.target.value };
                              else results.push({ assumptionId: a.id, actual: e.target.value });
                              updateGoal({ resultsLog: (g.resultsLog || []).map(x => x.id === r.id ? { ...x, results } : x) });
                            }}
                            placeholder={a.expectedResult ? `Expected: ${a.expectedResult}` : "What actually happened?"}
                            style={{ ...inp, fontSize: 12 }} />
                        </div>
                      );
                    })}
                  </div>
                )}

                <div>
                  <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 4 }}>MONTHLY NOTES</div>
                  <textarea value={r.notes || ""}
                    onChange={e => updateGoal({ resultsLog: (g.resultsLog || []).map(x => x.id === r.id ? { ...x, notes: e.target.value } : x) })}
                    placeholder="What happened this month? What did you learn?"
                    rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit", fontSize: 12 }} />
                </div>
              </div>
            ))}

            {/* AI Assumption Review */}
            {(g.assumptions || []).length > 0 && (
              <>
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button onClick={getAssumptionReview} disabled={assumptionReviewLoading} style={{ ...btn("#f59e0b", "#000"), flex: 1, opacity: assumptionReviewLoading ? 0.7 : 1 }}>
                    {assumptionReviewLoading ? "Reviewing…" : assumptionReview ? "↻ Re-review Assumptions" : "✦ Review My Assumptions"}
                  </button>
                  {assumptionReview && !assumptionReviewLoading && (
                    <button onClick={() => { updateGoal({ assumptionReview: "", assumptionReviewDate: null }); setAssumptionReview(""); }}
                      style={btn("#1e293b", "#475569")} title="Clear review">✕</button>
                  )}
                </div>
                {(assumptionReviewLoading || assumptionReview) && (
                  <div style={{ background: "#080f1e", border: "1px solid #f59e0b33", borderRadius: 10, padding: "14px 16px", marginTop: 10 }}>
                    {assumptionReviewLoading ? (
                      <div style={{ textAlign: "center", padding: "12px 0" }}>
                        <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>Comparing assumptions to results…</div>
                        <div style={{ display: "flex", justifyContent: "center", gap: 5 }}>
                          {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#f59e0b", animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
                        </div>
                      </div>
                    ) : (
                      <div>
                        {g.assumptionReviewDate && (
                          <div style={{ fontSize: 10, color: "#334155", marginBottom: 10, textAlign: "right" }}>
                            saved · {new Date(g.assumptionReviewDate).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                        {renderMd(assumptionReview)}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Milestones */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>MILESTONES — checkpoints on the way</div>
            {(g.milestones || []).map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <button onClick={() => toggleMilestone(m.id)} style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0, padding: 0, cursor: "pointer",
                  border: `2px solid ${m.done ? area.color : "#334155"}`, background: m.done ? area.color : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {m.done && <svg width="9" height="9" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" /></svg>}
                </button>
                <span style={{ flex: 1, fontSize: 13, color: m.done ? "#334155" : "#94a3b8", textDecoration: m.done ? "line-through" : "none" }}>{m.text}</span>
                <button onClick={() => delMilestone(m.id)} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 14 }}
                  onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <input value={milestoneText} onChange={e => setMilestoneText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addMilestone()}
                placeholder="Add a milestone..." style={{ ...inp, flex: 1 }} />
              <button onClick={addMilestone} style={btn("#334155", "#94a3b8")}>+ Add</button>
            </div>
          </div>

          {/* Progress Log */}
          <div>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>PROGRESS LOG</div>

            {/* Metric name */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: "#475569", whiteSpace: "nowrap" }}>Metric:</span>
              <input
                value={g.logMetric || ""}
                onChange={e => updateGoal({ logMetric: e.target.value })}
                placeholder="Value"
                style={{ ...inp, flex: 1, fontSize: 12, padding: "5px 8px" }}
              />
            </div>

            {/* Entry form */}
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)}
                style={{ ...inp, width: "auto", fontSize: 12, padding: "5px 8px", colorScheme: "dark", flexShrink: 0 }} />
              <input type="number" value={logValue} onChange={e => setLogValue(e.target.value)}
                placeholder={g.logMetric || "Value"}
                style={{ ...inp, width: 90, fontSize: 12, padding: "5px 8px" }} />
              <input value={logNote} onChange={e => setLogNote(e.target.value)}
                placeholder="Note (optional)"
                style={{ ...inp, flex: 1, minWidth: 100, fontSize: 12, padding: "5px 8px" }} />
              <button onClick={() => {
                if (!logValue.trim()) return;
                const entry = { id: uid(), date: logDate, value: parseFloat(logValue), note: logNote.trim() };
                const existing = g.progressLog || [];
                const updated = [...existing, entry].sort((a, b) => a.date.localeCompare(b.date));
                updateGoal({ progressLog: updated });
                setLogValue(""); setLogNote(""); setLogDate(todayKey());
              }} style={{ ...btn("#334155", "#94a3b8"), padding: "5px 12px", fontSize: 12 }}>+ Add</button>
            </div>

            {/* Chart */}
            {(g.progressLog || []).length >= 2 && (() => {
              const chartData = (g.progressLog || []).map(e => ({
                date: e.date.slice(5).replace("-", "/"), // MM/DD
                value: e.value,
                note: e.note,
                fullDate: e.date,
              }));
              return (
                <div style={{ background: "#080f1e", borderRadius: 8, padding: "12px 8px 8px", marginBottom: 8 }}>
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <XAxis dataKey="date" tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
                      <Tooltip
                        contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6, fontSize: 11 }}
                        labelStyle={{ color: "#94a3b8" }}
                        itemStyle={{ color: area.color }}
                        formatter={(value, _name, props) => [
                          `${value}${props.payload.note ? ` — ${props.payload.note}` : ""}`,
                          g.logMetric || "Value"
                        ]}
                      />
                      <Line type="monotone" dataKey="value" stroke={area.color} strokeWidth={2}
                        dot={{ fill: area.color, r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}

            {/* Log table */}
            {(g.progressLog || []).length > 0 && (
              <div style={{ background: "#080f1e", borderRadius: 8, overflow: "hidden" }}>
                {[...(g.progressLog || [])].reverse().slice(0, 10).map(e => (
                  <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #0f172a" }}>
                    <span style={{ fontSize: 11, color: "#475569", flexShrink: 0, minWidth: 72 }}>{e.date}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: area.color, flexShrink: 0, minWidth: 40 }}>{e.value}</span>
                    <span style={{ fontSize: 11, color: "#475569", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.note}</span>
                    <button onClick={() => {
                      const updated = (g.progressLog || []).filter(x => x.id !== e.id);
                      updateGoal({ progressLog: updated });
                    }} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 14, flexShrink: 0, padding: "0 2px" }}
                      onMouseEnter={ev => ev.target.style.color = "#ef4444"}
                      onMouseLeave={ev => ev.target.style.color = "#334155"}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Coach Me section */}
          <div>
            {/* Previous session toggle */}
            {g.coachingSession?.summary && !coachOpen && (
              <div style={{ marginBottom: 8 }}>
                <button onClick={() => setShowPrevSession(s => !s)} style={{ ...btn("#1e293b", "#64748b"), width: "100%", fontSize: 11, padding: "6px 12px" }}>
                  {showPrevSession ? "▴ Hide previous session" : "▾ Previous session — " + new Date(g.coachingSession.startedAt || Date.now()).toLocaleDateString([], { month: "short", day: "numeric" })}
                </button>
                {showPrevSession && (
                  <div style={{ background: "#071a12", border: "1px solid #10b98133", borderRadius: 8, padding: "12px 14px", marginTop: 6 }}>
                    <div style={{ fontSize: 10, color: "#10b981", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>LAST SESSION SUMMARY</div>
                    {g.coachingSession.summary.split("\n").map((line, i) => (
                      <div key={i} style={{ fontSize: 12, color: "#64748b", lineHeight: 1.8 }}>{line}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Coach Me button */}
            <button onClick={openCoach} style={{ ...btn("#10b981"), width: "100%", fontSize: 13, padding: "11px", marginBottom: 4, opacity: 1 }}>
              {coachOpen ? "◎ Coaching session active — tap to close" : "◎ Coach Me on This Goal"}
            </button>

            {/* Coaching panel */}
            {coachOpen && (
              <div style={{ background: "#071a12", border: "1px solid #10b98133", borderRadius: 12, overflow: "hidden", marginTop: 4, marginBottom: 4 }}>
                {/* Messages area */}
                <div ref={chatScrollRef} style={{ maxHeight: 380, overflowY: "auto", padding: "14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {coachMessages.filter(m => !m.hidden).map((m, i) => {
                    const globalIndex = coachMessages.indexOf(m);
                    return (
                      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                        <div style={{
                          maxWidth: "88%",
                          background: m.role === "user" ? "#1e1200" : "#0f172a",
                          border: m.role === "user" ? "1px solid #f59e0b44" : "1px solid #10b98133",
                          borderLeft: m.role === "assistant" ? "3px solid #10b981" : undefined,
                          borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                          padding: "10px 13px",
                        }}>
                          <div style={{ fontSize: 13, color: m.role === "user" ? "#fbbf24" : "#e2e8f0", lineHeight: 1.65 }}>{m.content}</div>
                          {m.role === "assistant" && hasCommitment(m.content) && (
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #10b98122" }}>
                              {taskSavedMap[globalIndex] ? (
                                <span style={{ fontSize: 11, color: "#10b981" }}>✓ Added to your tasks</span>
                              ) : (
                                <button onClick={() => saveAsTask(m.content, globalIndex)}
                                  style={{ background: "#10b98115", border: "1px solid #10b98144", borderRadius: 6, color: "#10b981", fontSize: 11, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                                  📌 Save as task
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {m.timestamp && (
                          <div style={{ fontSize: 9, color: "#334155", marginTop: 2, paddingLeft: 2 }}>
                            {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Typing indicator */}
                  {coachLoading && (
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <div style={{ background: "#0f172a", border: "1px solid #10b98133", borderLeft: "3px solid #10b981", borderRadius: "14px 14px 14px 4px", padding: "12px 16px" }}>
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          {[0,1,2].map(i => <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#10b981", animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Session summary */}
                  {coachSummary && (
                    <div style={{ background: "#080f1e", border: "1px solid #f59e0b33", borderRadius: 10, padding: "14px 16px", marginTop: 4 }}>
                      <div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 10 }}>◎ SESSION SUMMARY</div>
                      {coachSummary.split("\n").filter(l => l.trim()).map((line, i) => (
                        <div key={i} style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.8, marginBottom: 2 }}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Input area */}
                {!coachSummary && (
                  <div style={{ borderTop: "1px solid #1e293b", padding: "10px 12px", background: "#040d06" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={coachInput}
                        onChange={e => setCoachInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCoachMessage(); } }}
                        placeholder="Type your response…"
                        style={{ ...inp, flex: 1, fontSize: 13, background: "#071a12", borderColor: "#10b98133" }}
                        disabled={coachLoading}
                      />
                      <button onClick={sendCoachMessage} disabled={coachLoading || !coachInput.trim()}
                        style={{ ...btn("#10b981"), opacity: coachLoading || !coachInput.trim() ? 0.4 : 1, padding: "9px 16px" }}>
                        Send
                      </button>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                      <button onClick={endCoachSession} disabled={coachEnding || coachLoading}
                        style={{ ...btn("#1e293b", "#64748b"), fontSize: 11, opacity: coachEnding ? 0.7 : 1 }}>
                        {coachEnding ? "Saving session…" : "End Session"}
                      </button>
                    </div>
                  </div>
                )}

                {/* After summary: close button */}
                {coachSummary && (
                  <div style={{ borderTop: "1px solid #1e293b", padding: "10px 12px", textAlign: "right", background: "#040d06" }}>
                    <button onClick={() => setCoachOpen(false)} style={{ ...btn("#1e293b", "#64748b"), fontSize: 12 }}>Close session</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* AI Feedback button */}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={getAiFeedback} disabled={aiFeedbackLoading} style={{
              ...btn("#8b5cf6"), flex: 1, opacity: aiFeedbackLoading ? 0.7 : 1,
            }}>
              {aiFeedbackLoading ? "Analyzing..." : aiFeedback ? "↻ Refresh AI Feedback" : "✦ Get AI Feedback on This Goal"}
            </button>
            {aiFeedback && !aiFeedbackLoading && (
              <button onClick={() => { updateGoal({ aiFeedback: "", aiFeedbackDate: null }); setAiFeedback(""); }}
                style={btn("#1e293b", "#475569")} title="Clear feedback">✕ Clear</button>
            )}
          </div>

          {/* AI Feedback output */}
          {(aiFeedbackLoading || aiFeedback) && (
            <div style={{ background: "#080f1e", border: "1px solid #8b5cf633", borderRadius: 10, padding: "14px 16px" }}>
              {aiFeedbackLoading ? (
                <div style={{ textAlign: "center", padding: "16px 0" }}>
                  <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>Reading your goal and strategy...</div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 5 }}>
                    {[0,1,2].map(i => (
                      <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#8b5cf6", animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />
                    ))}
                  </div>
                  <style>{`
  @keyframes pulse{0%,100%{opacity:.2;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
  @keyframes pulse-badge{0%,100%{opacity:1}50%{opacity:0.6}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
`}</style>
                </div>
              ) : (
                <div>
                  {g.aiFeedbackDate && (
                    <div style={{ fontSize: 10, color: "#334155", marginBottom: 10, textAlign: "right" }}>
                      saved · {new Date(g.aiFeedbackDate).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                  {renderMd(aiFeedback)}
                </div>
              )}
            </div>
          )}

          {/* Next Action button */}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={getNextAction} disabled={nextActionLoading} style={{
              ...btn("#10b981"), flex: 1, opacity: nextActionLoading ? 0.7 : 1,
            }}>
              {nextActionLoading ? "Finding your next step…" : nextAction ? "↻ New suggestion" : "✦ What's My Next Action?"}
            </button>
            {nextAction && !nextActionLoading && (
              <button onClick={() => { updateGoal({ nextAction: null }); setNextAction(null); }}
                style={btn("#1e293b", "#475569")} title="Clear suggestion">✕</button>
            )}
          </div>

          {/* Next Action loading */}
          {nextActionLoading && (
            <div style={{ background: "#071a12", border: "1px solid #10b98133", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>Thinking of the smallest first step…</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 5 }}>
                {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
              </div>
            </div>
          )}

          {/* Next Action card */}
          {nextAction && !nextActionLoading && (
            <div style={{ background: "#071a12", borderLeft: "3px solid #10b981", border: "1px solid #10b98133", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 10, color: "#10b981", fontWeight: 700, letterSpacing: "0.06em" }}>✦ NEXT ACTION</span>
                <span style={{ fontSize: 10, color: "#334155" }}>generated {timeAgo(nextAction.generatedAt)}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 8, lineHeight: 1.5 }}>{nextAction.action}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, background: "#10b98122", color: "#10b981", padding: "2px 10px", borderRadius: 20, fontWeight: 600 }}>{nextAction.duration}</span>
                <span style={{ fontSize: 11, color: "#64748b" }}>{nextAction.suggestedDay} · {nextAction.suggestedTime}</span>
              </div>
              {nextAction.reason && (
                <div style={{ fontSize: 11, color: "#475569", fontStyle: "italic", marginBottom: 12, lineHeight: 1.6 }}>{nextAction.reason}</div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => addNextActionToSchedule(nextAction)}
                  style={{ ...btn(scheduleAdded ? "#1e293b" : "#10b981", scheduleAdded ? "#10b981" : "#fff"), fontSize: 12 }}>
                  {scheduleAdded ? "✓ Added to tomorrow" : "📅 Add to Schedule"}
                </button>
                <button onClick={() => setExpanded(true)}
                  style={{ ...btn("#1e293b", "#94a3b8"), fontSize: 12 }}>
                  ✓ Done — mark progress
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GoalsView({ goals, setGoals, userId, dayPlans, setDayPlans, tasks, setTasks }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ text: "", area: "business", horizon: "90d" });
  const [saveMsg, setSaveMsg] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("alicanb-goals-view") || "dashboard");
  const [expandGoalId, setExpandGoalId] = useState(null);
  const [coachGoalId, setCoachGoalId] = useState(null);

  useEffect(() => { localStorage.setItem("alicanb-goals-view", view); }, [view]);

  // Switch to list view with a goal expanded (optionally starting a coach session).
  function openGoalInList(id, coach) {
    setView("list");
    // Reset then set so the same goal can be re-triggered.
    setExpandGoalId(null); setCoachGoalId(null);
    setTimeout(() => {
      setExpandGoalId(id);
      if (coach) setCoachGoalId(id);
    }, 0);
  }

  async function addGoal() {
    if (!form.text.trim()) return;
    const newGoal = { id: uid(), ...form, why: "", success: "", strategy: "", customHorizon: form.customHorizon || "", progress: 0, milestones: [], createdAt: new Date().toISOString() };
    return new Promise(resolve => {
      setGoals(prev => {
        const updated = [newGoal, ...prev];
        save("goals", updated, userId).then(() => resolve());
        return updated;
      });
    }).then(() => {
      setForm({ text: "", area: "business", horizon: "90d" });
      setAdding(false);
      setSaveMsg("✓ Goal saved");
      setTimeout(() => setSaveMsg(""), 3000);
    });
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Your Goals</h2>
          <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>Add a goal. Tap it to add details, strategy, and get AI feedback.</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {saveMsg && <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600 }}>{saveMsg}</span>}
          <button onClick={() => setAdding(true)} style={btn("#f59e0b", "#000")}>+ Add Goal</button>
        </div>
      </div>

      {/* View toggle */}
      {goals.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 20, background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: 4, width: "fit-content" }}>
          {[["dashboard", "📊 Dashboard"], ["list", "☰ List"]].map(([key, label]) => (
            <button key={key} onClick={() => setView(key)}
              style={{
                background: view === key ? "#1e293b" : "transparent", border: "none", borderRadius: 7,
                padding: "7px 14px", color: view === key ? "#f1f5f9" : "#64748b",
                fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
              }}>{label}</button>
          ))}
        </div>
      )}

      {adding && (
        <div style={{ ...card, marginBottom: 16, borderColor: "#f59e0b44" }}>
          <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 12 }}>NEW GOAL</div>
          <div style={{ display: "grid", gap: 10 }}>
            <input value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && addGoal()}
              placeholder="What do you want to achieve?" style={inp} autoFocus />
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} style={{ ...inp, flex: 1 }}>
                {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
              </select>
              <select value={form.horizon} onChange={e => setForm(f => ({ ...f, horizon: e.target.value }))} style={{ ...inp, flex: 1 }}>
                {Object.entries(HORIZONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            {form.horizon === "custom" && (
              <input value={form.customHorizon || ""} onChange={e => setForm(f => ({ ...f, customHorizon: e.target.value }))}
                placeholder="e.g. End of summer, Before my 40th birthday..." style={inp} />
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={addGoal} style={btn("#10b981")}>Save Goal</button>
              <button onClick={() => setAdding(false)} style={btn("#1e293b", "#64748b")}>Cancel</button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#334155", marginTop: 10 }}>
            Tap the goal after saving to add why, strategy, milestones, and AI feedback.
          </div>
        </div>
      )}

      {goals.length === 0 && !adding && (
        <div style={{ ...card, textAlign: "center", padding: "50px 20px", color: "#334155" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>◎</div>
          <div style={{ fontSize: 14, color: "#475569" }}>No goals yet. Everything starts here.</div>
          <div style={{ fontSize: 12, marginTop: 6, color: "#334155" }}>Add goals across all areas of life. The AI will use them to judge every action you take.</div>
        </div>
      )}

      {view === "dashboard" && goals.length > 0 && (
        <GoalDashboard
          goals={goals}
          onOpen={id => openGoalInList(id, false)}
          onCoach={id => openGoalInList(id, true)}
        />
      )}

      {view === "list" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {goals.map(g => <GoalCard key={g.id} g={g} goals={goals} setGoals={setGoals} userId={userId} dayPlans={dayPlans} setDayPlans={setDayPlans} tasks={tasks} setTasks={setTasks} autoExpand={expandGoalId === g.id} autoCoach={coachGoalId === g.id} />)}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TODAY
// ═══════════════════════════════════════════════════════════════════════════════
// Time slots 05:00–23:00 in 30-min increments
const TIME_SLOTS = [];
for (let h = 5; h <= 23; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 23) TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}
const SCHED_START = 5 * 60;  // 300 min
const SCHED_END   = 23 * 60; // 1380 min
const SCHED_SPAN  = SCHED_END - SCHED_START; // 1080 min
const PX_PER_MIN  = 1.5; // 90px per hour

function timeToMins(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function TimeBlockSchedule({ blocks, onDelete, onEdit, editingId, onSaveEdit, onCancelEdit, areaMap }) {
  const totalHeight = SCHED_SPAN * PX_PER_MIN;
  const hours = Array.from({ length: 19 }, (_, i) => i + 5);

  // Local edit form state — initialised when editingId changes
  const [editTask, setEditTask] = useState("");
  const [editStart, setEditStart] = useState("08:00");
  const [editEnd, setEditEnd] = useState("09:00");
  const [editArea, setEditArea] = useState("business");

  useEffect(() => {
    if (editingId) {
      const b = (blocks || []).find(x => x.id === editingId);
      if (b) { setEditTask(b.taskName); setEditStart(b.startTime); setEditEnd(b.endTime); setEditArea(b.area || "business"); }
    }
  }, [editingId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: "relative", display: "flex", gap: 0 }}>
      {/* Hour labels */}
      <div style={{ position: "relative", width: 34, flexShrink: 0, height: totalHeight }}>
        {hours.map(h => {
          const top = (h * 60 - SCHED_START) * PX_PER_MIN;
          return (
            <div key={h} style={{
              position: "absolute", top, left: 0, width: "100%",
              fontSize: 9, color: "#334155", lineHeight: 1,
              borderTop: "1px solid #1e293b", paddingTop: 2,
            }}>{String(h).padStart(2, "0")}:00</div>
          );
        })}
      </div>

      {/* Block area */}
      <div style={{ position: "relative", flex: 1, height: totalHeight, background: "#080f1e", borderRadius: 6, overflow: "hidden" }}>
        {hours.map(h => (
          <div key={h} style={{ position: "absolute", top: (h * 60 - SCHED_START) * PX_PER_MIN, left: 0, right: 0, borderTop: "1px solid #1e293b" }} />
        ))}
        {hours.slice(0, -1).map(h => (
          <div key={h + 0.5} style={{ position: "absolute", top: (h * 60 + 30 - SCHED_START) * PX_PER_MIN, left: 0, right: 0, borderTop: "1px dashed #0f172a" }} />
        ))}
        {(blocks || []).map(b => {
          const startMins = timeToMins(b.startTime);
          const endMins   = timeToMins(b.endTime);
          const top    = (startMins - SCHED_START) * PX_PER_MIN;
          const height = Math.max((endMins - startMins) * PX_PER_MIN, 18);
          const area   = areaMap[b.area] || { color: "#475569", icon: "" };
          const dur    = endMins - startMins;
          const isEditing = editingId === b.id;

          if (isEditing) {
            return (
              <div key={b.id} style={{
                position: "absolute", top, left: 4, right: 4,
                background: "#0f172a", border: `1px solid ${area.color}88`,
                borderLeft: `3px solid ${area.color}`, borderRadius: 6,
                padding: "8px", zIndex: 10, minHeight: 120,
                boxShadow: "0 4px 20px #00000066",
              }}>
                <input value={editTask} onChange={e => setEditTask(e.target.value)}
                  style={{ ...{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 8px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 6 } }} />
                <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
                  <select value={editStart} onChange={e => setEditStart(e.target.value)}
                    style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "4px 6px", color: "#f1f5f9", fontSize: 11, fontFamily: "inherit" }}>
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={editEnd} onChange={e => setEditEnd(e.target.value)}
                    style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "4px 6px", color: "#f1f5f9", fontSize: 11, fontFamily: "inherit" }}>
                    {TIME_SLOTS.filter(t => timeToMins(t) > timeToMins(editStart)).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <select value={editArea} onChange={e => setEditArea(e.target.value)}
                  style={{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "4px 6px", color: "#f1f5f9", fontSize: 11, fontFamily: "inherit", marginBottom: 6, boxSizing: "border-box" }}>
                  {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
                </select>
                <div style={{ display: "flex", gap: 4 }}>
                  <button onClick={() => onSaveEdit(b.id, { taskName: editTask.trim(), startTime: editStart, endTime: editEnd, area: editArea })}
                    style={{ flex: 1, background: "#10b981", border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 700, padding: "5px", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                  <button onClick={() => onDelete(b.id)}
                    style={{ flex: 1, background: "#ef444422", border: "none", borderRadius: 6, color: "#ef4444", fontSize: 11, fontWeight: 700, padding: "5px", cursor: "pointer", fontFamily: "inherit" }}>Delete</button>
                  <button onClick={onCancelEdit}
                    style={{ background: "#1e293b", border: "none", borderRadius: 6, color: "#475569", fontSize: 11, padding: "5px 8px", cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                </div>
              </div>
            );
          }

          return (
            <div key={b.id} onClick={() => onEdit && onEdit(b.id)} style={{
              position: "absolute", top, left: 4, right: 4, height,
              background: area.color + "28", border: `1px solid ${area.color}55`,
              borderLeft: `3px solid ${area.color}`, borderRadius: 4,
              padding: "2px 6px", overflow: "hidden", display: "flex",
              alignItems: "center", justifyContent: "space-between", gap: 4,
              cursor: onEdit ? "pointer" : "default",
            }}>
              <span style={{ fontSize: 10, color: area.color, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {area.icon} {b.taskName} <span style={{ fontWeight: 400, color: "#475569" }}>{dur}m</span>
              </span>
              {!onEdit && onDelete && (
                <button onClick={e => { e.stopPropagation(); onDelete(b.id); }} style={{
                  background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 0, flexShrink: 0,
                }} onMouseEnter={e => e.target.style.color="#ef4444"} onMouseLeave={e => e.target.style.color="#334155"}>×</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const TAG_TO_AREA = { work: "business", personal: "personal", shopping: "home" };

// ═══════════════════════════════════════════════════════════════════════════════
// MORNING ROUTINE
// ═══════════════════════════════════════════════════════════════════════════════
function MorningRoutine({ habits, habitLogs, setHabitLogs, plan, update, tasks, setTasks, userId, setTab }) {
  const morningHour = new Date().getHours();
  const [expanded, setExpanded] = useState(morningHour >= 4 && morningHour < 11);
  const [feedbackNote, setFeedbackNote] = useState(plan.morningFeedback?.note || "");
  const [feedbackRating, setFeedbackRating] = useState(plan.morningFeedback?.rating || "");
  const [feedbackSaved, setFeedbackSaved] = useState(!!plan.morningFeedback?.savedAt);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editTask, setEditTask] = useState("");
  const [editStart, setEditStart] = useState("06:00");
  const [editEnd, setEditEnd] = useState("07:00");
  const [editArea, setEditArea] = useState("health");
  useEffect(() => {
    if (editingBlockId) {
      const b = (plan.timeBlocks || []).find(x => x.id === editingBlockId);
      if (b) { setEditTask(b.taskName); setEditStart(b.startTime); setEditEnd(b.endTime); setEditArea(b.area || "health"); }
    }
  }, [editingBlockId]); // eslint-disable-line react-hooks/exhaustive-deps

  const habitItems = habits
    .filter(h => h.trackingTime === "morning")
    .map(h => ({
      id: h.id, _type: "habit", name: h.name, icon: h.icon || "🔄",
      area: h.area, target: h.target, unit: h.unit,
    }));

  const MORNING_AREAS = new Set(["health", "habits", "home", "personal", "potential", "family"]);
  const blockItems = (plan.timeBlocks || [])
    .filter(b => b.startTime < "10:00" && MORNING_AREAS.has(b.area))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
    .map(b => ({ ...b, _type: "block" }));

  const taskItems = tasks
    .filter(t => t.isRecurring && t.frequency === "daily")
    .map(t => ({ ...t, _type: "task" }));

  const allItems = [...taskItems, ...blockItems, ...habitItems];
  const morningActual = plan.morningActual || {};

  // 3-state numeric: 0 = empty, 1 = done, 2 = skipped
  // cycle: 0 → 1 → 2 → 0
  async function toggleActual(itemId) {
    const cur = morningActual[itemId] ?? 0;
    const next = cur === 0 ? 1 : cur === 1 ? 2 : 0;
    const upd = { ...morningActual, [itemId]: next };
    await update({ morningActual: upd });
  }

  const doneCount = allItems.filter(item => (morningActual[item.id] ?? 0) === 1).length;
  const skippedCount = allItems.filter(item => (morningActual[item.id] ?? 0) === 2).length;
  const total = allItems.length;
  const pct = total > 0 ? Math.round(doneCount / total * 100) : 0;
  const allComplete = total > 0 && doneCount === total;
  const isEmpty = total === 0;

  // Auto-suggest feedback rating from checkbox states
  const actioned = doneCount + skippedCount;
  const autoRating = actioned === 0 ? "" : doneCount === total ? "nailed" : skippedCount === total ? "missed" : "partial";

  async function saveFeedback() {
    const rating = feedbackRating || autoRating;
    if (!rating) return;
    await update({ morningFeedback: { rating, note: feedbackNote.trim(), savedAt: new Date().toISOString() } });
    setFeedbackSaved(true);
  }

  return (
    <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #f59e0b" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: expanded ? 12 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>🌅</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", letterSpacing: "0.08em" }}>MORNING ROUTINE</span>
          {!expanded && total > 0 && (
            <span style={{ fontSize: 10, color: "#475569" }}>{doneCount}/{total} done</span>
          )}
          {!expanded && allComplete && (
            <span style={{ fontSize: 10, color: "#10b981", fontWeight: 700 }}>✓ complete</span>
          )}
          {!expanded && plan.morningFeedback?.rating && (
            <span style={{ fontSize: 10, color: plan.morningFeedback.rating === "nailed" ? "#10b981" : plan.morningFeedback.rating === "partial" ? "#f59e0b" : "#ef4444" }}>
              {plan.morningFeedback.rating === "nailed" ? "✓ Nailed it" : plan.morningFeedback.rating === "partial" ? "~ Partial" : "✗ Missed it"}
            </span>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "none", color: "#475569", cursor: "pointer",
          fontSize: 13, padding: "2px 4px", fontFamily: "inherit",
        }}>{expanded ? "▲" : "▼"}</button>
      </div>

      {expanded && (
        <>
          {isEmpty ? (
            <div>
              <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>No morning routine set up yet.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {[
                  { label: "→ Add habits in the Habits tab", tab: "Habits" },
                  { label: "→ Add daily tasks in Tasks → Recurring", tab: "Tasks" },
                  { label: "→ Plan your morning in Plan Tomorrow (Day tab)", tab: "Day" },
                ].map(({ label, tab }) => (
                  <button key={tab} onClick={() => setTab?.(tab)} style={{
                    background: "none", border: "none", color: "#f59e0b", cursor: "pointer",
                    fontFamily: "inherit", fontSize: 12, textAlign: "left", padding: "2px 0",
                    textDecoration: "underline",
                  }}>{label}</button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Progress bar */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: "#64748b" }}>
                    {doneCount}/{total} complete{skippedCount > 0 ? `, ${skippedCount} skipped` : ""}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: allComplete ? "#10b981" : "#f59e0b" }}>{pct}%</span>
                </div>
                <div style={{ height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: allComplete ? "#10b981" : "#f59e0b", borderRadius: 2, transition: "width 0.4s" }} />
                </div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 4 }}>Tap: empty → ✓ done → ✗ skipped → empty</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
                {allItems.map(item => {
                  const state = morningActual[item.id] ?? 0; // 0=empty, 1=done, 2=skipped
                  const isDone = state === 1;
                  const isSkipped = state === 2;
                  const isEditing = item._type === "block" && editingBlockId === item.id;
                  const areaColor = (AREA_MAP[item.area] || AREAS[0]).color;

                  if (isEditing) {
                    return (
                      <div key={item.id} style={{ background: "#0f172a", border: `1px solid ${areaColor}88`, borderLeft: `3px solid ${areaColor}`, borderRadius: 8, padding: "10px 12px" }}>
                        <input value={editTask} onChange={e => setEditTask(e.target.value)}
                          style={{ ...inp, fontSize: 13, marginBottom: 8 }} />
                        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                          <select value={editStart} onChange={e => setEditStart(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select value={editEnd} onChange={e => setEditEnd(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {TIME_SLOTS.filter(t => timeToMins(t) > timeToMins(editStart)).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select value={editArea} onChange={e => setEditArea(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => {
                            if (!editTask.trim()) return;
                            const updated = (plan.timeBlocks || []).map(b => b.id === item.id ? { ...b, taskName: editTask.trim(), startTime: editStart, endTime: editEnd, area: editArea } : b);
                            update({ timeBlocks: updated });
                            setEditingBlockId(null);
                          }} style={{ flex: 1, background: "#10b981", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, padding: "6px", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                          <button onClick={() => setEditingBlockId(null)}
                            style={{ background: "#1e293b", border: "none", borderRadius: 6, color: "#475569", fontSize: 12, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", borderRadius: 8, transition: "all 0.2s",
                      background: isDone ? "#10b98108" : isSkipped ? "#ef444408" : "#1e293b",
                      border: `1px solid ${isDone ? "#10b98133" : isSkipped ? "#ef444433" : "transparent"}`,
                      opacity: isDone ? 0.75 : 1,
                    }}>
                      <button onClick={() => toggleActual(item.id)} style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${isDone ? "#10b981" : isSkipped ? "#ef4444" : "#475569"}`,
                        background: isDone ? "#10b981" : isSkipped ? "#ef4444" : "transparent",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 0, transition: "all 0.15s",
                      }}>
                        {isDone && <svg width="11" height="11" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
                        {isSkipped && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 2L8 8M8 2L2 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
                      </button>

                      <div style={{ flex: 1, minWidth: 0, cursor: item._type === "block" ? "pointer" : "default" }}
                        onClick={() => item._type === "block" && setEditingBlockId(item.id)}>
                        <span style={{
                          fontSize: 13,
                          color: isDone ? "#10b98199" : isSkipped ? "#ef444488" : "#e2e8f0",
                          textDecoration: isSkipped ? "line-through" : "none",
                        }}>
                          {item._type === "task" && <span style={{ fontSize: 10, marginRight: 4 }}>🔄</span>}
                          {item._type === "habit" && item.icon && <span style={{ marginRight: 4 }}>{item.icon}</span>}
                          {item._type === "block" && (
                            <span style={{ fontSize: 10, color: "#64748b", marginRight: 6 }}>{item.startTime}–{item.endTime}</span>
                          )}
                          {item.name || item.taskName || item.text}
                        </span>
                        {item._type === "habit" && item.target > 0 && (
                          <span style={{ fontSize: 10, color: "#475569", marginLeft: 6 }}>{item.target}{item.unit}</span>
                        )}
                      </div>

                      <span style={{ fontSize: 9, color: "#334155", fontWeight: 600, letterSpacing: "0.04em", flexShrink: 0 }}>
                        {item._type === "habit" ? "HABIT" : item._type === "block" ? "✎ SCHED" : "DAILY"}
                      </span>
                    </div>
                  );
                })}
              </div>

              {allComplete && (
                <div style={{ textAlign: "center", padding: "8px 0 12px", fontSize: 13, color: "#10b981", fontWeight: 600 }}>
                  🌅 Morning routine complete! Great start to the day.
                </div>
              )}
            </>
          )}

          {/* Morning Feedback — shown after 09:00 or when routine has been touched */}
          {(!isEmpty || morningHour >= 9) && (
            <div style={{ borderTop: "1px solid #1e293b", paddingTop: 12, marginTop: isEmpty ? 0 : 4 }}>
              <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>HOW DID YOUR MORNING GO?</div>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {[
                  { id: "nailed", label: "✓ Nailed it", color: "#10b981" },
                  { id: "partial", label: "~ Partial", color: "#f59e0b" },
                  { id: "missed", label: "✗ Missed it", color: "#ef4444" },
                ].map(opt => {
                  const effective = feedbackRating || autoRating;
                  const isActive = effective === opt.id;
                  const isSuggested = !feedbackRating && autoRating === opt.id;
                  return (
                    <button key={opt.id} onClick={() => { setFeedbackRating(opt.id); setFeedbackSaved(false); }} style={{
                      flex: 1, padding: "7px 4px", borderRadius: 8,
                      border: `1px solid ${isActive ? opt.color : "#1e293b"}`,
                      background: isActive ? opt.color + "20" : "#1e293b",
                      color: isActive ? opt.color : "#475569",
                      cursor: "pointer", fontFamily: "inherit", fontSize: 11,
                      fontWeight: isActive ? 700 : 500,
                      outline: isSuggested ? `1px dashed ${opt.color}88` : "none",
                      outlineOffset: 2,
                      transition: "all 0.15s",
                    }}>{opt.label}</button>
                  );
                })}
              </div>
              <input value={feedbackNote} onChange={e => { setFeedbackNote(e.target.value); setFeedbackSaved(false); }}
                placeholder="What happened? (optional — helps the AI improve)"
                style={{ ...inp, fontSize: 12, marginBottom: 8 }} />
              <button onClick={saveFeedback} disabled={!feedbackRating && !autoRating} style={{
                ...btn(feedbackSaved ? "#10b981" : "#f59e0b", "#000"),
                width: "100%", fontSize: 12, opacity: (feedbackRating || autoRating) ? 1 : 0.4,
              }}>
                {feedbackSaved ? "✓ Morning Feedback Saved" : "Save Morning Feedback"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WorkPhase({ plan, update, userSchedule = {} }) {
  const hour = new Date().getHours();
  const [expanded, setExpanded] = useState(hour >= 11 && hour < 23);
  const [freeText, setFreeText] = useState((plan.workActual || {}).freeText || "");
  const [feedbackNote, setFeedbackNote] = useState(plan.workFeedback?.note || "");
  const [feedbackRating, setFeedbackRating] = useState(plan.workFeedback?.rating || "");
  const [energyLevel, setEnergyLevel] = useState(plan.workFeedback?.energy || 5);
  const [feedbackSaved, setFeedbackSaved] = useState(!!plan.workFeedback?.savedAt);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editTask, setEditTask] = useState("");
  const [editStart, setEditStart] = useState("08:00");
  const [editEnd, setEditEnd] = useState("09:00");
  const [editArea, setEditArea] = useState("business");

  useEffect(() => {
    if (editingBlockId) {
      const b = (plan.timeBlocks || []).find(x => x.id === editingBlockId);
      if (b) { setEditTask(b.taskName); setEditStart(b.startTime); setEditEnd(b.endTime); setEditArea(b.area || "business"); }
    }
  }, [editingBlockId]); // eslint-disable-line react-hooks/exhaustive-deps

  const workStart = userSchedule.workStart || "08:00";

  const workBlocks = (plan.timeBlocks || [])
    .filter(b => (b.area === "business" || b.area === "financial") && b.startTime >= workStart)
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
    .map(b => ({ ...b, _type: "block" }));

  const workActual = plan.workActual || {};

  async function toggleActual(blockId) {
    const cur = workActual[blockId] ?? 0;
    const next = cur === 0 ? 1 : cur === 1 ? 2 : 0;
    await update({ workActual: { ...workActual, [blockId]: next } });
  }

  async function saveFreeText() {
    await update({ workActual: { ...workActual, freeText: freeText.trim() } });
  }

  const doneCount = workBlocks.filter(b => (workActual[b.id] ?? 0) === 1).length;
  const skippedCount = workBlocks.filter(b => (workActual[b.id] ?? 0) === 2).length;
  const total = workBlocks.length;
  const pct = total > 0 ? Math.round(doneCount / total * 100) : 0;
  const allComplete = total > 0 && doneCount === total;

  // Auto-suggest: 80%+ → Productive, 40-79% → Mixed, <40% → Off track
  const actioned = doneCount + skippedCount;
  const autoRating = actioned === 0 ? "" : pct >= 80 ? "productive" : pct >= 40 ? "mixed" : "offtrack";

  async function saveFeedback() {
    const rating = feedbackRating || autoRating;
    if (!rating) return;
    await update({ workFeedback: { rating, energy: energyLevel, note: feedbackNote.trim(), savedAt: new Date().toISOString() } });
    setFeedbackSaved(true);
  }

  return (
    <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #3b82f6" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: expanded ? 12 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>💼</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#3b82f6", letterSpacing: "0.08em" }}>WORK PHASE</span>
          {!expanded && total > 0 && (
            <span style={{ fontSize: 10, color: "#475569" }}>{doneCount}/{total} done</span>
          )}
          {!expanded && allComplete && (
            <span style={{ fontSize: 10, color: "#10b981", fontWeight: 700 }}>✓ complete</span>
          )}
          {!expanded && plan.workFeedback?.rating && (
            <span style={{ fontSize: 10, color: plan.workFeedback.rating === "productive" ? "#10b981" : plan.workFeedback.rating === "mixed" ? "#f59e0b" : "#ef4444" }}>
              {plan.workFeedback.rating === "productive" ? "✓ Productive" : plan.workFeedback.rating === "mixed" ? "~ Mixed" : "✗ Off track"}
            </span>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "none", color: "#475569", cursor: "pointer",
          fontSize: 13, padding: "2px 4px", fontFamily: "inherit",
        }}>{expanded ? "▲" : "▼"}</button>
      </div>

      {expanded && (
        <>
          {total === 0 ? (
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 14 }}>
              No work blocks scheduled. Add business/financial blocks in Plan Tomorrow (Day tab).
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: "#64748b" }}>
                    {doneCount}/{total} complete{skippedCount > 0 ? `, ${skippedCount} skipped` : ""}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: allComplete ? "#10b981" : "#3b82f6" }}>{pct}%</span>
                </div>
                <div style={{ height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: allComplete ? "#10b981" : "#3b82f6", borderRadius: 2, transition: "width 0.4s" }} />
                </div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 4 }}>Tap: empty → ✓ done → ✗ skipped → empty</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 14 }}>
                {workBlocks.map(item => {
                  const state = workActual[item.id] ?? 0;
                  const isDone = state === 1;
                  const isSkipped = state === 2;
                  const isEditing = editingBlockId === item.id;
                  const areaColor = (AREA_MAP[item.area] || AREAS[0]).color;

                  if (isEditing) {
                    return (
                      <div key={item.id} style={{ background: "#0f172a", border: `1px solid ${areaColor}88`, borderLeft: `3px solid ${areaColor}`, borderRadius: 8, padding: "10px 12px" }}>
                        <input value={editTask} onChange={e => setEditTask(e.target.value)}
                          style={{ ...inp, fontSize: 13, marginBottom: 8 }} />
                        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                          <select value={editStart} onChange={e => setEditStart(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select value={editEnd} onChange={e => setEditEnd(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {TIME_SLOTS.filter(t => timeToMins(t) > timeToMins(editStart)).map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <select value={editArea} onChange={e => setEditArea(e.target.value)}
                            style={{ flex: 1, background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "5px 6px", color: "#f1f5f9", fontSize: 12, fontFamily: "inherit" }}>
                            {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
                          </select>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => {
                            if (!editTask.trim()) return;
                            const updated = (plan.timeBlocks || []).map(b => b.id === item.id ? { ...b, taskName: editTask.trim(), startTime: editStart, endTime: editEnd, area: editArea } : b);
                            update({ timeBlocks: updated });
                            setEditingBlockId(null);
                          }} style={{ flex: 1, background: "#10b981", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, fontWeight: 700, padding: "6px", cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                          <button onClick={() => setEditingBlockId(null)}
                            style={{ background: "#1e293b", border: "none", borderRadius: 6, color: "#475569", fontSize: 12, padding: "6px 10px", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={item.id} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", borderRadius: 8, transition: "all 0.2s",
                      background: isDone ? "#10b98108" : isSkipped ? "#ef444408" : "#1e293b",
                      border: `1px solid ${isDone ? "#10b98133" : isSkipped ? "#ef444433" : "transparent"}`,
                      opacity: isDone ? 0.75 : 1,
                    }}>
                      <button onClick={() => toggleActual(item.id)} style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        border: `2px solid ${isDone ? "#10b981" : isSkipped ? "#ef4444" : "#475569"}`,
                        background: isDone ? "#10b981" : isSkipped ? "#ef4444" : "transparent",
                        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 0, transition: "all 0.15s",
                      }}>
                        {isDone && <svg width="11" height="11" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
                        {isSkipped && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 2L8 8M8 2L2 8" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
                      </button>

                      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                        onClick={() => setEditingBlockId(item.id)}>
                        <span style={{
                          fontSize: 13,
                          color: isDone ? "#10b98199" : isSkipped ? "#ef444488" : "#e2e8f0",
                          textDecoration: isSkipped ? "line-through" : "none",
                        }}>
                          <span style={{ fontSize: 10, color: "#64748b", marginRight: 6 }}>{item.startTime}–{item.endTime}</span>
                          {item.taskName}
                        </span>
                      </div>

                      <span style={{ fontSize: 9, color: "#334155", fontWeight: 600, letterSpacing: "0.04em", flexShrink: 0 }}>
                        {item.area === "business" ? "BIZ" : "FIN"} ✎
                      </span>
                    </div>
                  );
                })}
              </div>

              {allComplete && (
                <div style={{ textAlign: "center", padding: "8px 0 12px", fontSize: 13, color: "#10b981", fontWeight: 600 }}>
                  💼 Work phase complete! Great execution.
                </div>
              )}
            </>
          )}

          {/* Free text */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 6 }}>WHAT ACTUALLY HAPPENED TODAY?</div>
            <textarea value={freeText} onChange={e => setFreeText(e.target.value)}
              onBlur={saveFreeText}
              placeholder="Wins, blockers, detours, unexpected work... (auto-saves on blur)"
              rows={3}
              style={{ ...inp, fontSize: 12, resize: "vertical", minHeight: 70 }} />
          </div>

          {/* Work Feedback */}
          <div style={{ borderTop: "1px solid #1e293b", paddingTop: 12 }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>HOW WAS YOUR WORK PHASE?</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[
                { id: "productive", label: "✓ Productive", color: "#10b981" },
                { id: "mixed", label: "~ Mixed", color: "#f59e0b" },
                { id: "offtrack", label: "✗ Off track", color: "#ef4444" },
              ].map(opt => {
                const effective = feedbackRating || autoRating;
                const isActive = effective === opt.id;
                const isSuggested = !feedbackRating && autoRating === opt.id;
                return (
                  <button key={opt.id} onClick={() => { setFeedbackRating(opt.id); setFeedbackSaved(false); }} style={{
                    flex: 1, padding: "7px 4px", borderRadius: 8,
                    border: `1px solid ${isActive ? opt.color : "#1e293b"}`,
                    background: isActive ? opt.color + "20" : "#1e293b",
                    color: isActive ? opt.color : "#475569",
                    cursor: "pointer", fontFamily: "inherit", fontSize: 11,
                    fontWeight: isActive ? 700 : 500,
                    outline: isSuggested ? `1px dashed ${opt.color}88` : "none",
                    outlineOffset: 2,
                    transition: "all 0.15s",
                  }}>{opt.label}</button>
                );
              })}
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>ENERGY LEVEL</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: energyLevel >= 7 ? "#10b981" : energyLevel >= 4 ? "#f59e0b" : "#ef4444" }}>{energyLevel}/10</span>
              </div>
              <input type="range" min={1} max={10} value={energyLevel}
                onChange={e => { setEnergyLevel(Number(e.target.value)); setFeedbackSaved(false); }}
                style={{ width: "100%", accentColor: energyLevel >= 7 ? "#10b981" : energyLevel >= 4 ? "#f59e0b" : "#ef4444" }} />
            </div>

            <input value={feedbackNote} onChange={e => { setFeedbackNote(e.target.value); setFeedbackSaved(false); }}
              placeholder="Key wins or blockers today? (optional)"
              style={{ ...inp, fontSize: 12, marginBottom: 8 }} />
            <button onClick={saveFeedback} disabled={!feedbackRating && !autoRating} style={{
              ...btn(feedbackSaved ? "#10b981" : "#3b82f6", feedbackSaved ? "#000" : "#fff"),
              width: "100%", fontSize: 12, opacity: (feedbackRating || autoRating) ? 1 : 0.4,
            }}>
              {feedbackSaved ? "✓ Work Feedback Saved" : "Save Work Feedback"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TodayView({ goals, dayPlans, setDayPlans, userId, userName, setUserName, tasks = [], setTasks, userQuestions = [], setUserQuestions, userMode = "growth", setTab, habits = [], habitLogs = {}, setHabitLogs, userSchedule = {}, userTagline = "", futureMePhoto = "" }) {
  const [viewOffset, setViewOffset] = useState(0); // 0 = today, -1 = yesterday, etc.
  // Tomorrow planner form state
  const [blockStart, setBlockStart] = useState("08:00");
  const [blockEnd, setBlockEnd] = useState("09:00");
  const [blockTask, setBlockTask] = useState("");
  const [blockArea, setBlockArea] = useState("business");
  // Today planner form state
  const [todayBlockStart, setTodayBlockStart] = useState("08:00");
  const [todayBlockEnd, setTodayBlockEnd] = useState("09:00");
  const [todayBlockTask, setTodayBlockTask] = useState("");
  const [todayBlockArea, setTodayBlockArea] = useState("business");
  const [todayBlockError, setTodayBlockError] = useState("");
  const [editingBlockId, setEditingBlockId] = useState(null);
  // Daily questions state
  const [managingQuestions, setManagingQuestions] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState("");

  const viewedDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + viewOffset);
    return d.toISOString().slice(0, 10);
  })();

  const isToday = viewOffset === 0;
  const key = viewedDate;
  const plan = dayPlans[key] || { intention: "", todos: [], reflection: "", mealPlan: "", kidsTime: "" };

  // Clear a stale Future Me message so it regenerates fresh each new day.
  const todayStr = todayKey();
  useEffect(() => {
    const todayPlan = dayPlans[todayStr];
    if (todayPlan?.futureMeMessageDate && todayPlan.futureMeMessageDate !== todayStr) {
      const u = { ...dayPlans, [todayStr]: { ...todayPlan, futureMeMessage: null, futureMeMessageDate: null } };
      setDayPlans(u); save("dayplans", u, userId);
    }
  }, [todayStr]); // eslint-disable-line react-hooks/exhaustive-deps
  const [todoText, setTodoText] = useState("");
  const [todoArea, setTodoArea] = useState("business");
  const [todoGoal, setTodoGoal] = useState("");
  const [todoPriority, setTodoPriority] = useState("normal");
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(userName || "");

  function viewedDateLabel() {
    if (viewOffset === 0) return "Today";
    if (viewOffset === -1) return "Yesterday";
    const d = new Date();
    d.setDate(d.getDate() + viewOffset);
    return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  }

  async function saveName() {
    setUserName(nameInput);
    await save("username", nameInput, userId);
    setEditingName(false);
  }

  // Tomorrow's date key (only relevant when viewing today)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKeyStr = tomorrow.toISOString().slice(0, 10);
  const tomorrowPlan = dayPlans[tomorrowKeyStr] || { intention: "", mealPlan: "", kidsTime: "" };

  async function update(patch) {
    const u = { ...dayPlans, [key]: { ...plan, ...patch } };
    setDayPlans(u); await save("dayplans", u, userId);
  }

  async function updateTomorrow(patch) {
    const u = { ...dayPlans, [tomorrowKeyStr]: { ...tomorrowPlan, ...patch } };
    setDayPlans(u); await save("dayplans", u, userId);
  }

  // AI planner
  const [aiPlanLoading, setAiPlanLoading] = useState(false);
  const [aiPlanMsg, setAiPlanMsg] = useState(""); // "" | "success" | "error"

  async function planWithAI() {
    setAiPlanLoading(true);
    setAiPlanMsg("");
    const tomorrowDayName = new Date(tomorrowKeyStr + "T12:00:00").toLocaleDateString([], { weekday: "long" });
    const planningPrompt = `You are an expert daily planner and life coach. Build an optimal schedule for tomorrow for this user.

THEIR GOALS (prioritize actions that move these forward):
${goals.length ? goals.map(g => `- ${g.text} (${g.progress || 0}% done, area: ${(AREA_MAP[g.area] || AREAS[0]).label})`).join('\n') : "No goals set yet."}

THEIR DAILY HABITS (must be scheduled at the right time):
${habits.length ? habits.map(h => `- ${h.name} | ${h.target}${h.unit} | tracking: ${h.trackingTime} | freq: ${h.freq}`).join('\n') : "No habits set yet."}

THEIR OPEN TASKS (include the most important ones):
${tasks.filter(t => !t.done && t.when === 'today').slice(0, 10).map(t => `- ${t.text}${t.urgent ? ' [URGENT]' : ''}${t.estimatedMins ? ` (~${t.estimatedMins}min)` : ''}`).join('\n') || "No open tasks."}

THEIR SCHEDULE CONSTRAINTS:
- Wake up: ${userSchedule.wakeTime || '04:00'}
- Work starts: ${userSchedule.workStart || '08:00'}
- Home from work: ${userSchedule.workEnd || '18:00'}
- Kids done / free time: ${userSchedule.kidsEnd || '20:30'}
- Sleep by: ${userSchedule.sleepTime || '22:00'}
- Exercise: ${userSchedule.exerciseDays || '3x'} per week
- Fixed commitments: ${userSchedule.fixedCommitments || 'none'}

THEIR CURRENT MODE: ${userMode || 'growth'}
${userMode === 'fight' ? 'They are under pressure — prioritize high-impact work blocks.' : ''}
${userMode === 'support' ? 'They are struggling — keep the schedule realistic and achievable.' : ''}

RECENT MORNING ROUTINE FEEDBACK (last 3 days):
${(() => {
  const lines = [];
  for (let i = 1; i <= 3; i++) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const k = d.toISOString().slice(0, 10);
    const fb = (dayPlans[k] || {}).morningFeedback;
    if (fb?.rating) lines.push(`- ${k}: ${fb.rating === 'nailed' ? '✓ Nailed it' : fb.rating === 'partial' ? '~ Partial' : '✗ Missed it'}${fb.note ? ` — "${fb.note}"` : ''}`);
  }
  return lines.length ? lines.join('\n') + '\nUse this to calibrate the morning routine — if they consistently miss items, suggest fewer or easier blocks. If they nail it, you can add more.' : 'No morning feedback yet.';
})()}

YESTERDAY'S WORK PHASE FEEDBACK:
${(() => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  const k = d.toISOString().slice(0, 10);
  const fb = (dayPlans[k] || {}).workFeedback;
  const wa = (dayPlans[k] || {}).workActual;
  if (!fb?.rating && !wa?.freeText) return 'No work phase feedback yesterday.';
  const parts = [
    fb?.rating === 'productive' ? '✓ Productive' : fb?.rating === 'mixed' ? '~ Mixed' : fb?.rating === 'offtrack' ? '✗ Off track' : '',
    fb?.energy ? `energy: ${fb.energy}/10` : '',
    fb?.note || '',
    wa?.freeText ? `"${wa.freeText}"` : '',
  ].filter(Boolean);
  return parts.join(' | ');
})()}
Use this to calibrate tomorrow's work blocks — if energy was low, schedule lighter tasks or a recovery block; if they were off track, front-load the single most important task; if productive, maintain the pattern.

TOMORROW IS: ${tomorrowKeyStr} (${tomorrowDayName})

IMPORTANT RULES FOR MORNING ROUTINE (before work starts):
- Include ONLY personal habits and self-care activities before work: wake up, meditation, exercise, study, breakfast, morning hygiene
- Do NOT include any work tasks, business calls, emails, or professional activities in the morning routine blocks
- Work tasks start only from ${userSchedule.workStart || '08:00'}
- Morning routine ends at ${userSchedule.workStart || '08:00'}

BUILD THE SCHEDULE:
Create a realistic time-blocked schedule for tomorrow.
Rules:
- Start from wake up time
- Include all morning habits in the right order
- Include travel/transition time between activities
- Block deep work in the morning when energy is highest (after ${userSchedule.workStart || '08:00'})
- Include the most urgent/important tasks
- Protect family time after ${userSchedule.workEnd || '18:00'}
- Include the alicanb evening ritual at 21:00-21:15
- End at sleep time
- Be realistic — do not over-schedule
- If tomorrow is a weekend day, adjust accordingly

RESPOND WITH ONLY a JSON array of time blocks, no other text:
[
  {
    "startTime": "05:00",
    "endTime": "05:30",
    "taskName": "Morning routine",
    "area": "health"
  }
]

Area must be one of: business, financial, potential, habits, health, family, home, personal`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-opus-4-5",
          max_tokens: 3000,
          messages: [{ role: "user", content: planningPrompt }]
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "";
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error("No JSON array in response");
      const blocks = JSON.parse(jsonMatch[0]);
      const timeBlocks = blocks.map(b => ({ id: uid(), startTime: b.startTime, endTime: b.endTime, taskName: b.taskName, area: b.area }));
      await updateTomorrow({ timeBlocks });
      setAiPlanMsg("success");
    } catch {
      setAiPlanMsg("error");
    }
    setAiPlanLoading(false);
  }

  async function addTodo() {
    if (!todoText.trim()) return;
    const t = { id: uid(), text: todoText.trim(), area: todoArea, linkedGoal: todoGoal, priority: todoPriority, done: false, createdAt: new Date().toISOString() };
    await update({ todos: [t, ...(plan.todos || [])] });
    setTodoText(""); setTodoPriority("normal");
  }

  async function toggleTodo(id) {
    await update({ todos: (plan.todos || []).map(t => t.id === id ? { ...t, done: !t.done, doneAt: new Date().toISOString() } : t) });
  }

  async function delTodo(id) {
    await update({ todos: (plan.todos || []).filter(t => t.id !== id) });
  }

  const todos = plan.todos || [];
  const done = todos.filter(t => t.done).length;
  const pct = todos.length ? Math.round(done / todos.length * 100) : 0;

  // Inspiration panel
  const INSPIRATION_STATIC = [
    { icon: "🧠", text: "Locke's Goal Setting Theory: Specific goals with deadlines outperform vague intentions by up to 250%." },
    { icon: "📊", text: "Drucker: What gets measured gets managed. What gets logged gets improved." },
    { icon: "📌", text: "Implementation Intentions: Deciding when and where you will do something doubles your follow-through rate." },
    { icon: "🔗", text: "Habit Stacking: Attaching a new habit to an existing one makes it 3x more likely to stick." },
    { icon: "📈", text: "The Progress Principle: Small daily wins create more motivation than big occasional victories." },
    { icon: "⚡", text: "CBT Behavioral Activation: Action comes before motivation, not after. Start small — momentum follows." },
    { icon: "🔄", text: "Zeigarnik Effect: Unfinished tasks create mental tension that drives completion. Use that pressure." },
    { icon: "🗓", text: "Time Blocking: Assigning tasks to specific time slots increases completion rate significantly." },
    { icon: "⚡", text: "Energy Management: Matching important work to peak energy hours doubles output quality." },
    { icon: "🔁", text: "Permanence principle: Small repeatable daily actions, combined with honest self-reflection, create lasting change." },
    { icon: "❓", text: "Daily Questions: Asking 'did I do my best today' shifts awareness from hindsight to real-time choice." },
    { icon: "➡️", text: "Feedforward: Instead of reviewing what went wrong, ask what you can try differently tomorrow." },
    { icon: "💡", text: "The days you skip logging are the most important days to analyze. Run the analysis anyway." },
    { icon: "🗑", text: "A task on your list for more than a week needs to be done, delegated, or deleted." },
    { icon: "🌙", text: "Your evening reflection is more valuable than your morning intention. Don't skip it." },
    { icon: "🎯", text: "Link every task to a goal. Every action should serve something that matters." },
    { icon: "✦", text: "The ✦ AI button works best on tasks you are avoiding." },
    { icon: "📭", text: "Missing data tells a story. Pay attention to what you consistently skip." },
    { icon: "🔥", text: "Run the analysis even on bad days — especially on bad days." },
  ];
  const inspirationPool = [...INSPIRATION_STATIC];
  // Dynamic personal achievements
  const thisWeekDone = tasks.filter(t => t.done && t.doneAt && t.doneAt >= new Date(Date.now() - 7*24*60*60*1000).toISOString()).length;
  if (thisWeekDone > 5) inspirationPool.push({ icon: "✓", text: `${thisWeekDone} tasks completed this week. Momentum is building.` });
  if (goals.length >= 3) inspirationPool.push({ icon: "◎", text: `You are tracking ${goals.length} active goals. You know where you are going.` });
  // Best habit streak
  const bestStreak = habits.reduce((best, h) => {
    let streak = 0, d = new Date();
    while (true) {
      const k = d.toISOString().slice(0,10);
      if ((habitLogs[k] || []).includes(h.id)) { streak++; d.setDate(d.getDate()-1); } else break;
    }
    return streak > best.streak ? { streak, name: h.name } : best;
  }, { streak: 0, name: "" });
  if (bestStreak.streak >= 7) inspirationPool.push({ icon: "🔥", text: `${bestStreak.name} streak: ${bestStreak.streak} days. That is real discipline.` });

  const todayNum = parseInt(todayKey().replace(/-/g, ""), 10);
  const inspirationItem = inspirationPool[todayNum % inspirationPool.length];
  const inspirationDismissKey = `alicanb-inspiration-${todayKey()}`;
  const [inspirationDismissed, setInspirationDismissed] = useState(
    () => localStorage.getItem(inspirationDismissKey) === "1"
  );
  function dismissInspiration() {
    localStorage.setItem(inspirationDismissKey, "1");
    setInspirationDismissed(true);
  }

  // Mode-based daily messages
  const MODE_MESSAGES = {
    fight: [
      "You are in Fight Mode. Every hour today is a weapon. Use it.",
      "Pressure reveals character. Show up anyway.",
      "The gap between where you are and where you want to be closes today. One action at a time.",
      "Your competition is not resting. Neither should your focus.",
      "Hard days build the person who handles hard days. This is that day.",
      "Execute first. Feel good about it later.",
      "Nobody is coming to save you. You are the plan.",
      "Fight Mode means no wasted hours. What is the most important thing right now?",
      "The discomfort you feel is the distance closing. Keep going.",
      "Today's discipline is tomorrow's freedom.",
      "You did not come this far to slow down now.",
      "One focused hour beats three distracted ones. Choose focused.",
      "The work does not care how you feel. Do it anyway.",
      "Small actions compounded daily. That is the entire strategy.",
      "Show up. Log it. Analyze it. Repeat.",
    ],
    support: [
      "Today does not have to be perfect. It just has to happen.",
      "You are showing up. That already counts.",
      "One small thing done today is better than nothing waiting for a better day.",
      "Be honest with yourself and gentle at the same time. Both are possible.",
      "The goal is not to be perfect. The goal is to keep going.",
      "You have come further than you give yourself credit for.",
      "Start where you are. Use what you have. Do what you can.",
      "A slow day is not a lost day. Rest if you need to. Then continue.",
      "The fact that you opened this app today means something.",
      "Progress is not always visible. Keep planting.",
      "You are not behind. You are exactly where the work begins.",
      "Be the friend to yourself that you would be to someone you love.",
      "Small wins count. Write one down today.",
      "You do not have to fix everything today. Just do the next right thing.",
      "Kindness toward yourself is not weakness. It is strategy.",
    ],
    growth: [
      "Consistent beats brilliant. Show up again today.",
      "You are building something. Stay patient and keep building.",
      "What you do today compounds into who you are in a year.",
      "Growth is not loud. It is the quiet work done when nobody is watching.",
      "Every habit logged is a vote for the person you are becoming.",
      "The system works if you work the system. Trust the process.",
      "Check your goals today. Are your actions pointing toward them?",
      "A good day starts with a clear intention. Write yours now.",
      "You are closing the gap. Slowly, then suddenly.",
      "The best investment you can make today is in your own discipline.",
      "Small daily actions. That is the entire secret.",
      "Momentum is built one logged day at a time. Keep the streak alive.",
      "What would the best version of you do in the next hour?",
      "Steady and honest. That is the alicanb way.",
      "You chose to track your life. That decision already separates you.",
    ],
  };
  const dayIndex = new Date().getDate() + new Date().getMonth() * 31;
  const modeMessages = MODE_MESSAGES[userMode] || [];
  const modeMsg = modeMessages.length ? modeMessages[dayIndex % modeMessages.length] : null;
  const modeMsgDismissKey = `alicanb-modemsg-${userMode}-${todayKey()}`;
  const [modeMsgDismissed, setModeMsgDismissed] = useState(
    () => localStorage.getItem(modeMsgDismissKey) === "1"
  );
  function dismissModeMsg() {
    localStorage.setItem(modeMsgDismissKey, "1");
    setModeMsgDismissed(true);
  }
  const modeMeta = {
    fight:   { icon: "⚔️", label: "FIGHT MODE",   border: "#ef4444", bg: "#1a0000", color: "#fca5a5" },
    support: { icon: "🤝", label: "SUPPORT MODE", border: "#8b5cf6", bg: "#120a1e", color: "#c4b5fd" },
    growth:  { icon: "🌱", label: "GROWTH MODE",  border: "#10b981", bg: "#011a10", color: "#6ee7b7" },
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      {/* Future Me — pinned to the very top in Fight Mode */}
      {isToday && futureMePhoto && userMode === "fight" && (
        <FutureMe futureMePhoto={futureMePhoto} plan={plan} update={update} userMode={userMode} goals={goals} habits={habits} habitLogs={habitLogs} userId={userId} />
      )}

      {/* Date navigator */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 16, background: "#0f172a", borderRadius: 12, padding: "10px 16px" }}>
        <button onClick={() => setViewOffset(v => v - 1)} style={{
          background: "none", border: "1px solid #1e293b", borderRadius: 8,
          color: "#64748b", cursor: "pointer", fontSize: 18, width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>‹</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{viewedDateLabel()}</div>
          <div style={{ fontSize: 11, color: "#475569" }}>{viewedDate}</div>
        </div>
        <button onClick={() => setViewOffset(v => Math.min(0, v + 1))}
          disabled={isToday}
          style={{
            background: "none", border: "1px solid #1e293b", borderRadius: 8,
            color: isToday ? "#1e293b" : "#64748b",
            cursor: isToday ? "default" : "pointer",
            fontSize: 18, width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>›</button>
      </div>

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>{fmtDate(key)}</h2>
        <p style={{ margin: "0 0 10px", color: "#f59e0b", fontSize: 13, fontStyle: "italic", fontWeight: 600 }}>
          "Design your day before it designs you."
        </p>
        {/* Name setup */}
        {!userName && !editingName && (
          <div style={{ background: "#0f172a", border: "1px solid #f59e0b44", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#64748b" }}>👋 What should we call you?</span>
            <button onClick={() => setEditingName(true)} style={{ ...btn("#f59e0b", "#000"), padding: "4px 12px", fontSize: 12 }}>Set my name</button>
          </div>
        )}
        {editingName && (
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input value={nameInput} onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && saveName()}
              placeholder="Your first name..." style={{ ...inp, flex: 1 }} autoFocus />
            <button onClick={saveName} style={{ ...btn("#10b981"), padding: "8px 16px" }}>Save</button>
            <button onClick={() => setEditingName(false)} style={{ ...btn("#1e293b", "#64748b"), padding: "8px 12px" }}>✕</button>
          </div>
        )}
        {userName && !editingName && (
          <span style={{ fontSize: 11, color: "#334155", cursor: "pointer" }} onClick={() => { setEditingName(true); setNameInput(userName); }}>
            Hi {userName} · <span style={{ textDecoration: "underline" }}>change name</span>
          </span>
        )}
        {/* Mode indicator */}
        {setTab && (
          <button onClick={() => setTab("Settings")} style={{
            display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8,
            background: "none", border: `1px solid ${userMode === "fight" ? "#ef444433" : userMode === "support" ? "#3b82f633" : "#10b98133"}`,
            borderRadius: 20, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit",
            color: userMode === "fight" ? "#ef4444" : userMode === "support" ? "#3b82f6" : "#10b981",
            fontSize: 11, fontWeight: 600,
          }}>
            {userMode === "fight" ? "⚔️ FIGHT MODE" : userMode === "support" ? "🤝 SUPPORT MODE" : "🌱 GROWTH MODE"}
            <span style={{ fontSize: 9, color: "#334155" }}>· tap to change</span>
          </button>
        )}
      </div>

      {/* Morning Routine */}
      {isToday && setHabitLogs && (
        <MorningRoutine
          habits={habits}
          habitLogs={habitLogs}
          setHabitLogs={setHabitLogs}
          plan={plan}
          update={update}
          tasks={tasks}
          setTasks={setTasks}
          userId={userId}
          setTab={setTab}
        />
      )}

      {/* Work Phase */}
      {isToday && (
        <WorkPhase
          plan={plan}
          update={update}
          userSchedule={userSchedule}
        />
      )}

      {/* Mode-based daily message */}
      {isToday && modeMsg && !modeMsgDismissed && modeMeta[userMode] && (
        <div style={{
          background: modeMeta[userMode].bg,
          border: `2px solid ${modeMeta[userMode].border}`,
          borderRadius: 12, padding: "14px 18px", marginBottom: 12,
          animation: "fadeIn 0.4s ease",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: modeMeta[userMode].border, letterSpacing: "0.08em" }}>
              {modeMeta[userMode].icon} {modeMeta[userMode].label}
            </span>
            <button onClick={dismissModeMsg} style={{
              background: "none", border: "none", color: "#334155", cursor: "pointer",
              fontSize: 16, lineHeight: 1, padding: "0 2px",
            }}>×</button>
          </div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: modeMeta[userMode].color, lineHeight: 1.6 }}>
            {modeMsg}
          </p>
        </div>
      )}

      {/* Future Me — message from your future self (non-Fight modes) */}
      {isToday && futureMePhoto && userMode !== "fight" && (
        <FutureMe futureMePhoto={futureMePhoto} plan={plan} update={update} userMode={userMode} goals={goals} habits={habits} habitLogs={habitLogs} userId={userId} />
      )}

      {/* Personal tagline */}
      {isToday && userTagline && (
        <div style={{ fontSize: 12, color: "#f59e0b", fontStyle: "italic", textAlign: "center", marginBottom: 12 }}>
          {userTagline.length > 60 ? userTagline.slice(0, 60) + "…" : userTagline}
        </div>
      )}

      {/* Inspiration panel */}
      {isToday && (
        inspirationDismissed ? (
          <button onClick={() => { localStorage.removeItem(inspirationDismissKey); setInspirationDismissed(false); }} style={{
            display: "block", marginBottom: 10, background: "none", border: "none",
            color: "#334155", cursor: "pointer", fontFamily: "inherit", fontSize: 11,
          }}>💡</button>
        ) : (
          <div style={{
            ...card, marginBottom: 12, borderLeft: "3px solid #f59e0b",
            display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px",
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{inspirationItem.icon}</span>
            <span style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6, flex: 1 }}>{inspirationItem.text}</span>
            <button onClick={dismissInspiration} style={{
              background: "none", border: "none", color: "#334155", cursor: "pointer",
              fontSize: 14, lineHeight: 1, padding: "0 2px", flexShrink: 0,
            }} onMouseEnter={e => e.target.style.color="#64748b"} onMouseLeave={e => e.target.style.color="#334155"}>×</button>
          </div>
        )
      )}

      {/* TODAY'S SCHEDULE — editable */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #3b82f6" }}>
        <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>⏰ TODAY'S SCHEDULE</div>
        {/* Add block form */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>FROM</div>
              <select value={todayBlockStart} onChange={e => {
                const s = e.target.value;
                setTodayBlockStart(s);
                if (timeToMins(todayBlockEnd) <= timeToMins(s)) {
                  const next = TIME_SLOTS.find(t => timeToMins(t) > timeToMins(s));
                  if (next) setTodayBlockEnd(next);
                }
                setTodayBlockError("");
              }} style={{ ...inp, fontSize: 12, padding: "7px 8px" }}>
                {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>TO</div>
              <select value={todayBlockEnd} onChange={e => { setTodayBlockEnd(e.target.value); setTodayBlockError(""); }} style={{ ...inp, fontSize: 12, padding: "7px 8px" }}>
                {TIME_SLOTS.filter(t => timeToMins(t) > timeToMins(todayBlockStart)).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>TASK / ACTIVITY</div>
            <input value={todayBlockTask} onChange={e => setTodayBlockTask(e.target.value)}
              placeholder="What did you / will you do?" style={{ ...inp, fontSize: 13, marginBottom: 8 }} />
            {tasks.filter(t => !t.done && t.when === "today").length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {tasks.filter(t => !t.done && t.when === "today").map(t => {
                  const area = AREA_MAP[TAG_TO_AREA[t.tag] || "business"] || AREAS[0];
                  return (
                    <button key={t.id} onClick={() => { setTodayBlockTask(t.text); setTodayBlockArea(TAG_TO_AREA[t.tag] || "business"); }}
                      style={{ fontSize: 11, padding: "3px 9px", borderRadius: 20, border: `1px solid ${area.color}44`, background: area.color + "15", color: area.color, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}
                      onMouseEnter={e => e.currentTarget.style.background = area.color + "30"}
                      onMouseLeave={e => e.currentTarget.style.background = area.color + "15"}>
                      {area.icon} {t.text.slice(0, 28)}{t.text.length > 28 ? "…" : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={todayBlockArea} onChange={e => setTodayBlockArea(e.target.value)} style={{ ...inp, flex: 1, fontSize: 12, padding: "7px 8px" }}>
              {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
            </select>
            <button onClick={() => {
              if (!todayBlockTask.trim()) { setTodayBlockError("Enter a task or activity name."); return; }
              if (timeToMins(todayBlockEnd) <= timeToMins(todayBlockStart)) { setTodayBlockError("End time must be after start time."); return; }
              const block = { id: uid(), startTime: todayBlockStart, endTime: todayBlockEnd, taskName: todayBlockTask.trim(), area: todayBlockArea };
              const existing = plan.timeBlocks || [];
              update({ timeBlocks: [...existing, block].sort((a, b) => a.startTime.localeCompare(b.startTime)) });
              setTodayBlockTask("");
              setTodayBlockStart("08:00");
              setTodayBlockEnd("09:00");
              setTodayBlockError("");
            }} style={{ ...btn("#3b82f6"), padding: "8px 18px", fontSize: 13, flexShrink: 0 }}>+ Add</button>
          </div>
          {todayBlockError && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>{todayBlockError}</div>}
        </div>
        {(plan.timeBlocks || []).length > 0 ? (
          <TimeBlockSchedule
            blocks={plan.timeBlocks} areaMap={AREA_MAP}
            editingId={editingBlockId}
            onEdit={id => setEditingBlockId(id)}
            onCancelEdit={() => setEditingBlockId(null)}
            onSaveEdit={(id, patch) => {
              const updated = (plan.timeBlocks || []).map(b => b.id === id ? { ...b, ...patch } : b).sort((a, b) => a.startTime.localeCompare(b.startTime));
              update({ timeBlocks: updated }); setEditingBlockId(null);
            }}
            onDelete={id => { const updated = (plan.timeBlocks || []).filter(b => b.id !== id); update({ timeBlocks: updated }); setEditingBlockId(null); }}
          />
        ) : (
          <div style={{ fontSize: 12, color: "#334155", textAlign: "center", padding: "12px 0" }}>No blocks yet. Add your first block above.</div>
        )}
      </div>

      {/* Morning intention */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #f59e0b" }}>
        <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>☀ MORNING — INTENTION</div>
        <textarea value={plan.intention || ""} onChange={e => update({ intention: e.target.value })}
          placeholder="What is the single most important thing today? What does a successful day look like?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
      </div>

      {/* Meal plan */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #fb923c" }}>
        <div style={{ fontSize: 11, color: "#fb923c", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>🏠 MEAL PLAN TODAY</div>
        <textarea value={plan.mealPlan || ""} onChange={e => update({ mealPlan: e.target.value })}
          placeholder="What are you cooking? Did you prep in advance? What do the kids eat tonight?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
      </div>

      {/* Kids time */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #f472b6" }}>
        <div style={{ fontSize: 11, color: "#f472b6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>👨‍👧 TIME WITH KIDS</div>
        <textarea value={plan.kidsTime || ""} onChange={e => update({ kidsTime: e.target.value })}
          placeholder="What do you plan to do with the kids today? What do you want to teach them?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
      </div>

      {/* Add task — only on today */}
      {isToday && <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>+ ADD TASK</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input value={todoText} onChange={e => setTodoText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTodo()}
            placeholder="Task..." style={{ ...inp, flex: 1 }} />
          <button onClick={addTodo} style={btn("#3b82f6")}>Add</button>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={todoArea} onChange={e => setTodoArea(e.target.value)} style={{ ...inp, flex: 1 }}>
            {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
          </select>
          <select value={todoGoal} onChange={e => setTodoGoal(e.target.value)} style={{ ...inp, flex: 1 }}>
            <option value="">— Link to goal —</option>
            {goals.map(g => <option key={g.id} value={g.id}>{g.text.slice(0, 40)}</option>)}
          </select>
          <select value={todoPriority} onChange={e => setTodoPriority(e.target.value)} style={{ ...inp, flex: 1 }}>
            <option value="normal">📋 Normal</option>
            <option value="important">⭐ Important</option>
            <option value="urgent">🔥 Urgent</option>
            <option value="critical">🚨 Critical</option>
          </select>
        </div>
      </div>}

      {/* Task list */}
      {todos.length > 0 && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.1em" }}>TASKS — {done}/{todos.length}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 80, height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: pct + "%", height: "100%", background: "#10b981", transition: "width 0.3s" }} />
              </div>
              <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>{pct}%</span>
            </div>
          </div>
          {todos.map(t => {
            const area = AREA_MAP[t.area] || AREAS[0];
            const linked = goals.find(g => g.id === t.linkedGoal);
            return (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e293b" }}>
                <button onClick={() => toggleTodo(t.id)} style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0, padding: 0, cursor: "pointer",
                  border: `2px solid ${t.done ? area.color : "#334155"}`, background: t.done ? area.color : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {t.done && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" /></svg>}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: t.done ? "#334155" : "#cbd5e1", textDecoration: t.done ? "line-through" : "none" }}>{t.text}</div>
                  <div style={{ fontSize: 11, color: "#334155", marginTop: 1, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ color: area.color }}>{area.icon} {area.label}</span>
                    {linked && <span style={{ color: "#475569" }}>→ {linked.text.slice(0, 25)}</span>}
                    {t.priority && t.priority !== "normal" && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
                        background: t.priority === "critical" ? "#ef444422" : t.priority === "urgent" ? "#f9731622" : "#f59e0b22",
                        color: t.priority === "critical" ? "#ef4444" : t.priority === "urgent" ? "#f97316" : "#f59e0b",
                      }}>
                        {t.priority === "critical" ? "🚨 Critical" : t.priority === "urgent" ? "🔥 Urgent" : "⭐ Important"}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => delTodo(t.id)} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 16 }}
                  onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Evening reflection */}
      <div style={{ ...card, borderLeft: "3px solid #8b5cf6", marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>🌙 EVENING — REFLECTION</div>
        <textarea value={plan.reflection || ""} onChange={e => update({ reflection: e.target.value })}
          placeholder="What actually happened today? What did you avoid? What slipped through unplanned? What will you fix tomorrow?"
          rows={3} style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
      </div>

      {/* Daily Questions */}
      {isToday && (() => {
        const scores = plan.dailyScores || [];
        const allAnswered = userQuestions.length > 0 && userQuestions.every(q => scores.find(s => s.questionId === q.id));
        // Yesterday's scores for trend
        const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
        const yKey = yesterday.toISOString().slice(0, 10);
        const yScores = (dayPlans[yKey] || {}).dailyScores || [];

        function setScore(q, val) {
          const existing = scores.filter(s => s.questionId !== q.id);
          update({ dailyScores: [...existing, { questionId: q.id, question: q.text, score: val }] });
        }

        function scoreColor(v) {
          if (v >= 8) return "#10b981"; if (v >= 6) return "#84cc16";
          if (v >= 4) return "#f59e0b"; if (v >= 2) return "#f97316";
          return "#ef4444";
        }

        return (
          <div style={{ ...card, borderLeft: "3px solid #a78bfa", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#a78bfa", fontWeight: 700, letterSpacing: "0.1em" }}>
                ✦ DAILY QUESTIONS {allAnswered && <span style={{ color: "#10b981", marginLeft: 6 }}>✓ complete</span>}
              </div>
              <button onClick={() => setManagingQuestions(m => !m)}
                style={{ fontSize: 10, background: "#1e293b", border: "none", borderRadius: 6, color: "#475569", padding: "3px 9px", cursor: "pointer", fontFamily: "inherit" }}>
                {managingQuestions ? "Done" : "Manage"}
              </button>
            </div>

            {/* Manage questions panel */}
            {managingQuestions && (
              <div style={{ marginBottom: 14, padding: "10px 12px", background: "#080f1e", borderRadius: 8 }}>
                {userQuestions.map((q, i) => (
                  <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ flex: 1, fontSize: 12, color: "#94a3b8" }}>{i + 1}. {q.text}</span>
                    <button onClick={async () => {
                      const updated = userQuestions.filter(x => x.id !== q.id);
                      setUserQuestions(updated); await save("daily-questions", updated, userId);
                    }} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 14, padding: "0 2px" }}
                    onMouseEnter={e => e.target.style.color="#ef4444"} onMouseLeave={e => e.target.style.color="#334155"}>×</button>
                  </div>
                ))}
                {userQuestions.length < 5 && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <input value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)}
                      onKeyDown={async e => {
                        if (e.key === "Enter" && newQuestionText.trim()) {
                          const updated = [...userQuestions, { id: uid(), text: newQuestionText.trim() }];
                          setUserQuestions(updated); await save("daily-questions", updated, userId); setNewQuestionText("");
                        }
                      }}
                      placeholder="Add a question..." style={{ ...inp, flex: 1, fontSize: 12, padding: "6px 8px" }} />
                    <button onClick={async () => {
                      if (!newQuestionText.trim()) return;
                      const updated = [...userQuestions, { id: uid(), text: newQuestionText.trim() }];
                      setUserQuestions(updated); await save("daily-questions", updated, userId); setNewQuestionText("");
                    }} style={{ ...btn("#a78bfa", "#000"), padding: "6px 12px", fontSize: 12 }}>+ Add</button>
                  </div>
                )}
              </div>
            )}

            {/* Setup state: no questions yet */}
            {userQuestions.length === 0 && !managingQuestions && (
              <div style={{ padding: "12px", background: "#080f1e", borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, marginBottom: 10 }}>
                  Set 3–5 personal questions you'll answer every evening. Focus on effort, not outcome.<br/>
                  <span style={{ color: "#475569", fontStyle: "italic" }}>Example: "Did I do my best to focus on my most important work today?"</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === "Enter" && newQuestionText.trim()) {
                        const updated = [...userQuestions, { id: uid(), text: newQuestionText.trim() }];
                        setUserQuestions(updated); await save("daily-questions", updated, userId); setNewQuestionText("");
                      }
                    }}
                    placeholder="Your first question..." style={{ ...inp, flex: 1, fontSize: 12, padding: "7px 8px" }} autoFocus />
                  <button onClick={async () => {
                    if (!newQuestionText.trim()) return;
                    const updated = [...userQuestions, { id: uid(), text: newQuestionText.trim() }];
                    setUserQuestions(updated); await save("daily-questions", updated, userId); setNewQuestionText("");
                  }} style={{ ...btn("#a78bfa", "#000"), padding: "7px 14px", fontSize: 12 }}>+ Add</button>
                </div>
              </div>
            )}

            {/* Yesterday's summary */}
            {yScores.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                <span style={{ fontSize: 10, color: "#334155", alignSelf: "center" }}>Yesterday:</span>
                {yScores.map(s => (
                  <span key={s.questionId} style={{ fontSize: 10, fontWeight: 700, color: scoreColor(s.score), background: scoreColor(s.score) + "18", padding: "2px 7px", borderRadius: 10 }}>
                    {s.score}/10
                  </span>
                ))}
                <span style={{ fontSize: 10, color: "#334155", alignSelf: "center" }}>avg {yScores.length ? Math.round(yScores.reduce((s, x) => s + x.score, 0) / yScores.length * 10) / 10 : "—"}</span>
              </div>
            )}

            {/* Daily scoring sliders */}
            {userQuestions.map(q => {
              const entry = scores.find(s => s.questionId === q.id);
              const val = entry?.score ?? 0;
              return (
                <div key={q.id} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6, lineHeight: 1.5 }}>{q.text}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[1,2,3,4,5,6,7,8,9,10].map(v => (
                        <button key={v} onClick={() => setScore(q, v)} style={{
                          width: 26, height: 26, borderRadius: 6, border: "none", cursor: "pointer",
                          fontFamily: "inherit", fontSize: 10, fontWeight: 700,
                          background: v <= val ? scoreColor(val) + "33" : "#1e293b",
                          color: v <= val ? scoreColor(val) : "#334155",
                          outline: v === val ? `2px solid ${scoreColor(val)}` : "none",
                          transition: "all 0.1s",
                        }}>{v}</button>
                      ))}
                    </div>
                    {val > 0 && <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(val), minWidth: 28 }}>{val}/10</span>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Plan Tomorrow — only on today */}
      {isToday && <div style={{ ...card, borderLeft: "3px solid #f59e0b", marginBottom: 12, background: "#080f1e" }}>
        <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4 }}>📅 PLAN TOMORROW — do this tonight</div>
        <div style={{ fontSize: 11, color: "#334155", marginBottom: 12 }}>Set your intention now, before sleep. Tomorrow starts tonight.</div>
        <textarea value={tomorrowPlan.intention || ""} onChange={e => updateTomorrow({ intention: e.target.value })}
          placeholder="What is the ONE most important thing for tomorrow? What kind of day do you want to have?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit", marginBottom: 10 }} />
        <textarea value={tomorrowPlan.mealPlan || ""} onChange={e => updateTomorrow({ mealPlan: e.target.value })}
          placeholder="Meal plan for tomorrow — what will you cook? Did you prep? What do the kids eat?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit", marginBottom: 10 }} />
        <textarea value={tomorrowPlan.kidsTime || ""} onChange={e => updateTomorrow({ kidsTime: e.target.value })}
          placeholder="Time with kids tomorrow — what's planned? What do you want to teach them?"
          rows={2} style={{ ...inp, resize: "none", fontFamily: "inherit", marginBottom: 16 }} />

        {/* Tomorrow's Schedule */}
        <div style={{ borderTop: "1px solid #1e293b", paddingTop: 14 }}>
          <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>⏰ TOMORROW'S SCHEDULE</div>

          {/* AI Plan My Tomorrow */}
          <button
            onClick={planWithAI}
            disabled={aiPlanLoading}
            style={{
              ...btn("#8b5cf6", "#fff"),
              width: "100%", padding: "11px 16px", fontSize: 13, fontWeight: 700,
              marginBottom: 14, opacity: aiPlanLoading ? 0.7 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
            {aiPlanLoading ? "Planning your tomorrow…" : "✦ AI Plan My Tomorrow"}
          </button>
          {aiPlanMsg === "success" && (
            <div style={{ fontSize: 12, color: "#10b981", marginBottom: 12, textAlign: "center" }}>
              ✓ Tomorrow planned — review and adjust below
            </div>
          )}
          {aiPlanMsg === "error" && (
            <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, textAlign: "center" }}>
              Could not generate plan. Try again or plan manually below.
            </div>
          )}

          {/* Add block form */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>FROM</div>
                <select value={blockStart} onChange={e => {
                  const s = e.target.value;
                  setBlockStart(s);
                  if (timeToMins(blockEnd) <= timeToMins(s)) {
                    const next = TIME_SLOTS.find(t => timeToMins(t) > timeToMins(s));
                    if (next) setBlockEnd(next);
                  }
                }} style={{ ...inp, fontSize: 12, padding: "7px 8px" }}>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>TO</div>
                <select value={blockEnd} onChange={e => setBlockEnd(e.target.value)} style={{ ...inp, fontSize: 12, padding: "7px 8px" }}>
                  {TIME_SLOTS.filter(t => timeToMins(t) > timeToMins(blockStart)).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}>TASK / ACTIVITY</div>
              <input
                value={blockTask}
                onChange={e => setBlockTask(e.target.value)}
                placeholder="What will you do?"
                style={{ ...inp, fontSize: 13, marginBottom: 8 }}
              />
              {/* Today-tasks chips */}
              {tasks.filter(t => !t.done && t.when === "today").length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {tasks.filter(t => !t.done && t.when === "today").map(t => {
                    const area = AREA_MAP[TAG_TO_AREA[t.tag] || "business"] || AREAS[0];
                    return (
                      <button key={t.id} onClick={() => { setBlockTask(t.text); setBlockArea(TAG_TO_AREA[t.tag] || "business"); }}
                        style={{
                          fontSize: 11, padding: "3px 9px", borderRadius: 20, border: `1px solid ${area.color}44`,
                          background: area.color + "15", color: area.color, cursor: "pointer",
                          fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = area.color + "30"}
                        onMouseLeave={e => e.currentTarget.style.background = area.color + "15"}>
                        {area.icon} {t.text.slice(0, 28)}{t.text.length > 28 ? "…" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <select value={blockArea} onChange={e => setBlockArea(e.target.value)} style={{ ...inp, flex: 1, fontSize: 12, padding: "7px 8px" }}>
                {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
              </select>
              <button onClick={() => {
                if (!blockTask.trim()) return;
                const startMins = timeToMins(blockStart);
                const endMins   = timeToMins(blockEnd);
                if (endMins <= startMins) return;
                const block = { id: uid(), startTime: blockStart, endTime: blockEnd, taskName: blockTask.trim(), area: blockArea };
                const existing = tomorrowPlan.timeBlocks || [];
                updateTomorrow({ timeBlocks: [...existing, block].sort((a, b) => a.startTime.localeCompare(b.startTime)) });
                setBlockTask("");
                setBlockStart("08:00");
                setBlockEnd("09:00");
              }} style={{ ...btn("#f59e0b", "#000"), padding: "8px 18px", fontSize: 13, flexShrink: 0 }}>
                + Add
              </button>
            </div>
          </div>

          {/* Visual schedule */}
          {(tomorrowPlan.timeBlocks || []).length > 0 ? (
            <TimeBlockSchedule
              blocks={tomorrowPlan.timeBlocks}
              areaMap={AREA_MAP}
              editingId={editingBlockId}
              onEdit={id => setEditingBlockId(id)}
              onCancelEdit={() => setEditingBlockId(null)}
              onSaveEdit={(id, patch) => {
                const updated = (tomorrowPlan.timeBlocks || []).map(b => b.id === id ? { ...b, ...patch } : b)
                  .sort((a, b) => a.startTime.localeCompare(b.startTime));
                updateTomorrow({ timeBlocks: updated });
                setEditingBlockId(null);
              }}
              onDelete={id => {
                const updated = (tomorrowPlan.timeBlocks || []).filter(b => b.id !== id);
                updateTomorrow({ timeBlocks: updated });
                setEditingBlockId(null);
              }}
            />
          ) : (
            <div style={{ fontSize: 12, color: "#334155", textAlign: "center", padding: "16px 0" }}>
              No blocks yet. Add your first time block above.
            </div>
          )}
        </div>
      </div>}

      {/* Sleep Ritual Checklist — only on today */}
      {isToday && <div style={{ ...card, borderLeft: "3px solid #60a5fa" }}>
        <div style={{ fontSize: 11, color: "#60a5fa", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 12 }}>🛏 SLEEP RITUAL</div>
        {[
          { key: "s1", label: "Evening reflection written", check: !!(plan.reflection && plan.reflection.length > 10) },
          { key: "s2", label: "Tomorrow's intention set", check: !!(tomorrowPlan.intention && tomorrowPlan.intention.length > 5) },
          { key: "s3", label: "Meal plan for tomorrow ready", check: !!(tomorrowPlan.mealPlan && tomorrowPlan.mealPlan.length > 5) },
          { key: "s4", label: "Kids time planned for tomorrow", check: !!(tomorrowPlan.kidsTime && tomorrowPlan.kidsTime.length > 5) },
        ].map(item => (
          <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div style={{
              width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
              border: `2px solid ${item.check ? "#10b981" : "#334155"}`,
              background: item.check ? "#10b981" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {item.check && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" /></svg>}
            </div>
            <span style={{ fontSize: 13, color: item.check ? "#10b981" : "#475569" }}>{item.label}</span>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: "10px 12px", background: "#0a1628", borderRadius: 8, textAlign: "center" }}>
          {[plan.reflection, tomorrowPlan.intention, tomorrowPlan.mealPlan, tomorrowPlan.kidsTime].filter(v => v && v.length > 5).length === 4
            ? <span style={{ fontSize: 13, color: "#10b981", fontWeight: 600 }}>✓ Sleep ritual complete. Rest well. 🌙</span>
            : <span style={{ fontSize: 12, color: "#475569" }}>Complete all steps above before sleep.</span>
          }
        </div>
      </div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TIME LOG
// ═══════════════════════════════════════════════════════════════════════════════
function TimeLogView({ timeLogs, setTimeLogs, customActivities, setCustomActivities, userId }) {
  const key = todayKey();
  const logs = timeLogs[key] || [];

  // Default activity library
  const DEFAULT_ACTIVITIES = [
    // Morning
    { label: "Woke up", area: "health", type: "moment" },
    { label: "Morning routine (shower/dress)", area: "health", type: "block" },
    { label: "Exercise / workout", area: "health", type: "block" },
    { label: "Breakfast", area: "home", type: "block" },
    { label: "Scrolled phone", area: "habits", type: "block" },
    { label: "Meditated", area: "health", type: "block" },
    // Travel
    { label: "Arrived at work", area: "business", type: "moment" },
    { label: "Left office", area: "business", type: "moment" },
    { label: "Driving / commute", area: "habits", type: "block" },
    { label: "Delivery run", area: "business", type: "block" },
    // Work
    { label: "Deep work / focused task", area: "business", type: "block" },
    { label: "Meeting", area: "business", type: "block" },
    { label: "Calls / follow-ups", area: "business", type: "block" },
    { label: "Admin / emails", area: "business", type: "block" },
    { label: "SaaS work", area: "business", type: "block" },
    { label: "Supplier communication", area: "business", type: "block" },
    // Personal / home
    { label: "Lunch", area: "home", type: "block" },
    { label: "Cooking dinner", area: "home", type: "block" },
    { label: "Batch cooking / meal prep", area: "home", type: "block" },
    { label: "Grocery shopping", area: "home", type: "block" },
    { label: "Time with kids", area: "family", type: "block" },
    { label: "Teaching kids something", area: "family", type: "block" },
    { label: "Veg garden", area: "personal", type: "block" },
    { label: "Reading / learning", area: "potential", type: "block" },
    // Evening
    { label: "Dinner", area: "home", type: "block" },
    { label: "Unwinding / TV", area: "habits", type: "block" },
    { label: "In bed", area: "health", type: "moment" },
    { label: "Asleep", area: "health", type: "moment" },
  ];

  const allActivities = [...DEFAULT_ACTIVITIES, ...(customActivities || [])];

  // Time-aware activity ordering
  function getTimeOrderedActivities() {
    const hour = new Date().getHours();
    const priorityLabels =
      hour >= 5 && hour < 9   ? ["Woke up", "Morning routine (shower/dress)", "Exercise / workout", "Breakfast"] :
      hour >= 9 && hour < 12  ? ["Deep work / focused task", "Meeting", "Calls / follow-ups", "Admin / emails"] :
      hour >= 12 && hour < 14 ? ["Lunch", "Calls / follow-ups"] :
      hour >= 14 && hour < 17 ? ["Deep work / focused task", "Meeting", "Supplier communication"] :
      hour >= 17 && hour < 20 ? ["Left office", "Cooking dinner", "Time with kids"] :
                                 ["Dinner", "Unwinding / TV", "In bed", "Asleep"];
    const priority = allActivities.filter(a => priorityLabels.includes(a.label));
    const rest = allActivities.filter(a => !priorityLabels.includes(a.label));
    return { priority, rest };
  }

  const [entryType, setEntryType] = useState("block"); // block | moment
  const [selected, setSelected] = useState("");
  const [activitySearch, setActivitySearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [customText, setCustomText] = useState("");
  const [area, setArea] = useState("business");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [momentTime, setMomentTime] = useState("");
  const [note, setNote] = useState("");
  const [energy, setEnergy] = useState(7);
  const [mood, setMood] = useState(7);
  const [moodNote, setMoodNote] = useState("");
  const [saveNewActivity, setSaveNewActivity] = useState(false);
  const [running, setRunning] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const iRef = useRef(null);

  // Auto-set area when activity selected
  function handleSelect(val) {
    setSelected(val);
    setCustomText("");
    setActivitySearch("");
    setSearchFocused(false);
    const found = allActivities.find(a => a.label === val);
    if (found) {
      setArea(found.area);
      setEntryType(found.type);
    }
  }

  // Get display name
  const activityName = selected === "__custom__" ? customText : selected;

  // Timer
  useEffect(() => {
    if (running) {
      iRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - new Date(running.start)) / 1000)), 1000);
    } else { clearInterval(iRef.current); setElapsed(0); }
    return () => clearInterval(iRef.current);
  }, [running]);

  function fmtE(s) {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sc = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,"0")}:${String(sc).padStart(2,"0")}` : `${m}:${String(sc).padStart(2,"0")}`;
  }

  async function saveLog(entry) {
    const sorted = [entry, ...logs].sort((a, b) => new Date(b.start) - new Date(a.start));
    const u = { ...timeLogs, [key]: sorted };
    setTimeLogs(u);
    await save("timelogs", u, userId);
  }

  function energyColor(level) {
    if (level >= 8) return "#10b981"; // green
    if (level >= 6) return "#84cc16"; // lime
    if (level >= 4) return "#f59e0b"; // amber
    if (level >= 2) return "#f97316"; // orange
    return "#ef4444"; // red
  }

  function energyLabel(level) {
    if (level >= 9) return "Peak";
    if (level >= 7) return "High";
    if (level >= 5) return "Good";
    if (level >= 3) return "Low";
    return "Drained";
  }

  function moodColor(level) {
    if (level >= 8) return "#a78bfa";
    if (level >= 6) return "#818cf8";
    if (level >= 4) return "#60a5fa";
    if (level >= 2) return "#64748b";
    return "#475569";
  }

  function moodLabel(level) {
    if (level >= 9) return "Amazing";
    if (level >= 7) return "Good";
    if (level >= 5) return "Okay";
    if (level >= 3) return "Low";
    return "Struggling";
  }

  async function startTimer() {
    if (!activityName.trim()) return;
    if (saveNewActivity && selected === "__custom__" && customText.trim()) {
      const newAct = { label: customText.trim(), area, type: "block" };
      const updated = [...(customActivities || []), newAct];
      setCustomActivities(updated);
      await save("custom-activities", updated, userId);
    }
    setRunning({ id: uid(), text: activityName, area, note, energy, mood, moodNote, start: new Date().toISOString() });
    setNote("");
  }

  async function stopTimer() {
    if (!running) return;
    const end = new Date().toISOString();
    const entry = { ...running, end, mins: minsBetween(running.start, end), logType: "block", energy };
    await saveLog(entry);
    setRunning(null);
  }

  async function addManual() {
    if (!activityName.trim()) return;
    if (saveNewActivity && selected === "__custom__" && customText.trim()) {
      const newAct = { label: customText.trim(), area, type: entryType };
      const updated = [...(customActivities || []), newAct];
      setCustomActivities(updated);
      await save("custom-activities", updated, userId);
    }
    let entry;
    if (entryType === "moment") {
      if (!momentTime) return;
      const start = key + "T" + momentTime + ":00";
      entry = { id: uid(), text: activityName, area, note, energy, mood, moodNote, start, end: start, mins: 0, logType: "moment" };
    } else {
      if (!timeFrom || !timeTo) return;
      const start = key + "T" + timeFrom + ":00";
      const end = key + "T" + timeTo + ":00";
      entry = { id: uid(), text: activityName, area, note, energy, mood, moodNote, start, end, mins: minsBetween(start, end), logType: "block" };
    }
    await saveLog(entry);
    setSelected(""); setCustomText(""); setActivitySearch(""); setNote(""); setTimeFrom(""); setTimeTo(""); setMomentTime(""); setSaveNewActivity(false); setEnergy(7); setMood(7); setMoodNote("");
  }

  async function delEntry(id) {
    const u = { ...timeLogs, [key]: logs.filter(l => l.id !== id) };
    setTimeLogs(u); await save("timelogs", u, userId);
  }

  async function delCustomActivity(label) {
    const updated = (customActivities || []).filter(a => a.label !== label);
    setCustomActivities(updated);
    await save("custom-activities", updated, userId);
  }

  const breakdown = AREAS.map(a => ({ ...a, mins: logs.filter(l => l.area === a.id && l.logType !== "moment").reduce((s, l) => s + (l.mins || 0), 0) })).filter(a => a.mins > 0);
  const total = breakdown.reduce((s, a) => s + a.mins, 0);
  const isReady = activityName.trim() && (entryType === "moment" ? momentTime : (timeFrom && timeTo));

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Time Log</h2>
        <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>Track every block. Unplanned time is the enemy of goals.</p>
      </div>

      {/* Entry card */}
      <div style={{ ...card, marginBottom: 16 }}>

        {/* Activity selector */}
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>WHAT DID YOU DO?</div>
        {/* Search input */}
        <div style={{ position: "relative", marginBottom: 8 }}>
          <input
            value={selected && !activitySearch ? selected : activitySearch}
            onChange={e => { setActivitySearch(e.target.value); if (selected) setSelected(""); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            placeholder="Search or type activity..."
            style={{ ...inp }}
          />
          {selected && !activitySearch && (
            <button onClick={() => { setSelected(""); setActivitySearch(""); }}
              style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>
          )}
          {/* Dropdown results */}
          {(searchFocused || activitySearch) && (() => {
            const { priority, rest } = getTimeOrderedActivities();
            const q = activitySearch.toLowerCase();
            const filterList = list => list.filter(a => !q || a.label.toLowerCase().includes(q));
            const customs = filterList(customActivities || []);
            const prio = filterList(priority);
            const other = filterList(rest.filter(a => !(customActivities || []).some(c => c.label === a.label)));
            const allFiltered = [...customs, ...prio, ...other, ...(q ? [{ label: `+ "${activitySearch}" (custom)`, area: area, type: "block", _custom: true }] : [])];
            if (!allFiltered.length) return null;
            return (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "#1e293b", border: "1px solid #334155", borderRadius: 8, maxHeight: 220, overflowY: "auto", boxShadow: "0 8px 24px #00000066", marginTop: 2 }}>
                {!q && prio.length > 0 && <div style={{ fontSize: 10, color: "#475569", padding: "6px 12px 2px", fontWeight: 700, letterSpacing: "0.08em" }}>NOW — MOST RELEVANT</div>}
                {customs.length > 0 && !q && <div style={{ fontSize: 10, color: "#475569", padding: "6px 12px 2px", fontWeight: 700, letterSpacing: "0.08em" }}>YOUR ACTIVITIES</div>}
                {allFiltered.map((a, i) => {
                  return (
                    <div key={i}>
                      {!q && i === customs.length && prio.length > 0 && customs.length > 0 && (
                        <div style={{ fontSize: 10, color: "#475569", padding: "6px 12px 2px", fontWeight: 700, letterSpacing: "0.08em" }}>NOW — MOST RELEVANT</div>
                      )}
                      {!q && i === customs.length + prio.length && other.length > 0 && (
                        <div style={{ fontSize: 10, color: "#475569", padding: "6px 12px 2px", fontWeight: 700, letterSpacing: "0.08em" }}>ALL ACTIVITIES</div>
                      )}
                      <div
                        onMouseDown={() => {
                          if (a._custom) {
                            setSelected("__custom__");
                            setCustomText(activitySearch);
                            setActivitySearch("");
                            setSearchFocused(false);
                          } else {
                            handleSelect(a.label);
                          }
                        }}
                        style={{ padding: "9px 12px", cursor: "pointer", fontSize: 13, color: "#cbd5e1", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#334155"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <span>{a._custom ? a.label : a.label}</span>
                        {!a._custom && <span style={{ fontSize: 11, color: "#475569" }}>{AREA_MAP[a.area]?.icon}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {selected === "__custom__" && (
          <div style={{ marginBottom: 8 }}>
            <input value={customText} onChange={e => setCustomText(e.target.value)}
              placeholder="Describe what you did..." style={{ ...inp, marginBottom: 6 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#64748b", cursor: "pointer" }}>
              <input type="checkbox" checked={saveNewActivity} onChange={e => setSaveNewActivity(e.target.checked)} />
              Save to my activity list for next time
            </label>
          </div>
        )}

        {/* Area */}
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <select value={area} onChange={e => setArea(e.target.value)} style={{ ...inp, flex: 1 }}>
            {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
          </select>

          {/* Moment vs Block toggle */}
          <div style={{ display: "flex", background: "#1e293b", borderRadius: 8, overflow: "hidden", flexShrink: 0 }}>
            <button onClick={() => setEntryType("moment")} style={{
              padding: "8px 14px", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit",
              background: entryType === "moment" ? "#3b82f6" : "transparent",
              color: entryType === "moment" ? "#fff" : "#475569", fontWeight: 600,
            }}>Moment</button>
            <button onClick={() => setEntryType("block")} style={{
              padding: "8px 14px", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit",
              background: entryType === "block" ? "#10b981" : "transparent",
              color: entryType === "block" ? "#fff" : "#475569", fontWeight: 600,
            }}>Block</button>
          </div>
        </div>

        {/* Time inputs */}
        {entryType === "moment" ? (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>AT WHAT TIME?</div>
            <input type="time" value={momentTime} onChange={e => setMomentTime(e.target.value)} style={{ ...inp, width: "auto" }} />
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>FROM</div>
              <input type="time" value={timeFrom} onChange={e => setTimeFrom(e.target.value)} style={inp} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>TO</div>
              <input type="time" value={timeTo} onChange={e => setTimeTo(e.target.value)} style={inp} />
            </div>
            {timeFrom && timeTo && timeTo > timeFrom && (
              <div style={{ flex: 1, display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
                <div style={{ fontSize: 13, color: "#10b981", fontWeight: 700 }}>
                  {minsBetween(key+"T"+timeFrom+":00", key+"T"+timeTo+":00")}m
                </div>
              </div>
            )}
          </div>
        )}

        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder="Note (optional)" style={{ ...inp, marginBottom: 10 }} />

        {/* Energy level */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em" }}>ENERGY LEVEL</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: energyColor(energy), fontWeight: 700 }}>{energyLabel(energy)}</span>
              <span style={{ 
                fontSize: 13, fontWeight: 800, color: "#fff",
                background: energyColor(energy), 
                borderRadius: "50%", width: 26, height: 26,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>{energy}</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, color: "#ef4444" }}>😴</span>
            <input type="range" min={1} max={10} value={energy} onChange={e => setEnergy(+e.target.value)}
              style={{ flex: 1, accentColor: energyColor(energy) }} />
            <span style={{ fontSize: 10, color: "#10b981" }}>⚡</span>
          </div>
          {/* Visual energy bar */}
          <div style={{ display: "flex", gap: 3, marginTop: 6 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <div key={n} onClick={() => setEnergy(n)} style={{
                flex: 1, height: 8, borderRadius: 4, cursor: "pointer",
                background: n <= energy ? energyColor(energy) : "#1e293b",
                transition: "all 0.15s",
                opacity: n <= energy ? (0.5 + n/energy * 0.5) : 1,
              }} />
            ))}
          </div>
        </div>

        {/* Mood level */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em" }}>MOOD</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: moodColor(mood), fontWeight: 700 }}>{moodLabel(mood)}</span>
              <span style={{
                fontSize: 13, fontWeight: 800, color: "#fff",
                background: moodColor(mood),
                borderRadius: "50%", width: 26, height: 26,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>{mood}</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10 }}>😔</span>
            <input type="range" min={1} max={10} value={mood} onChange={e => setMood(+e.target.value)}
              style={{ flex: 1, accentColor: moodColor(mood) }} />
            <span style={{ fontSize: 10 }}>😄</span>
          </div>
          <div style={{ display: "flex", gap: 3, marginTop: 6, marginBottom: 8 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <div key={n} onClick={() => setMood(n)} style={{
                flex: 1, height: 8, borderRadius: 4, cursor: "pointer",
                background: n <= mood ? moodColor(mood) : "#1e293b",
                transition: "all 0.15s",
              }} />
            ))}
          </div>
          <input value={moodNote} onChange={e => setMoodNote(e.target.value)}
            placeholder="Why this mood? (optional — stress, energy, situation...)" style={{ ...inp, fontSize: 12 }} />
        </div>

        {/* Mood remark */}
        <input value={moodNote} onChange={e => setMoodNote(e.target.value)}
          placeholder="Mood note (optional) — what's affecting your mood right now?"
          style={{ ...inp, marginBottom: 10 }} />

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={addManual} disabled={!isReady}
            style={{ ...btn("#3b82f6"), flex: 1, opacity: isReady ? 1 : 0.4 }}>
            + Log Entry
          </button>
          {entryType === "block" && !running && (
            <button onClick={startTimer} disabled={!activityName.trim()}
              style={{ ...btn("#10b981"), opacity: activityName.trim() ? 1 : 0.4 }}>
              ▶ Timer
            </button>
          )}
        </div>

        {/* Running timer */}
        {running && (
          <div style={{ marginTop: 14, background: "#0a1628", borderRadius: 10, padding: "14px", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>NOW TRACKING — {running.text}</div>
            <div style={{ fontSize: 40, fontFamily: "monospace", color: AREA_MAP[running.area]?.color || "#10b981", fontWeight: 800, letterSpacing: 2 }}>
              {fmtE(elapsed)}
            </div>
            <button onClick={stopTimer} style={{ ...btn("#ef4444"), marginTop: 10 }}>■ Stop & Save</button>
          </div>
        )}
      </div>

      {/* Today breakdown */}
      {breakdown.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 12 }}>
            TODAY — {Math.floor(total / 60)}h {total % 60}m tracked
          </div>
          {breakdown.map(a => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: a.color, minWidth: 140 }}>{a.icon} {a.label}</span>
              <div style={{ flex: 1, height: 5, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: (a.mins / total * 100) + "%", height: "100%", background: a.color, borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 12, color: "#475569", minWidth: 52, textAlign: "right" }}>
                {a.mins >= 60 ? `${Math.floor(a.mins / 60)}h ${a.mins % 60}m` : `${a.mins}m`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Log entries */}
      {logs.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 12 }}>LOG ENTRIES</div>
          <div style={{ display: "flex", gap: 10 }}>
            {/* Colorful energy sidebar */}
            <div style={{ width: 10, flexShrink: 0, display: "flex", flexDirection: "column", gap: 2, borderRadius: 6, overflow: "hidden" }}>
              {[...logs].reverse().map((l) => (
                <div key={l.id} title={`Energy: ${l.energy || 5}/10`} style={{
                  flex: 1, minHeight: 32,
                  background: energyColor(l.energy || 5),
                  opacity: 0.5 + (l.energy || 5) * 0.05,
                }} />
              ))}
            </div>
            {/* Entry list */}
            <div style={{ flex: 1 }}>
              {logs.map(l => {
                const a = AREA_MAP[l.area] || AREAS[0];
                const lvl = l.energy || 5;
                return (
                  <div key={l.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #1e293b", alignItems: "flex-start" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, color: "#cbd5e1", fontWeight: 500 }}>{l.text}</span>
                        {l.logType === "moment" && (
                          <span style={{ fontSize: 10, background: "#3b82f622", color: "#3b82f6", padding: "1px 6px", borderRadius: 10, fontWeight: 700 }}>MOMENT</span>
                        )}
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10, background: energyColor(lvl) + "22", color: energyColor(lvl) }}>
                          ⚡{lvl} {energyLabel(lvl)}
                        </span>
                        {l.mood && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10, background: moodColor(l.mood) + "22", color: moodColor(l.mood) }}>
                            😊{l.mood} {moodLabel(l.mood)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#334155", marginTop: 2 }}>
                        {l.logType === "moment" ? fmt(l.start) : `${fmt(l.start)} – ${fmt(l.end)} · ${l.mins}m`}
                        {" · "}<span style={{ color: a.color }}>{a.icon} {a.label}</span>
                      </div>
                      {l.note && <div style={{ fontSize: 11, color: "#475569", fontStyle: "italic", marginTop: 2 }}>{l.note}</div>}
                      {l.moodNote && <div style={{ fontSize: 11, color: "#a78bfa", fontStyle: "italic", marginTop: 2 }}>😊 {l.moodNote}</div>}
                    </div>
                    <button onClick={() => delEntry(l.id)} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 16, flexShrink: 0 }}
                      onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Custom activity manager */}
      {(customActivities || []).length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>YOUR SAVED ACTIVITIES</div>
          {(customActivities || []).map((a, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #1e293b" }}>
              <span style={{ fontSize: 13, color: "#64748b" }}>{a.label}</span>
              <button onClick={() => delCustomActivity(a.label)} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 14 }}
                onMouseEnter={e => e.target.style.color = "#ef4444"} onMouseLeave={e => e.target.style.color = "#334155"}>×</button>
            </div>
          ))}
        </div>
      )}

      {logs.length === 0 && (
        <div style={{ textAlign: "center", color: "#1e293b", padding: "30px 0", fontSize: 13 }}>
          Pick an activity above and log it. Every hour tracked is a data point.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HABITS VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const HABIT_FREQUENCIES = [
  { id: "daily", label: "Every day" },
  { id: "3x", label: "3× a week" },
  { id: "2x", label: "2× a week" },
  { id: "weekly", label: "Once a week" },
];

const HABIT_PRESETS = [
  { name: "Meditate", icon: "🧘", area: "health", freq: "daily", target: 60, unit: "min", trackingTime: "morning" },
  { name: "Walk", icon: "🚶", area: "health", freq: "3x", target: 20, unit: "min", trackingTime: "morning" },
  { name: "Pilates / Exercise", icon: "🤸", area: "health", freq: "daily", target: 20, unit: "min", trackingTime: "morning" },
  { name: "No phone scrolling", icon: "📵", area: "habits", freq: "daily", target: 0, unit: "", trackingTime: "evening" },
  { name: "Read / Study", icon: "📚", area: "potential", freq: "daily", target: 60, unit: "min", trackingTime: "anytime" },
  { name: "Cold shower", icon: "🚿", area: "health", freq: "daily", target: 0, unit: "", trackingTime: "morning" },
  { name: "Journaling", icon: "✍️", area: "habits", freq: "daily", target: 10, unit: "min", trackingTime: "evening" },
  { name: "Veg garden", icon: "🌱", area: "personal", freq: "daily", target: 20, unit: "min", trackingTime: "anytime" },
  { name: "Family dinner", icon: "👨‍👧", area: "family", freq: "daily", target: 0, unit: "", trackingTime: "evening" },
  { name: "Batch cook", icon: "🍳", area: "home", freq: "weekly", target: 90, unit: "min", trackingTime: "anytime" },
];

function getStreak(habitId, habitLogs) {
  const logs = habitLogs[habitId] || {};
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    if (logs[key]) streak++;
    else if (i > 0) break;
  }
  return streak;
}

function getLast7(habitId, habitLogs) {
  const logs = habitLogs[habitId] || {};
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ key, done: !!logs[key], actual: logs[key]?.actual || null });
  }
  return days;
}

function HabitsView({ habits, setHabits, habitLogs, setHabitLogs, goals, userId }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", icon: "⭐", freq: "daily", target: 0, unit: "min", linkedGoal: "", area: "health", trackingTime: "anytime" });
  const [showPresets, setShowPresets] = useState(false);
  const [logActual, setLogActual] = useState({});
  const [viewOffset, setViewOffset] = useState(0); // 0 = today, -1 = yesterday, etc.
  const today = todayKey();

  // Compute the viewed date
  const viewedDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + viewOffset);
    return d.toISOString().slice(0, 10);
  })();

  function viewedDateLabel() {
    if (viewOffset === 0) return "Today";
    if (viewOffset === -1) return "Yesterday";
    const d = new Date();
    d.setDate(d.getDate() + viewOffset);
    return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  }

  const isToday = viewOffset === 0;

  async function addHabit(h) {
    const newH = { id: uid(), ...h, createdAt: new Date().toISOString() };
    const updated = [...habits, newH];
    setHabits(updated);
    await save("habits", updated, userId);
    setAdding(false);
    setShowPresets(false);
    setForm({ name: "", icon: "⭐", freq: "daily", target: 0, unit: "min", linkedGoal: "", area: "health", trackingTime: "anytime" });
  }

  async function deleteHabit(id) {
    const updated = habits.filter(h => h.id !== id);
    setHabits(updated);
    await save("habits", updated, userId);
  }

  async function toggleHabit(habitId, actual) {
    const logs = habitLogs[habitId] || {};
    const alreadyDone = !!logs[viewedDate];
    const updated = {
      ...habitLogs,
      [habitId]: {
        ...logs,
        [viewedDate]: alreadyDone ? null : { done: true, actual: actual || null, time: new Date().toISOString() }
      }
    };
    if (!updated[habitId][viewedDate]) delete updated[habitId][viewedDate];
    setHabitLogs(updated);
    await save("habitlogs", updated, userId);
    setLogActual(prev => ({ ...prev, [habitId]: "" }));
  }

  // Summary stats
  const totalHabits = habits.length;
  const doneToday = habits.filter(h => !!(habitLogs[h.id] || {})[viewedDate]).length;
  const topStreak = habits.reduce((max, h) => Math.max(max, getStreak(h.id, habitLogs)), 0);

  // Streak milestone detection
  const MILESTONES = [3, 7, 14, 21, 30, 60, 100];
  const MILESTONE_MESSAGES = {
    3:   (name) => `🔥 3 Day Streak — ${name}! The habit is forming.`,
    7:   (name) => `🔥 7 Days — ${name}! One full week. This is real.`,
    14:  (name) => `🔥 14 Days — ${name}! Two weeks of discipline.`,
    21:  (name) => `🌟 21 Days — ${name}! Science says this is now a habit.`,
    30:  (name) => `🏆 30 Days — ${name}! One month. You are different now.`,
    60:  (name) => `🏆 60 Days — ${name}! Two months of consistent discipline.`,
    100: (name) => `👑 100 Days — ${name}! This is who you are now.`,
  };
  const milestoneBanner = (() => {
    let best = null;
    for (const h of habits) {
      const streak = getStreak(h.id, habitLogs);
      const logs = habitLogs[h.id] || {};
      if (!logs[today]) continue;
      for (const m of MILESTONES) {
        if (streak !== m) continue;
        const lsKey = `streak-celebrated-${h.id}-${m}-${today}`;
        if (localStorage.getItem(lsKey)) continue;
        if (!best || m > best.milestone) best = { habit: h, milestone: m, lsKey };
      }
    }
    return best;
  })();
  const [milestoneDismissed, setMilestoneDismissed] = useState(false);
  function dismissMilestone() {
    if (milestoneBanner) localStorage.setItem(milestoneBanner.lsKey, "true");
    setMilestoneDismissed(true);
  }

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      {/* Streak milestone celebration banner */}
      {milestoneBanner && !milestoneDismissed && (
        <div style={{
          width: "100%", boxSizing: "border-box",
          background: "#1a1200", border: "2px solid #f59e0b",
          borderRadius: 12, padding: "14px 20px", marginBottom: 20,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          animation: "fadeIn 0.4s ease",
        }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#fcd34d", textAlign: "center", flex: 1 }}>
            {MILESTONE_MESSAGES[milestoneBanner.milestone](milestoneBanner.habit.name)}
          </span>
          <button onClick={dismissMilestone} style={{
            background: "none", border: "none", color: "#92400e", cursor: "pointer",
            fontSize: 18, lineHeight: 1, padding: "0 4px", flexShrink: 0,
          }}>×</button>
        </div>
      )}
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Habits</h2>
          <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>Small daily actions. Compounded over time. Everything changes.</p>
        </div>
        <button onClick={() => setAdding(true)} style={btn("#10b981", "#fff")}>+ Add Habit</button>
      </div>

      {/* Date navigator */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginBottom: 16, background: "#0f172a", borderRadius: 12, padding: "10px 16px" }}>
        <button onClick={() => setViewOffset(v => v - 1)} style={{
          background: "none", border: "1px solid #1e293b", borderRadius: 8,
          color: "#64748b", cursor: "pointer", fontSize: 18, width: 36, height: 36,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>‹</button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{viewedDateLabel()}</div>
          <div style={{ fontSize: 11, color: "#475569" }}>{viewedDate}</div>
        </div>
        <button onClick={() => setViewOffset(v => Math.min(0, v + 1))}
          disabled={viewOffset === 0}
          style={{
            background: "none", border: "1px solid #1e293b", borderRadius: 8,
            color: viewOffset === 0 ? "#1e293b" : "#64748b",
            cursor: viewOffset === 0 ? "default" : "pointer",
            fontSize: 18, width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>›</button>
      </div>

      {/* Day summary */}
      {habits.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 20 }}>
          {[
            { label: "Done Today", value: `${doneToday}/${totalHabits}`, color: "#10b981" },
            { label: "Completion", value: totalHabits ? Math.round(doneToday/totalHabits*100) + "%" : "—", color: "#f59e0b" },
            { label: "Best Streak", value: `🔥 ${topStreak}d`, color: "#ef4444" },
          ].map(s => (
            <div key={s.label} style={{ ...card, textAlign: "center", padding: "12px 8px" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: "#334155", marginTop: 2, letterSpacing: "0.05em" }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add habit form */}
      {adding && (
        <div style={{ ...card, marginBottom: 16, borderColor: "#10b98144" }}>
          <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 12 }}>NEW HABIT</div>

          {/* Presets */}
          <button onClick={() => setShowPresets(s => !s)} style={{ ...btn("#1e293b", "#64748b"), marginBottom: 10, fontSize: 12 }}>
            {showPresets ? "▴ Hide presets" : "▾ Pick from common habits"}
          </button>

          {showPresets && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {HABIT_PRESETS.map((p, i) => (
                <button key={i} onClick={() => addHabit(p)} style={{
                  background: "#1e293b", border: "1px solid #334155", borderRadius: 20,
                  padding: "6px 12px", color: "#94a3b8", fontSize: 12, cursor: "pointer",
                  fontFamily: "inherit", transition: "all 0.15s",
                }} onMouseEnter={e => { e.target.style.background="#10b98122"; e.target.style.color="#10b981"; }}
                   onMouseLeave={e => { e.target.style.background="#1e293b"; e.target.style.color="#94a3b8"; }}>
                  {p.icon} {p.name}
                </button>
              ))}
            </div>
          )}

          <div style={{ display: "grid", gap: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}
                style={{ ...inp, width: 50, textAlign: "center", fontSize: 20, flexShrink: 0 }} />
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Habit name..." style={{ ...inp, flex: 1 }} autoFocus />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={form.freq} onChange={e => setForm(f => ({ ...f, freq: e.target.value }))} style={{ ...inp, flex: 1 }}>
                {HABIT_FREQUENCIES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <select value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} style={{ ...inp, flex: 1 }}>
                {AREAS.map(a => <option key={a.id} value={a.id}>{a.icon} {a.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>When do you track this habit?</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[{ id: "morning", label: "🌅 Morning" }, { id: "evening", label: "🌙 Evening" }, { id: "anytime", label: "📅 Anytime" }].map(opt => (
                  <button key={opt.id} onClick={() => setForm(f => ({ ...f, trackingTime: opt.id }))} style={{
                    flex: 1, padding: "8px 4px", borderRadius: 8, fontSize: 12, fontFamily: "inherit",
                    cursor: "pointer", border: "1px solid",
                    borderColor: form.trackingTime === opt.id ? "#10b981" : "#1e293b",
                    background: form.trackingTime === opt.id ? "#10b98122" : "#1e293b",
                    color: form.trackingTime === opt.id ? "#10b981" : "#64748b",
                    fontWeight: form.trackingTime === opt.id ? 700 : 400,
                    transition: "all 0.15s",
                  }}>{opt.label}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>Target duration:</span>
              <input type="number" value={form.target} onChange={e => setForm(f => ({ ...f, target: +e.target.value }))}
                min={0} style={{ ...inp, width: 70, flexShrink: 0 }} />
              <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} style={{ ...inp, flex: 1 }}>
                <option value="min">minutes</option>
                <option value="hr">hours</option>
                <option value="">no target</option>
              </select>
            </div>
            <select value={form.linkedGoal} onChange={e => setForm(f => ({ ...f, linkedGoal: e.target.value }))} style={inp}>
              <option value="">— Link to a goal (optional) —</option>
              {goals.map(g => <option key={g.id} value={g.id}>{g.text.slice(0, 50)}</option>)}
            </select>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => addHabit(form)} disabled={!form.name.trim()} style={{ ...btn("#10b981"), opacity: form.name.trim() ? 1 : 0.4 }}>Save Habit</button>
              <button onClick={() => setAdding(false)} style={btn("#1e293b", "#64748b")}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {habits.length === 0 && !adding && (
        <div style={{ ...card, textAlign: "center", padding: "50px 20px" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔄</div>
          <div style={{ fontSize: 14, color: "#475569" }}>No habits yet.</div>
          <div style={{ fontSize: 12, color: "#334155", marginTop: 6 }}>Pilates, meditation, walking — add the things you want to do every day.</div>
        </div>
      )}

      {/* Habit list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {habits.map(h => {
          const area = AREA_MAP[h.area] || AREAS[0];
          const streak = getStreak(h.id, habitLogs);
          const last7 = getLast7(h.id, habitLogs);
          const doneToday2 = !!(habitLogs[h.id] || {})[viewedDate];
          const linked = goals.find(g => g.id === h.linkedGoal);
          const actual = logActual[h.id] || "";

          return (
            <div key={h.id} style={{
              ...card,
              borderLeft: `3px solid ${doneToday2 ? "#10b981" : area.color}`,
              opacity: 1,
              transition: "all 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* ONE TAP button */}
                <button onClick={() => toggleHabit(h.id, h.target > 0 ? (actual || h.target) : null)}
                  title={isToday ? "Tap to mark done" : `Mark as done for ${viewedDateLabel()}`}
                  style={{
                    width: 48, height: 48, borderRadius: "50%", flexShrink: 0,
                    border: `3px solid ${doneToday2 ? "#10b981" : area.color}`,
                    background: doneToday2 ? "#10b981" : "transparent",
                    cursor: "pointer", fontSize: 20, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}>
                  {doneToday2 ? "✓" : h.icon}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: doneToday2 ? "#10b981" : "#f1f5f9" }}>{h.name}</span>
                    <span style={{ fontSize: 11, color: "#334155" }}>
                      {HABIT_FREQUENCIES.find(f => f.id === h.freq)?.label}
                    </span>
                    {h.target > 0 && (
                      <span style={{ fontSize: 11, color: "#475569" }}>{h.target}{h.unit}</span>
                    )}
                    {h.trackingTime && (
                      <span style={{ fontSize: 11, color: "#475569" }}>
                        {h.trackingTime === "morning" ? "🌅" : h.trackingTime === "evening" ? "🌙" : "📅"}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    {/* 7-day mini calendar */}
                    <div style={{ display: "flex", gap: 3 }}>
                      {last7.map((d, i) => (
                        <div key={i} title={d.key} style={{
                          width: 14, height: 14, borderRadius: 3,
                          background: d.done ? "#10b981" : "#1e293b",
                          border: d.key === today ? "1px solid #f59e0b" : "1px solid transparent",
                        }} />
                      ))}
                    </div>
                    {streak > 0 && (
                      <span style={{ fontSize: 11, color: streak >= 7 ? "#ef4444" : "#f59e0b", fontWeight: 700 }}>
                        🔥 {streak} day streak
                      </span>
                    )}
                    {linked && (
                      <span style={{ fontSize: 10, color: "#334155" }}>→ {linked.text.slice(0, 25)}</span>
                    )}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                  {/* Actual duration input for habits with targets */}
                  {h.target > 0 && !doneToday2 && (
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input type="number" value={actual}
                        onChange={e => setLogActual(prev => ({ ...prev, [h.id]: e.target.value }))}
                        placeholder={String(h.target)}
                        style={{ ...inp, width: 52, padding: "4px 6px", fontSize: 12, textAlign: "center" }} />
                      <span style={{ fontSize: 10, color: "#475569" }}>{h.unit}</span>
                    </div>
                  )}
                  <button onClick={() => deleteHabit(h.id)}
                    style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 14 }}
                    onMouseEnter={e => e.target.style.color="#ef4444"}
                    onMouseLeave={e => e.target.style.color="#334155"}>×</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Motivational message when all done */}
      {habits.length > 0 && doneToday === totalHabits && isToday && (
        <div style={{ ...card, marginTop: 16, textAlign: "center", borderColor: "#10b98144", background: "#10b98108" }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🌟</div>
          <div style={{ fontSize: 14, color: "#10b981", fontWeight: 700, marginBottom: 4 }}>All habits done today!</div>
          <div style={{ fontSize: 12, color: "#475569" }}>
            This is what discipline looks like. Every day you do this, the gap closes a little more. 🌱
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FUTURE ME — daily AI message from the user's future self
// ═══════════════════════════════════════════════════════════════════════════════
function FutureMe({ futureMePhoto, plan, update, userMode = "growth", goals = [], habits = [], habitLogs = {}, userId }) {
  const [loading, setLoading] = useState(false);
  const genRef = useRef(false);

  const todayStr = todayKey();
  const needsNewMessage = !plan.futureMeMessage || plan.futureMeMessageDate !== todayStr;
  const isFight = userMode === "fight";
  const accent = isFight ? "#ef4444" : "#f59e0b";

  async function generate() {
    setLoading(true);
    const doneHabitsCount = habits.filter(h => !!(habitLogs[h.id] || {})[todayKey()]).length;
    const totalHabits = habits.length;
    const avgProgress = Math.round(goals.reduce((s, g) => s + (g.progress || 0), 0) / Math.max(goals.length, 1));
    const maxProgress = goals.length ? Math.max(...goals.map(g => g.progress || 0)) : 0;
    let latest = null;
    goals.forEach(g => (g.progressLog || []).forEach(e => { if (e.date && (!latest || e.date > latest)) latest = e.date; }));
    const daysSinceProgress = latest ? Math.round((new Date(todayKey()) - new Date(latest)) / 86400000) : "unknown";

    let yesterdayResponse = "none";
    try {
      const responses = await load("analysis-response", {}, userId);
      const y = new Date(); y.setDate(y.getDate() - 1);
      const yKey = y.toISOString().slice(0, 10);
      if (responses && responses[yKey]?.text) yesterdayResponse = responses[yKey].text;
    } catch {}

    // Daily angle seeded by day-of-year so the message shifts register even when the data is static.
    const angles = ["gratitude", "urgency", "pride", "encouragement", "challenge", "love", "warning", "celebration"];
    const now = new Date();
    const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const todayAngle = angles[dayOfYear % angles.length];
    const dayName = now.toLocaleDateString([], { weekday: "long" });
    const dateLabel = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

    const system = "You are the future version of this person, writing to your past self. You are healthier, wealthier, more disciplined, and deeply grateful for the choices your past self made. Speak directly and personally. Keep it under 80 words. Alternate between gratitude, encouragement, and urgency depending on the data.";

    const prompt = `Today is ${dateLabel}. Generate a fresh message — do not repeat yesterday's.

Today's emotional angle: ${todayAngle}
- gratitude: deeply thankful for what past self is doing
- urgency: time is running out, act now
- pride: proud of who past self is becoming
- encouragement: you can do this, I believe in you
- challenge: I dare you to be better today
- love: speaking with warmth and deep care
- warning: gently warning about a pattern you see
- celebration: celebrating a win, big or small

Also include today's date in the message naturally so it feels fresh and current. Reference the day of the week: 'This ${dayName}...' or 'As you start this ${dayName}...'

IMPORTANT: This message must feel completely different from yesterday's. Use a different opening word, different emotional register, different length (vary between 2 sentences and 5 sentences). Never start with 'I know'.

Write a message from my future self to my present self based on this data:

Current mode: ${userMode}
Goals: ${goals.length} active goals, average progress ${avgProgress}%
Habits done today: ${doneHabitsCount}/${totalHabits}
Yesterday's analysis response: ${yesterdayResponse}
Days since any goal progress logged: ${daysSinceProgress}
Highest single goal progress: ${maxProgress}%

Tone rules:
- If mode is 'fight': urgent, intense, deeply personal — 'I know what you are going through right now. I lived it. Keep going.'
- If mode is 'support': warm, gentle, hopeful — 'I know today is hard. But you made it. Every small step counts.'
- If mode is 'growth': grateful, forward-looking — 'Thank you. The work you are doing today is exactly why I am who I am.'
- If habits done === total habits: deeply grateful — 'You did everything today. I feel it. Thank you.'
- If habits done === 0: urgent plea — 'I need you today. Please. Just one thing.'
- If goal progress > 70% on any goal: celebratory — 'You are so close. I can see the finish line from here.'

Sign the message: '— Future You'`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 300,
          system,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const d = await res.json();
      const message = d.content?.map(b => b.text || "").join("").trim() || "";
      if (message) await update({ futureMeMessage: message, futureMeMessageDate: todayStr });
    } catch {}
    setLoading(false);
  }

  useEffect(() => {
    if (!futureMePhoto || !needsNewMessage || genRef.current) return;
    genRef.current = true;
    generate().finally(() => { genRef.current = false; });
  }, [futureMePhoto, needsNewMessage, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!futureMePhoto) return null;

  function refresh() {
    if (loading) return;
    update({ futureMeMessage: "", futureMeMessageDate: "" });
  }

  // Split the signature off so it can be styled separately.
  const raw = plan.futureMeMessage || "";
  const sigMatch = raw.match(/—\s*Future You\.?\s*$/i);
  const body = sigMatch ? raw.slice(0, sigMatch.index).trim() : raw;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: accent, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>
        ✦ MESSAGE FROM YOUR FUTURE SELF
      </div>
      <div style={{
        background: "#0f172a",
        border: `1px solid ${accent}`,
        borderRadius: 14,
        padding: "16px 18px",
        boxShadow: isFight ? "0 0 20px #ef444422" : "0 0 20px #f59e0b22",
        animation: isFight ? "pulse-border 2s ease-in-out infinite" : "none",
        position: "relative",
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
      }}>
        <img src={futureMePhoto} alt="Future you" style={{
          width: 80, height: 80, borderRadius: "50%", objectFit: "cover",
          border: `2px solid ${accent}`, flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <button onClick={refresh} title="Regenerate message" style={{
            position: "absolute", top: 10, right: 12, background: "none", border: "none",
            color: accent, cursor: loading ? "default" : "pointer", fontSize: 16, lineHeight: 1,
            opacity: loading ? 0.4 : 1, fontFamily: "inherit",
          }}>↻</button>
          {loading ? (
            <div style={{ display: "flex", gap: 5, alignItems: "center", paddingTop: 8 }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: accent, animation: `pulse 1.2s ${i * 0.2}s ease-in-out infinite` }} />)}
              <span style={{ fontSize: 11, color: "#475569", marginLeft: 6 }}>Your future self is writing…</span>
            </div>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: 13, color: "#f1f5f9", fontStyle: "italic", lineHeight: 1.7, paddingRight: 18 }}>
                {body}
              </p>
              {raw && (
                <div style={{ marginTop: 8, fontSize: 12, color: accent, fontWeight: 700, textAlign: "right" }}>
                  — Future You
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <style>{`
  @keyframes pulse{0%,100%{opacity:.2;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
  @keyframes pulse-border {
    0%, 100% { box-shadow: 0 0 20px #ef444422; }
    50% { box-shadow: 0 0 40px #ef444466; }
  }
`}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYZE — goal-aware AI
// ═══════════════════════════════════════════════════════════════════════════════
function AnalyzeView({ goals, dayPlans, setDayPlans, timeLogs, habits, habitLogs, tasks, userId, analysisPrefs = {}, setTab, userQuestions = [], userMode = "growth", futureMePhoto = "" }) {
  const [period, setPeriod] = useState("today");
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState({});
  const [responseText, setResponseText] = useState("");
  const [responseEditing, setResponseEditing] = useState(false);
  const [responseSaving, setResponseSaving] = useState(false);
  const [analysisDialogue, setAnalysisDialogue] = useState({});
  const [dialogueLoading, setDialogueLoading] = useState(false);

  const tone = analysisPrefs.tone || "balanced";
  const emphasis = analysisPrefs.emphasis || "whole";
  const format = analysisPrefs.format || "full";

  const TONE_LABELS = { tough: "Tough Coach", balanced: "Balanced", encouraging: "Encouraging" };
  const EMPHASIS_LABELS = { whole: "Whole Life", business: "Business Focus", health: "Health Focus", family: "Family Focus" };
  const FORMAT_LABELS = { full: "Full Analysis", quick: "Quick Summary", morning: "Morning Catch-up" };

  useEffect(() => {
    load("analysis-response", {}, userId).then(r => {
      setResponses(r);
      const todayResponse = r[todayKey()];
      if (todayResponse) setResponseText(todayResponse.text);
    });
    load("analysis-dialogue", {}, userId).then(d => setAnalysisDialogue(d || {}));
  }, [userId]);

  async function saveResponse() {
    setResponseSaving(true);
    const updated = { ...responses, [todayKey()]: { text: responseText, savedAt: new Date().toISOString() } };
    setResponses(updated);
    await save("analysis-response", updated, userId);
    setResponseEditing(false);
    setResponseSaving(false);
  }

  async function getAIDialogue() {
    const k = todayKey();
    const userResponse = responses[k]?.text;
    if (!analysis || !userResponse) return;
    setDialogueLoading(true);
    const dialoguePrompt = `You previously gave this daily analysis:
${analysis}

The user responded with:
${userResponse}

Now respond directly to what they wrote.
- If they make a valid point you missed, acknowledge it
- If they are rationalizing or making excuses, call it out gently but honestly
- If they disagree with your assessment, explain your reasoning or update your view if they are right
- End with one specific actionable suggestion based on the dialogue

Keep it under 150 words. This is a conversation, not another full analysis.`;
    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 400,
          messages: [{ role: "user", content: dialoguePrompt }],
        }),
      });
      const d = await res.json();
      const aiReply = d.content?.map(b => b.text || "").join("") || "No response.";
      const updated = { ...analysisDialogue, [k]: { userResponse, aiReply, repliedAt: new Date().toISOString() } };
      setAnalysisDialogue(updated);
      await save("analysis-dialogue", updated, userId);
    } catch { /* silently fail */ }
    setDialogueLoading(false);
  }

  async function runAnalysis() {
    setLoading(true); setAnalysis("");
    const key = todayKey();

    const yesterdayDate = new Date(); yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterdayKey = yesterdayDate.toISOString().slice(0, 10);
    const allResponses = responses;
    const yesterdayResponse = allResponses[yesterdayKey];

    let data = {};
    if (period === "today") {
      data = { date: key, plan: dayPlans[key] || {}, timelog: timeLogs[key] || [], goals };
    } else {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = d.toISOString().slice(0, 10);
        days.push({ date: k, plan: dayPlans[k] || {}, timelog: timeLogs[k] || [] });
      }
      data = { period: "week", days, goals };
    }

    const goalsText = goals.length
      ? goals.map(g => `- [${AREA_MAP[g.area]?.label}] ${g.text} (${g.progress}% done, horizon: ${HORIZONS[g.horizon]})${g.why ? ` | Why: ${g.why}` : ""}`).join("\n")
      : "No goals set yet.";

    const estimationTasks = tasks.filter(t => t.done && t.estimatedMins && t.actualMins);
    const estimationText = estimationTasks.length
      ? estimationTasks.map(t => `- "${t.text}": est ${t.estimatedMins}min → actual ${t.actualMins}min (${t.actualMins <= t.estimatedMins ? "on time" : `over by ${t.actualMins - t.estimatedMins}min`})`).join("\n")
      : null;

    const dailyScores = (dayPlans[key] || {}).dailyScores || [];
    const dailyScoresText = dailyScores.length && userQuestions.length
      ? dailyScores.map(s => `- "${s.question}": ${s.score}/10`).join("\n")
      : null;

    const morningFeedbackText = (() => {
      const lines = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = d.toISOString().slice(0, 10);
        const fb = (dayPlans[k] || {}).morningFeedback;
        if (fb?.rating) lines.push(`- ${k}: ${fb.rating === 'nailed' ? '✓ Nailed it' : fb.rating === 'partial' ? '~ Partial' : '✗ Missed it'}${fb.note ? ` — "${fb.note}"` : ''}`);
      }
      return lines.length ? lines.join('\n') : null;
    })();

    const workFeedbackText = (() => {
      const lines = [];
      for (let i = 0; i < 3; i++) {
        const d = new Date(); d.setDate(d.getDate() - i);
        const k = d.toISOString().slice(0, 10);
        const fb = (dayPlans[k] || {}).workFeedback;
        const wa = (dayPlans[k] || {}).workActual;
        if (fb?.rating || wa?.freeText) {
          const ratingStr = fb?.rating === 'productive' ? '✓ Productive' : fb?.rating === 'mixed' ? '~ Mixed' : fb?.rating === 'offtrack' ? '✗ Off track' : '';
          const energyStr = fb?.energy ? ` | energy: ${fb.energy}/10` : '';
          const noteStr = fb?.note ? ` — "${fb.note}"` : '';
          const freeStr = wa?.freeText ? ` | notes: "${wa.freeText}"` : '';
          lines.push(`- ${k}: ${ratingStr}${energyStr}${noteStr}${freeStr}`);
        }
      }
      return lines.length ? lines.join('\n') : null;
    })();

    const fourteenDaysAgo = new Date(); fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const stagnantGoals = goals.filter(g => {
      if (g.progress >= 100) return false;
      const recentEntries = (g.progressLog || []).filter(e => new Date(e.date) >= fourteenDaysAgo);
      return recentEntries.length === 0;
    });
    const stagnantGoalsText = stagnantGoals.length > 0
      ? stagnantGoals.map(g => `- ${g.text} (${g.progress}%, area: ${AREA_MAP[g.area]?.label || g.area}, deadline: ${g.deadline || "no deadline"})`).join("\n")
      : null;

    const prompt = `You are a sharp, no-nonsense life coach and strategic advisor. You know this person well:

BACKGROUND:
- Owns two businesses: a family manufacturing company (injection molding, supplier management) and an early-stage SaaS platform (supply chain communication)
- Hebrew-speaking environment, practically minded
- Vegan diet, cooks for kids
- Wants to close the gap between his potential and his actual results across ALL areas of life
- Key focus areas: Business growth, Financial improvement, Daily habits, Health & energy, Family & kids quality time, Home systems (meal planning, batch cooking), Personal growth

For stagnant goals, think like a practical coach who knows the user's life. Suggest the smallest possible concrete first step — a physical action with a location if relevant (buy X at Y, call Z, write to W). The goal is to break inertia with one tiny action this week.

THEIR GOALS:
${goalsText}

TODAY'S DATA:
${JSON.stringify(data, null, 2)}
${estimationText ? `\nTIME ESTIMATION ACCURACY (completed tasks with estimates):\n${estimationText}` : ""}${dailyScoresText ? `\nDAILY SELF-ASSESSMENT SCORES:\n${dailyScoresText}\nThe user has rated themselves on their personal daily questions. Reference these scores specifically in your analysis — if they scored low on something, address it directly. If they scored high, acknowledge it.` : ""}${morningFeedbackText ? `\nMORNING ROUTINE FEEDBACK (recent days):\n${morningFeedbackText}\nReference the morning routine pattern if relevant — consistent misses signal a routine that needs adjusting. Consistent wins deserve acknowledgment.` : ""}${workFeedbackText ? `\nWORK PHASE CHECK-IN (recent days):\n${workFeedbackText}\nComment on work productivity and energy patterns — low energy + off-track signals need direct attention. High energy + productive deserves acknowledgment. Reference specific notes they wrote if available.` : ""}${yesterdayResponse ? `\nYESTERDAY'S USER RESPONSE TO ANALYSIS:\n"${yesterdayResponse.text}"\nReference this if relevant — did they follow through on what they committed to? Acknowledge what they wrote and hold them accountable to it.` : ""}
ANALYSIS PREFERENCES (set by the user — follow these exactly):

TONE: ${tone === "tough"
  ? "Be direct and unsparing. Do not soften feedback. Name patterns and avoidance behaviors explicitly."
  : tone === "encouraging"
  ? "Lead with what went well. Be honest about gaps but frame everything as opportunity. End strongly."
  : "Be honest but constructive. Name issues clearly but with the intent to help, not to criticize."}

EMPHASIS: ${emphasis === "business"
  ? "Prioritize analysis of Business and Financial goals. Spend less time on other areas unless they directly impact work."
  : emphasis === "health"
  ? "Prioritize Health, Habits, and Energy. Flag anything that undermines physical or mental performance."
  : emphasis === "family"
  ? "Prioritize Family, Home, and Personal areas. Note whether the person is present and intentional in their personal life."
  : "Give equal weight to all 8 life areas."}

FORMAT: ${format === "quick"
  ? "Respond with exactly 5 bullet points maximum. Each bullet one sentence. Total response under 100 words. Be ruthlessly concise. Skip section headers."
  : format === "morning"
  ? "This is a morning catch-up for yesterday. Keep it light and forward-focused. What happened, what to fix today. Under 150 words. Skip most sections — just WHAT YOU ACTUALLY DID, 3 CONCRETE ACTIONS FOR TOMORROW, and PERSONAL MOTIVATION."
  : "Full analysis — use all sections as instructed below."}

USER CURRENT MODE: ${userMode === "fight"
  ? "The user is under significant pressure right now. Be sharp, tactical, and efficiency-focused. Every hour matters. Help them prioritize ruthlessly."
  : userMode === "support"
  ? "The user is struggling right now. Lead with empathy and understanding. Be gentle but honest. Focus on small wins and getting back to basics. Do not overwhelm."
  : "The user is in a stable growth phase. Be encouraging and forward-focused. Celebrate consistency."}

CRITICAL INSTRUCTION — ALWAYS RUN THE ANALYSIS:
Never refuse or say "not enough data." The absence of data is itself data — treat it as signal, not silence. Apply the principle of survivorship bias in reverse: the days that go unlogged are often the most important days to understand.

Before writing, silently assess which scenario applies:

SCENARIO A — Nothing logged (no time entries, no reflection, no completed tasks):
The day happened. Acknowledge it. Ask gently: what got in the way of logging today? Was it a hard day, a late night, something that made it feel pointless? Look at the goals and tell them what tomorrow should focus on regardless. If this pattern repeats on certain days, name it.

SCENARIO B — Partial data (some sections filled, some empty):
Analyze what WAS entered fully. For each missing section, name it specifically — e.g. "No time log entries today — the hours are unaccounted for." Treat each gap as a potential signal: no evening reflection often means the evening didn't go as planned. Ask one pointed question about the most significant missing piece.

SCENARIO C — Full data logged:
Full analysis. Briefly acknowledge that logging consistently is itself a win worth noting.

Now write the ${period === "today" ? "DAILY END-OF-DAY" : "WEEKLY"} analysis. Structure it EXACTLY like this:

**WHAT YOU ACTUALLY DID**
Honest summary of what was logged. If little or nothing was logged, acknowledge the day still happened and describe what the data silence might mean.

**GOAL ALIGNMENT SCORE**
For each goal, rate how today's actions moved it forward (or didn't). If no actions were logged, rate it 0 and explain what that streak of zeros means for the goal's timeline.

**TIME TRAPS**
What wasted time? What was reactive vs. proactive? What tasks should be delegated, eliminated, or systemized? (Example: if they drove 2.5 hours to deliver parts, call it out — that's a CEO doing a courier's job.) If time estimation data is available, comment on accuracy — are they consistently underestimating? What does that pattern reveal?

**MISSING SYSTEMS**
What unplanned things happened that a good system would have prevented? (Unplanned cooking, last-minute decisions, reactive tasks)

**MISSING DATA SIGNALS**
What wasn't logged today, and what might that tell us? Apply survivorship bias thinking — the absence of data is data. Name each missing section specifically. If nothing was logged at all, ask one honest question: what happened today that made logging feel impossible or pointless?

**3 CONCRETE ACTIONS FOR TOMORROW**
Specific. Actionable. Not generic advice. At least one action should directly address the biggest gap identified above.

**THE HARD TRUTH**
One thing they need to hear. Be direct. This person wants real insight, not comfort. If they logged nothing, the hard truth is about that.

**PERSONAL MOTIVATION**
End with 2-3 sentences of genuine, specific encouragement. Not generic. Reference something real — even if it's just that they opened this app and asked for analysis. Make them feel seen. This is the last thing they read before tomorrow — make it count.${futureMePhoto ? `
This person has set up a Future Self feature. Write the PERSONAL MOTIVATION section as if from their future self — grateful, personal, specific to their actual data. Sign it '— Future You'` : ""}
${stagnantGoalsText ? `
**STAGNANT GOALS — NEXT PHYSICAL ACTIONS**
The following goals have had NO progress log entries in the last 14 days:
${stagnantGoalsText}

For each of these stagnant goals:
- Name the goal
- Ask directly why it hasn't moved (be honest, not harsh — one sentence)
- Suggest ONE specific next physical action under 30 minutes (be specific: "buy X at Y", "send email to Z", "call W about the quote" — not generic advice)
- Suggest a specific day and time this week to do it
- Keep the entire entry under 2 sentences` : ""}

Keep it under 650 words. Every sentence must earn its place. No filler.`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const d = await res.json();
      setAnalysis(d.content?.map(b => b.text || "").join("") || "No response.");
    } catch { setAnalysis("Error. Please try again."); }
    setLoading(false);
  }

  function renderMd(text) {
    return text.split("\n").map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
      const isBold = line.startsWith("**") && line.includes("**", 2);
      if (isBold) {
        const label = line.replace(/\*\*/g, "");
        return <div key={i} style={{ fontWeight: 700, color: "#f59e0b", marginTop: 18, marginBottom: 6, fontSize: 12, letterSpacing: "0.05em" }}>{label}</div>;
      }
      // inline bold
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return (
        <div key={i} style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7, marginBottom: 2 }}>
          {parts.map((p, j) => j % 2 === 1 ? <strong key={j} style={{ color: "#cbd5e1" }}>{p}</strong> : p)}
        </div>
      );
    });
  }

  async function addToScheduleFromAnalyze(na, areaId) {
    const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
    const startTime = na.suggestedTime || "09:00";
    const durationMins = parseInt(na.duration) || 30;
    const [h, m] = startTime.split(":").map(Number);
    const endMins = Math.min(h * 60 + m + durationMins, 23 * 60);
    const endTime = `${String(Math.floor(endMins / 60)).padStart(2, "0")}:${String(endMins % 60).padStart(2, "0")}`;
    const block = { id: uid(), startTime, endTime, taskName: na.action, area: areaId };
    const currentPlan = (dayPlans || {})[tomorrowStr] || {};
    const updatedBlocks = [...(currentPlan.timeBlocks || []), block].sort((a, b) => a.startTime.localeCompare(b.startTime));
    const updated = { ...(dayPlans || {}), [tomorrowStr]: { ...currentPlan, timeBlocks: updatedBlocks } };
    if (setDayPlans) setDayPlans(updated);
    await save("dayplans", updated, userId);
  }

  const key = todayKey();
  const todayLogs = timeLogs[key] || [];
  const todayPlan = dayPlans[key] || {};
  const totalMins = todayLogs.reduce((s, l) => s + (l.mins || 0), 0);

  const threeDaysAgo = new Date(); threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  const goalsWithRecentNextAction = goals.filter(g => g.nextAction?.generatedAt && new Date(g.nextAction.generatedAt) >= threeDaysAgo);

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Analysis</h2>
        <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>AI reads your data and judges it against your goals. Honestly.</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
        {[
          { label: "Goals", value: goals.length, color: "#f59e0b" },
          { label: "Tasks", value: (todayPlan.todos || []).length, color: "#3b82f6" },
          { label: "Done", value: (todayPlan.todos || []).filter(t => t.done).length, color: "#10b981" },
          { label: "Logged", value: totalMins >= 60 ? `${Math.floor(totalMins/60)}h${totalMins%60}m` : `${totalMins}m`, color: "#8b5cf6" },
        ].map(s => (
          <div key={s.label} style={{ ...card, textAlign: "center", padding: "12px 8px" }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "#334155", marginTop: 2, letterSpacing: "0.05em" }}>{s.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Trigger */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {["today", "week"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: "7px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: "inherit",
              fontWeight: 600, fontSize: 12, transition: "all 0.15s",
              background: period === p ? "#f59e0b" : "#1e293b",
              color: period === p ? "#000" : "#475569",
            }}>{p === "today" ? "Today" : "This Week"}</button>
          ))}
        </div>
        {/* Prefs tags */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {[TONE_LABELS[tone], EMPHASIS_LABELS[emphasis], FORMAT_LABELS[format]].map((label, i) => (
            <button key={i} onClick={() => setTab("Settings")} style={{
              fontSize: 10, padding: "3px 9px", borderRadius: 20, border: "1px solid #1e293b",
              background: "#0f172a", color: "#64748b", cursor: "pointer", fontFamily: "inherit",
              fontWeight: 600, letterSpacing: "0.04em", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#8b5cf6"; e.currentTarget.style.color = "#8b5cf6"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e293b"; e.currentTarget.style.color = "#64748b"; }}>
              {label}
            </button>
          ))}
          <span style={{ fontSize: 10, color: "#334155" }}>· tap to change in Settings</span>
        </div>
        {goals.length === 0 && (
          <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10, padding: "8px 12px", background: "#ef444411", borderRadius: 8 }}>
            ⚠ Add goals first — the analysis is only as good as the goals it measures against.
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: userMode === "fight" ? "#ef4444" : userMode === "support" ? "#3b82f6" : "#10b981" }}>
            {userMode === "fight" ? "⚔️ FIGHT MODE" : userMode === "support" ? "🤝 SUPPORT MODE" : "🌱 GROWTH MODE"}
          </span>
          <button onClick={() => setTab("Settings")} style={{ fontSize: 9, color: "#334155", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>· change</button>
        </div>
        <button onClick={runAnalysis} disabled={loading} style={{
          ...btn("#8b5cf6"), width: "100%", fontSize: 14, padding: "13px",
          opacity: loading ? 0.7 : 1,
        }}>
          {loading ? "Reading your data..." : "✦ Get Goal-Aware Analysis"}
        </button>
      </div>

      {/* Output */}
      {(loading || analysis) && (
        <div style={{ ...card, borderColor: "#8b5cf633" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>Analyzing against your goals...</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 7, height: 7, borderRadius: "50%", background: "#8b5cf6",
                    animation: `pulse 1.2s ${i * 0.2}s ease-in-out infinite`,
                  }} />
                ))}
              </div>
              <style>{`
  @keyframes pulse{0%,100%{opacity:.2;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
  @keyframes pulse-badge{0%,100%{opacity:1}50%{opacity:0.6}}
`}</style>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4 }}>
                {period === "today" ? "DAILY" : "WEEKLY"} ANALYSIS — {fmtDate(key)}
              </div>
              {renderMd(analysis)}
            </div>
          )}
        </div>
      )}

      {/* User Response */}
      {analysis && !loading && (
        <div style={{ ...card, marginTop: 16, borderLeft: "3px solid #10b981", paddingLeft: 16 }}>
          <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 10 }}>YOUR RESPONSE</div>
          {responses[key] && !responseEditing ? (
            <div>
              <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: 10 }}>{responses[key].text}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 10, color: "#334155" }}>
                  Saved {new Date(responses[key].savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                <button onClick={() => { setResponseEditing(true); setResponseText(responses[key].text); }} style={{
                  ...btn("#1e293b"), fontSize: 11, padding: "4px 12px", border: "1px solid #334155",
                }}>Edit</button>
              </div>
              {!analysisDialogue[key]?.aiReply && (
                <button onClick={getAIDialogue} disabled={dialogueLoading} style={{
                  ...btn("#8b5cf6", "#fff"), width: "100%", fontSize: 12,
                  opacity: dialogueLoading ? 0.6 : 1,
                }}>
                  {dialogueLoading ? "Thinking…" : "✦ Get AI Response to My Feedback"}
                </button>
              )}
              {analysisDialogue[key]?.aiReply && (
                <div style={{ borderLeft: "3px solid #8b5cf6", paddingLeft: 14, marginTop: 4 }}>
                  <div style={{ fontSize: 10, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8 }}>✦ AI RESPONSE</div>
                  {renderMd(analysisDialogue[key].aiReply)}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                    <span style={{ fontSize: 10, color: "#334155" }}>
                      {new Date(analysisDialogue[key].repliedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <button onClick={getAIDialogue} disabled={dialogueLoading} style={{
                      ...btn("#1e293b"), fontSize: 11, padding: "4px 12px", border: "1px solid #334155",
                    }}>{dialogueLoading ? "…" : "↻ Regenerate"}</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <textarea
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
                placeholder={"How will you improve tomorrow? Do you agree with the analysis? Is there context the AI missed? Write your commitment here."}
                style={{
                  width: "100%", minHeight: 100, background: "#0f172a", border: "1px solid #1e293b",
                  borderRadius: 8, color: "#e2e8f0", fontSize: 13, padding: "10px 12px",
                  fontFamily: "inherit", lineHeight: 1.6, resize: "vertical", boxSizing: "border-box",
                  outline: "none",
                }}
              />
              <button
                onClick={saveResponse}
                disabled={responseSaving || !responseText.trim()}
                style={{
                  ...btn("#10b981"), marginTop: 10, fontSize: 13, padding: "10px 20px",
                  opacity: responseSaving || !responseText.trim() ? 0.6 : 1,
                }}
              >{responseSaving ? "Saving…" : "Save Response"}</button>
            </div>
          )}
        </div>
      )}

      {/* Goal overview */}
      {goals.length > 0 && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>GOAL PROGRESS OVERVIEW</div>
          {goals.map(g => {
            const area = AREA_MAP[g.area] || AREAS[0];
            return (
              <div key={g.id} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    <span style={{ color: area.color }}>{area.icon}</span> {g.text.slice(0, 55)}{g.text.length > 55 ? "…" : ""}
                  </span>
                  <span style={{ fontSize: 12, color: area.color, fontWeight: 700 }}>{g.progress}%</span>
                </div>
                <div style={{ height: 4, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ width: g.progress + "%", height: "100%", background: area.color, borderRadius: 2, transition: "width 0.5s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Next Actions This Week */}
      {goalsWithRecentNextAction.length > 0 && (
        <div style={{ ...card, marginTop: 16, borderLeft: "3px solid #10b981" }}>
          <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>✦ NEXT ACTIONS THIS WEEK</div>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>Next steps queued across your goals — ready to schedule.</div>
          {goalsWithRecentNextAction.map(g => {
            const area = AREA_MAP[g.area] || AREAS[0];
            const na = g.nextAction;
            return (
              <div key={g.id} style={{ background: "#071a12", border: "1px solid #10b98122", borderLeft: `3px solid ${area.color}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: area.color, fontWeight: 700, marginBottom: 4 }}>{area.icon} {g.text.slice(0, 50)}{g.text.length > 50 ? "…" : ""}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 6, lineHeight: 1.5 }}>{na.action}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>{na.suggestedDay} · {na.suggestedTime} · {na.duration}</div>
                <button onClick={() => addToScheduleFromAnalyze(na, g.area)}
                  style={{ ...btn("#10b981"), fontSize: 12 }}>
                  📅 Add to Schedule
                </button>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════════
// RECURRING TASKS
// ═══════════════════════════════════════════════════════════════════════════════
const DOW_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function RecurringFreqConfig({ f, onChange }) {
  if (f.frequency === "weekly") return (
    <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:6 }}>
      {DOW_LABELS.map((d,i) => (
        <button key={i} onClick={() => onChange({...f, dayOfWeek:i})} style={{
          padding:"4px 9px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:600,
          background: f.dayOfWeek===i ? "#06b6d422" : "#1e293b",
          color: f.dayOfWeek===i ? "#06b6d4" : "#475569",
        }}>{d}</button>
      ))}
    </div>
  );
  if (f.frequency === "monthly") return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
      <span style={{ fontSize:12, color:"#64748b" }}>Day of month:</span>
      <input type="number" min="1" max="31" value={f.dayOfMonth}
        onChange={e => onChange({...f, dayOfMonth: parseInt(e.target.value)||1})}
        style={{ ...inp, width:64, padding:"5px 8px", fontSize:12, textAlign:"center" }} />
    </div>
  );
  if (f.frequency === "yearly") return (
    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:6 }}>
      <select value={f.month} onChange={e => onChange({...f, month:parseInt(e.target.value)})}
        style={{ ...inp, width:"auto", padding:"5px 8px", fontSize:12, colorScheme:"dark" }}>
        {MONTH_NAMES.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
      </select>
      <input type="number" min="1" max="31" value={f.dayOfMonth}
        onChange={e => onChange({...f, dayOfMonth:parseInt(e.target.value)||1})}
        style={{ ...inp, width:64, padding:"5px 8px", fontSize:12, textAlign:"center" }} />
    </div>
  );
  return null;
}

function RecurringView({ recurringTasks, setRecurringTasks, userId }) {
  const [form, setForm] = useState({
    text:"", tag:"work", area:"business", frequency:"weekly",
    dayOfWeek:1, dayOfMonth:1, month:1, estimatedMins:"",
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  function ordinal(n) { const v=n%100,s=["th","st","nd","rd"]; return s[(v-20)%10]||s[v]||s[0]; }
  function freqLabel(tmpl) {
    if (tmpl.frequency==="daily") return "Daily";
    if (tmpl.frequency==="weekly") return `Every ${DOW_LABELS[tmpl.dayOfWeek??1]}`;
    if (tmpl.frequency==="monthly") return `${tmpl.dayOfMonth??1}${ordinal(tmpl.dayOfMonth??1)} of month`;
    if (tmpl.frequency==="yearly") return `${MONTH_NAMES[(tmpl.month??1)-1]} ${tmpl.dayOfMonth??1}`;
    return tmpl.frequency;
  }
  function nextDue(tmpl) {
    const t=new Date(); t.setHours(0,0,0,0);
    if (tmpl.frequency==="daily") return todayKey();
    if (tmpl.frequency==="weekly") {
      const diff=((tmpl.dayOfWeek??1)-t.getDay()+7)%7;
      const d=new Date(t); d.setDate(t.getDate()+diff);
      return d.toISOString().slice(0,10);
    }
    if (tmpl.frequency==="monthly") {
      const d=new Date(t.getFullYear(),t.getMonth(),tmpl.dayOfMonth??1);
      if(d<t) d.setMonth(d.getMonth()+1);
      return d.toISOString().slice(0,10);
    }
    if (tmpl.frequency==="yearly") {
      const d=new Date(t.getFullYear(),(tmpl.month??1)-1,tmpl.dayOfMonth??1);
      if(d<t) d.setFullYear(d.getFullYear()+1);
      return d.toISOString().slice(0,10);
    }
    return "—";
  }
  function tagColor(t) { return t==="work"?"#3b82f6":t==="personal"?"#8b5cf6":"#10b981"; }

  async function addRecurring() {
    if (!form.text.trim()) return;
    const tmpl = {
      id:uid(), text:form.text.trim(), tag:form.tag, area:form.area,
      frequency:form.frequency, dayOfWeek:form.dayOfWeek, dayOfMonth:form.dayOfMonth,
      month:form.month, estimatedMins:form.estimatedMins?parseInt(form.estimatedMins,10):null,
      createdAt:new Date().toISOString(), lastCompleted:null,
    };
    const updated=[tmpl,...recurringTasks];
    setRecurringTasks(updated);
    await save("recurring-tasks",updated,userId);
    setForm({text:"",tag:"work",area:"business",frequency:"weekly",dayOfWeek:1,dayOfMonth:1,month:1,estimatedMins:""});
  }

  async function saveEdit() {
    const updated=recurringTasks.map(r=>r.id===editingId?{...r,...editForm}:r);
    setRecurringTasks(updated);
    await save("recurring-tasks",updated,userId);
    setEditingId(null); setEditForm({});
  }

  async function deleteRecurring(id) {
    const updated=recurringTasks.filter(r=>r.id!==id);
    setRecurringTasks(updated);
    await save("recurring-tasks",updated,userId);
    setConfirmDelete(null);
  }

  return (
    <div>
      {/* Add form */}
      <div style={{ ...card, marginBottom:14, borderColor:"#06b6d444" }}>
        <div style={{ fontSize:11, color:"#06b6d4", fontWeight:700, letterSpacing:"0.08em", marginBottom:10 }}>
          🔄 ADD RECURRING TASK
        </div>
        <div style={{ display:"flex", gap:8, marginBottom:10 }}>
          <input value={form.text} onChange={e=>setForm(f=>({...f,text:e.target.value}))}
            onKeyDown={e=>e.key==="Enter"&&addRecurring()}
            placeholder="Task name..." style={{ ...inp, flex:1, fontSize:14 }} />
          <input type="number" min="1" max="999" value={form.estimatedMins}
            onChange={e=>setForm(f=>({...f,estimatedMins:e.target.value}))}
            placeholder="min" style={{ ...inp, width:64, padding:"9px 6px", fontSize:12, textAlign:"center" }} />
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
          {["work","personal","shopping"].map(t=>(
            <button key={t} onClick={()=>setForm(f=>({...f,tag:t}))} style={{
              padding:"5px 10px", borderRadius:8, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:600,
              background:form.tag===t?tagColor(t)+"22":"#1e293b", color:form.tag===t?tagColor(t):"#475569",
            }}>{t==="work"?"💼 Work":t==="personal"?"🏠 Personal":"🛒 Shopping"}</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
          {AREAS.map(a=>(
            <button key={a.id} onClick={()=>setForm(f=>({...f,area:a.id}))} style={{
              padding:"4px 8px", borderRadius:8, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:10,
              background:form.area===a.id?a.color+"22":"#1e293b", color:form.area===a.id?a.color:"#475569",
            }}>{a.icon} {a.label}</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {["daily","weekly","monthly","yearly"].map(f=>(
            <button key={f} onClick={()=>setForm(fv=>({...fv,frequency:f}))} style={{
              padding:"5px 12px", borderRadius:8, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:600,
              background:form.frequency===f?"#06b6d422":"#1e293b", color:form.frequency===f?"#06b6d4":"#475569",
              textTransform:"capitalize",
            }}>{f}</button>
          ))}
        </div>
        <RecurringFreqConfig f={form} onChange={setForm} />
        <button onClick={addRecurring} disabled={!form.text.trim()} style={{
          ...btn("#06b6d4","#000"), marginTop:12, width:"100%", opacity:form.text.trim()?1:0.4,
        }}>+ Add Recurring Task</button>
      </div>

      {/* Template list */}
      {recurringTasks.length===0 && (
        <div style={{ textAlign:"center", color:"#1e293b", padding:"40px 0", fontSize:13 }}>
          No recurring tasks yet. Add one above.
        </div>
      )}
      {recurringTasks.map(tmpl=>(
        <div key={tmpl.id} style={{ ...card, marginBottom:8, borderLeft:"3px solid #06b6d4" }}>
          {editingId===tmpl.id ? (
            <div>
              <input value={editForm.text||""} onChange={e=>setEditForm(f=>({...f,text:e.target.value}))}
                style={{ ...inp, width:"100%", marginBottom:8 }} />
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                {["work","personal","shopping"].map(t=>(
                  <button key={t} onClick={()=>setEditForm(f=>({...f,tag:t}))} style={{
                    padding:"4px 8px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11,
                    background:editForm.tag===t?tagColor(t)+"22":"#1e293b", color:editForm.tag===t?tagColor(t):"#475569",
                  }}>{t==="work"?"💼":t==="personal"?"🏠":"🛒"} {t}</button>
                ))}
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                {["daily","weekly","monthly","yearly"].map(f=>(
                  <button key={f} onClick={()=>setEditForm(fv=>({...fv,frequency:f}))} style={{
                    padding:"4px 8px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11,
                    background:editForm.frequency===f?"#06b6d422":"#1e293b", color:editForm.frequency===f?"#06b6d4":"#475569",
                    textTransform:"capitalize",
                  }}>{f}</button>
                ))}
              </div>
              <RecurringFreqConfig f={editForm} onChange={setEditForm} />
              <div style={{ display:"flex", gap:8, marginTop:10 }}>
                <button onClick={saveEdit} style={{ ...btn("#10b981"), padding:"6px 14px", fontSize:12 }}>Save</button>
                <button onClick={()=>{setEditingId(null);setEditForm({});}} style={{ ...btn("#1e293b","#64748b"), padding:"6px 12px", fontSize:12 }}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, color:"#cbd5e1", fontWeight:600, marginBottom:6 }}>🔄 {tmpl.text}</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#06b6d422", color:"#06b6d4", fontWeight:700 }}>
                      {freqLabel(tmpl)}
                    </span>
                    <span style={{ fontSize:10, color:tagColor(tmpl.tag), fontWeight:600 }}>
                      {tmpl.tag==="work"?"💼":tmpl.tag==="personal"?"🏠":"🛒"} {tmpl.tag}
                    </span>
                    {tmpl.estimatedMins&&<span style={{ fontSize:10, color:"#475569" }}>⏱ {tmpl.estimatedMins}min</span>}
                    <span style={{ fontSize:10, color:"#334155" }}>Next: {nextDue(tmpl)}</span>
                    {tmpl.lastCompleted&&<span style={{ fontSize:10, color:"#334155" }}>Done: {tmpl.lastCompleted}</span>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:4, flexShrink:0, marginLeft:8 }}>
                  <button onClick={()=>{setEditingId(tmpl.id);setEditForm({...tmpl});}} style={{
                    background:"none", border:"1px solid #1e293b", borderRadius:6,
                    color:"#64748b", cursor:"pointer", padding:"4px 8px", fontSize:11, fontFamily:"inherit",
                  }}>Edit</button>
                  {confirmDelete===tmpl.id ? (
                    <>
                      <button onClick={()=>deleteRecurring(tmpl.id)} style={{
                        background:"#ef444422", border:"1px solid #ef444444", borderRadius:6,
                        color:"#ef4444", cursor:"pointer", padding:"4px 8px", fontSize:11, fontFamily:"inherit",
                      }}>Delete?</button>
                      <button onClick={()=>setConfirmDelete(null)} style={{
                        background:"none", border:"1px solid #1e293b", borderRadius:6,
                        color:"#475569", cursor:"pointer", padding:"4px 4px", fontSize:13, fontFamily:"inherit",
                      }}>✕</button>
                    </>
                  ) : (
                    <button onClick={()=>setConfirmDelete(tmpl.id)} style={{
                      background:"none", border:"1px solid #1e293b", borderRadius:6,
                      color:"#475569", cursor:"pointer", padding:"4px 8px", fontSize:11, fontFamily:"inherit",
                    }}
                    onMouseEnter={e=>e.currentTarget.style.color="#ef4444"}
                    onMouseLeave={e=>e.currentTarget.style.color="#475569"}>Del</button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TASKS VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function TasksView({ tasks, setTasks, userId, recurringTasks = [], setRecurringTasks }) {
  const [input, setInput] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [when, setWhen] = useState("today"); // today | someday
  const [tag, setTag] = useState("work"); // work | personal | shopping
  const [filter, setFilter] = useState("all"); // all | today | late | recurring | someday | done | shopping
  const [estimatedMins, setEstimatedMins] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const inputRef = useRef(null);
  const sortedRef = useRef([]);

  async function addTask() {
    if (!input.trim()) return;
    const t = {
      id: uid(),
      text: input.trim(),
      urgent,
      when,
      tag,
      done: false,
      doneAt: null,
      order: Date.now(),
      createdAt: new Date().toISOString(),
      ...(estimatedMins ? { estimatedMins: parseInt(estimatedMins, 10) } : {}),
      ...(newUrl.trim() ? { url: newUrl.trim() } : {}),
    };
    const updated = [t, ...tasks];
    setTasks(updated);
    await save("tasks", updated, userId);
    setInput("");
    setUrgent(false);
    setTag("work");
    setEstimatedMins("");
    setNewUrl("");
    inputRef.current?.focus();
  }

  // The sorted list is grouped urgent → late → rest, and manual order only
  // applies inside a group. A swap across a boundary gets undone by the sort
  // on the next render, so ▲▼ stays inside the group.
  function taskGroup(t) {
    if (t.urgent) return "urgent";
    return taskDaysLate(t) >= 1 ? "late" : "rest";
  }

  async function moveTask(id, direction) {
    const s = sortedRef.current;
    const idx = s.findIndex(t => t.id === id);
    if (idx === -1) return;

    // The done list sorts purely by order — no grouping there.
    const groupOf = t => filter === "done" ? "done" : taskGroup(t);
    const group = groupOf(s[idx]);

    // Nearest task in the SAME group in the requested direction — skip over
    // tasks from other groups instead of blocking the move.
    let swapIdx = -1;
    if (direction === "up") {
      for (let i = idx - 1; i >= 0; i--) {
        if (groupOf(s[i]) === group) { swapIdx = i; break; }
      }
    } else {
      for (let i = idx + 1; i < s.length; i++) {
        if (groupOf(s[i]) === group) { swapIdx = i; break; }
      }
    }
    if (swapIdx === -1) return; // already at top/bottom of its group

    // Normalize: assign clean sequential order values to ALL visible tasks
    // first, so there are no timestamp vs integer scale conflicts.
    const normalized = tasks.map(t => {
      const posInSorted = s.findIndex(st => st.id === t.id);
      if (posInSorted !== -1) return { ...t, order: posInSorted * 100 };
      return t;
    });

    const idA = s[idx].id;
    const idB = s[swapIdx].id;
    const updated = normalized.map(t => {
      if (t.id === idA) return { ...t, order: swapIdx * 100 };
      if (t.id === idB) return { ...t, order: idx * 100 };
      return t;
    });

    setTasks(updated);
    await save("tasks", updated, userId);
  }

  async function toggleTask(id) {
    const task = tasks.find(x => x.id === id);
    const nowDone = !task?.done;
    const updated = tasks.map(t =>
      t.id === id ? { ...t, done: !t.done, doneAt: !t.done ? new Date().toISOString() : null } : t
    );
    setTasks(updated);
    await save("tasks", updated, userId);
    if (task?.isRecurring && task?.recurringId && nowDone && setRecurringTasks) {
      const updatedRec = recurringTasks.map(r =>
        r.id === task.recurringId ? { ...r, lastCompleted: todayKey() } : r
      );
      setRecurringTasks(updatedRec);
      await save("recurring-tasks", updatedRec, userId);
    }
  }

  async function deleteTask(id) {
    const updated = tasks.filter(t => t.id !== id);
    setTasks(updated);
    await save("tasks", updated, userId);
  }

  async function clearDone() {
    const updated = tasks.filter(t => !t.done);
    setTasks(updated);
    await save("tasks", updated, userId);
  }

  async function editTask(id, updates) {
    const patch = typeof updates === "string" ? { text: updates } : updates;
    const updated = tasks.map(t => t.id === id ? { ...t, ...patch } : t);
    setTasks(updated);
    await save("tasks", updated, userId);
  }

  async function updateTask(id, changes) {
    const updated = tasks.map(t => t.id === id ? { ...t, ...changes } : t);
    setTasks(updated);
    await save("tasks", updated, userId);
  }

  function taskDaysLate(t) {
    if (t.done || t.when !== "today" || !t.createdAt) return 0;
    return Math.floor((Date.now() - new Date(t.createdAt).getTime()) / (1000*60*60*24));
  }

  // Filtered and sorted tasks
  const filtered = tasks.filter(t => {
    if (filter === "recurring") return false; // managed separately
    if (filter === "today") return !t.done && t.when === "today";
    if (filter === "late") return !t.done && t.when === "today" && taskDaysLate(t) >= 1;
    if (filter === "someday") return !t.done && t.when === "someday";
    if (filter === "done") return t.done;
    if (filter === "shopping") return !t.done && t.tag === "shopping";
    if (filter === "work") return !t.done && t.tag === "work";
    if (filter === "personal") return !t.done && t.tag === "personal";
    return !t.done;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (filter === "done") {
      const aOrder = a.order ?? new Date(a.createdAt).getTime();
      const bOrder = b.order ?? new Date(b.createdAt).getTime();
      return aOrder - bOrder;
    }
    // 1. Urgent group always before non-urgent
    if (a.urgent && !b.urgent) return -1;
    if (!a.urgent && b.urgent) return 1;
    // 2. Within urgent — manual order only (user sets priority with ▲▼)
    if (a.urgent && b.urgent) {
      const aOrder = a.order ?? new Date(a.createdAt).getTime();
      const bOrder = b.order ?? new Date(b.createdAt).getTime();
      return aOrder - bOrder;
    }
    // 3. Within non-urgent — late tasks float up
    const al = taskDaysLate(a), bl = taskDaysLate(b);
    if (al > 0 && bl === 0) return -1;
    if (al === 0 && bl > 0) return 1;
    // 4. Inside the late group and inside the rest — manual order, so ▲▼ works
    const aOrder = a.order ?? new Date(a.createdAt).getTime();
    const bOrder = b.order ?? new Date(b.createdAt).getTime();
    return aOrder - bOrder;
  });
  sortedRef.current = sorted;

  const todayCount = tasks.filter(t => !t.done && t.when === "today").length;
  const urgentCount = tasks.filter(t => !t.done && t.urgent).length;
  const doneCount = tasks.filter(t => t.done).length;
  const somedayCount = tasks.filter(t => !t.done && t.when === "someday").length;
  const workCount = tasks.filter(t => !t.done && t.tag === "work").length;
  const personalCount = tasks.filter(t => !t.done && t.tag === "personal").length;
  const shoppingCount = tasks.filter(t => !t.done && t.tag === "shopping").length;
  const lateCount = tasks.filter(t => taskDaysLate(t) >= 1).length;
  const recurringCount = recurringTasks.length;
  const openCount = tasks.filter(t => !t.done).length;

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Tasks</h2>
        <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>Quick capture. Fast check-off. Nothing slips through.</p>
      </div>

      {/* Quick add — hidden in recurring view */}
      <div style={{ ...card, marginBottom: 14, borderColor: "#334155", display: filter === "recurring" ? "none" : undefined }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addTask()}
            placeholder="What needs to get done? (Enter to add)"
            style={{ ...inp, flex: 1, fontSize: 15 }}
            autoFocus
          />
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <input
              type="number" min="1" max="999"
              value={estimatedMins}
              onChange={e => setEstimatedMins(e.target.value)}
              placeholder="min"
              title="Estimated time (optional)"
              style={{ ...inp, width: 52, padding: "9px 6px", fontSize: 12, textAlign: "center" }}
            />
            <span style={{ fontSize: 11, color: "#475569", whiteSpace: "nowrap" }}>min</span>
          </div>
          <button onClick={addTask} disabled={!input.trim()} style={{
            ...btn("#f59e0b", "#000"),
            opacity: input.trim() ? 1 : 0.4,
            fontSize: 20, padding: "8px 16px",
          }}>+</button>
        </div>

        {/* Urgency + When + Tag */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setUrgent(u => !u)} style={{
            flex: 1, minWidth: 80, padding: "7px 8px", borderRadius: 8, border: "none",
            background: urgent ? "#ef444415" : "#1e293b",
            color: urgent ? "#ef4444" : "#475569",
            cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
            transition: "all 0.15s",
          }}>{urgent ? "🔥 Urgent" : "🔥 Urgent?"}</button>
          <button onClick={() => setWhen(w => w === "today" ? "someday" : "today")} style={{
            flex: 1, minWidth: 80, padding: "7px 8px", borderRadius: 8, border: "none",
            background: when === "today" ? "#3b82f615" : "#1e293b",
            color: when === "today" ? "#3b82f6" : "#475569",
            cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
            transition: "all 0.15s",
          }}>{when === "today" ? "📅 Today" : "🗓 Someday"}</button>
          {/* Tag selector */}
          {["work", "personal", "shopping"].map(t => (
            <button key={t} onClick={() => setTag(t)} style={{
              flex: 1, minWidth: 70, padding: "7px 8px", borderRadius: 8, border: "none",
              background: tag === t ? (t === "work" ? "#3b82f615" : t === "personal" ? "#8b5cf615" : "#10b98115") : "#1e293b",
              color: tag === t ? (t === "work" ? "#3b82f6" : t === "personal" ? "#8b5cf6" : "#10b981") : "#475569",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
              transition: "all 0.15s",
            }}>{t === "work" ? "💼 Work" : t === "personal" ? "🏠 Personal" : "🛒 Shopping"}</button>
          ))}
        </div>
        <input
          value={newUrl}
          onChange={e => setNewUrl(e.target.value)}
          placeholder="Link (optional) — paste any URL"
          style={{ ...inp, width: "100%", marginTop: 8, fontSize: 12, boxSizing: "border-box" }}
        />
      </div>

      {/* Stats row */}
      {tasks.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {[
            { label: `All (${openCount})`, id: "all", color: "#f59e0b" },
            { label: `Today (${todayCount})`, id: "today", color: "#3b82f6" },
            ...(lateCount > 0 ? [{ label: `🔥 Late (${lateCount})`, id: "late", color: "#f97316" }] : []),
            { label: `🔄 Recurring (${recurringCount})`, id: "recurring", color: "#06b6d4" },
            { label: `💼 Work (${workCount})`, id: "work", color: "#3b82f6" },
            { label: `🏠 Personal (${personalCount})`, id: "personal", color: "#8b5cf6" },
            { label: `🛒 Shopping (${shoppingCount})`, id: "shopping", color: "#10b981" },
            { label: `Someday (${somedayCount})`, id: "someday", color: "#64748b" },
            { label: `Done (${doneCount})`, id: "done", color: "#475569" },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{
              padding: "5px 12px", borderRadius: 20,
              background: filter === f.id ? f.color + "22" : "#0f172a",
              color: filter === f.id ? f.color : "#334155",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
              border: `1px solid ${filter === f.id ? f.color : "#1e293b"}`,
              transition: "all 0.15s",
            }}>{f.label}</button>
          ))}
          {doneCount > 0 && (
            <button onClick={clearDone} style={{
              padding: "5px 12px", borderRadius: 20, border: "1px solid #1e293b",
              background: "#0f172a", color: "#334155",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12,
              marginLeft: "auto",
            }}
            onMouseEnter={e => e.target.style.color="#ef4444"}
            onMouseLeave={e => e.target.style.color="#334155"}>
              Clear done
            </button>
          )}
        </div>
      )}

      {/* Recurring management view */}
      {filter === "recurring" && (
        <RecurringView recurringTasks={recurringTasks} setRecurringTasks={setRecurringTasks} userId={userId} />
      )}

      {/* Task list */}
      {filter !== "recurring" && sorted.length === 0 && (
        <div style={{ textAlign: "center", color: "#1e293b", padding: "40px 0", fontSize: 13 }}>
          {filter === "done" ? "No completed tasks yet." : "Nothing here. Add something above. ✓"}
        </div>
      )}

      {/* Summary line */}
      {filter !== "recurring" && openCount > 0 && (
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>
          {openCount} tasks open{urgentCount > 0 ? ` · ${urgentCount} urgent` : ""}{lateCount > 0 ? ` · ${lateCount} overdue` : ""}
        </div>
      )}

      {filter !== "recurring" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {sorted.map((t, index) => (
            <TaskItem key={t.id} t={t} index={index + 1} onToggle={toggleTask} onDelete={deleteTask} onEdit={editTask} onMove={filter !== "done" ? moveTask : null} onUpdate={updateTask} />
          ))}
        </div>
      )}

      {/* Urgent section label */}
      {urgentCount > 0 && filter === "all" && (
        <div style={{ fontSize: 11, color: "#ef4444", fontWeight: 700, marginBottom: 6, letterSpacing: "0.08em" }}>
          🔥 {urgentCount} URGENT
        </div>
      )}
    </div>
  );
}

function TaskItem({ t, index, onToggle, onDelete, onEdit, onMove, onUpdate }) {
  const daysLate = (!t.done && t.when === "today" && t.createdAt)
    ? Math.floor((Date.now() - new Date(t.createdAt).getTime()) / (1000*60*60*24))
    : 0;

  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistInput, setChecklistInput] = useState("");
  const [addingSubTask, setAddingSubTask] = useState(false);
  const [subTaskInput, setSubTaskInput] = useState("");
  const [allDonePrompt, setAllDonePrompt] = useState(false);
  const inputRef = useRef(null);
  const checklistInputRef = useRef(null);
  const subTaskInputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
  useEffect(() => { if (checklistOpen) checklistInputRef.current?.focus(); }, [checklistOpen]);
  useEffect(() => { if (addingSubTask) subTaskInputRef.current?.focus(); }, [addingSubTask]);

  function addItem() {
    if (!checklistInput.trim()) return;
    const items = [...(t.items || []), { id: uid(), text: checklistInput.trim(), done: false }];
    onUpdate(t.id, { items });
    setChecklistInput("");
    checklistInputRef.current?.focus();
  }
  function toggleItem(itemId) {
    const items = (t.items || []).map(it => it.id === itemId ? { ...it, done: !it.done } : it);
    onUpdate(t.id, { items });
  }
  function deleteItem(itemId) {
    const items = (t.items || []).filter(it => it.id !== itemId);
    onUpdate(t.id, { items });
  }

  function addSubTask() {
    if (!subTaskInput.trim()) return;
    const subTasks = [...(t.subTasks || []), { id: uid(), text: subTaskInput.trim(), done: false, createdAt: new Date().toISOString() }];
    onEdit(t.id, { subTasks });
    setSubTaskInput("");
    subTaskInputRef.current?.focus();
  }
  function toggleSubTask(stId) {
    const subTasks = (t.subTasks || []).map(s => s.id === stId ? { ...s, done: !s.done } : s);
    onEdit(t.id, { subTasks });
    setAllDonePrompt(subTasks.length > 0 && subTasks.every(s => s.done));
  }
  function deleteSubTask(stId) {
    const subTasks = (t.subTasks || []).filter(s => s.id !== stId);
    onEdit(t.id, { subTasks });
    setAllDonePrompt(false);
  }

  async function askTaskAI() {
    setAiLoading(true); setAiResponse("");
    const prompt = `You are a practical life coach inside the Alicanb productivity app.

The user has a task: "${t.text}"
Tag: ${t.tag || "work"} | Priority: ${t.urgent ? "Urgent" : "Normal"} | When: ${t.when}

Give them SHORT, PRACTICAL advice on this specific task:
- Best way to approach or execute it
- Any common mistakes to avoid
- One concrete first step

Max 150 words. Be direct. No fluff. No generic advice.`;

    try {
      const res = await fetch("/.netlify/functions/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-opus-4-5",
          max_tokens: 300,
          messages: [{ role: "user", content: prompt }]
        })
      });
      const data = await res.json();
      setAiResponse(data.content?.[0]?.text || "No response.");
    } catch {
      setAiResponse("Something went wrong. Try again.");
    }
    setAiLoading(false);
  }

  function saveEdit() {
    if (text.trim()) onEdit(t.id, text.trim());
    setEditing(false);
  }

  return (
    <div>
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 14px", borderRadius: 10,
      background: t.done ? "rgba(255,255,255,0.02)" : t.urgent ? "#ef444408" : "#0f172a",
      border: `1px solid ${t.done ? "#1e293b" : t.urgent ? "#ef444433" : "#1e293b"}`,
      borderLeft: !t.done && t.isRecurring ? "3px solid #06b6d4" : undefined,
      opacity: t.done ? 0.5 : 1,
      transition: "all 0.2s",
    }}>
      {/* Serial number */}
      {index != null && (
        <div style={{ fontSize: 11, color: "#64748b", minWidth: 24, textAlign: "right", flexShrink: 0, fontFamily: "monospace", fontWeight: 700, paddingRight: 4 }}>
          {index}
        </div>
      )}

      {/* Checkbox */}
      <button onClick={() => onToggle(t.id)} style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0, padding: 0,
        border: `2px solid ${t.done ? "#10b981" : t.urgent ? "#ef4444" : "#334155"}`,
        background: t.done ? "#10b981" : "transparent",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {t.done && <svg width="11" height="11" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
      </button>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <div>
            <input ref={inputRef} value={text} onChange={e => setText(e.target.value)}
              onBlur={saveEdit} onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(false); }}
              style={{ width: "100%", background: "transparent", border: "none", borderBottom: "1px solid #f59e0b", color: "#fff", fontSize: "14px", fontFamily: "inherit", outline: "none", padding: "2px 0", boxSizing: "border-box" }} />
            <input
              defaultValue={t.url || ""}
              onBlur={e => { const v = e.target.value.trim(); onUpdate(t.id, { url: v || null }); }}
              placeholder="Link (optional)"
              style={{ marginTop: 4, width: "100%", background: "transparent", border: "none", borderBottom: "1px solid #334155", color: "#64748b", fontSize: "12px", fontFamily: "inherit", outline: "none", padding: "2px 0", boxSizing: "border-box" }}
            />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span onDoubleClick={() => setEditing(true)} style={{
              fontSize: 14, color: t.done ? "#475569" : "#cbd5e1",
              textDecoration: t.done ? "line-through" : "none",
              cursor: "text",
            }}>
              {t.isRecurring && <span style={{ fontSize: 10, marginRight: 4, color: "#06b6d4" }}>🔄</span>}
              {t.text}
            </span>
            {t.url && (
              <button onClick={() => window.open(t.url, "_blank")} title={t.url}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 13, lineHeight: 1, padding: "1px 3px", flexShrink: 0,
                  color: "#475569", borderRadius: 4, transition: "color 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "#3b82f6"}
                onMouseLeave={e => e.currentTarget.style.color = "#475569"}>
                🔗
              </button>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
          {!t.done ? (
            <>
              <button onClick={() => onUpdate(t.id, { when: t.when === "today" ? "someday" : "today" })} style={{
                fontSize: 10, padding: "2px 7px", borderRadius: 10, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontWeight: 600,
                background: t.when === "today" ? "#3b82f615" : "#1e293b",
                color: t.when === "today" ? "#3b82f6" : "#475569",
                transition: "all 0.15s",
              }}>{t.when === "today" ? "📅 Today" : "🗓 Someday"}</button>
              <button onClick={() => onUpdate(t.id, { tag: t.tag === "work" ? "personal" : t.tag === "personal" ? "shopping" : "work" })} style={{
                fontSize: 10, padding: "2px 7px", borderRadius: 10, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontWeight: 600,
                background: t.tag === "work" ? "#3b82f615" : t.tag === "personal" ? "#8b5cf615" : "#10b98115",
                color: t.tag === "work" ? "#3b82f6" : t.tag === "personal" ? "#8b5cf6" : "#10b981",
                transition: "all 0.15s",
              }}>{t.tag === "work" ? "💼 Work" : t.tag === "personal" ? "🏠 Personal" : "🛒 Shopping"}</button>
              <button onClick={() => onUpdate(t.id, { urgent: !t.urgent })} style={{
                fontSize: 10, padding: "2px 7px", borderRadius: 10, border: "none", cursor: "pointer",
                fontFamily: "inherit", fontWeight: 600,
                background: t.urgent ? "#ef444415" : "#1e293b",
                color: t.urgent ? "#ef4444" : "#475569",
                transition: "all 0.15s",
              }}>🔥 {t.urgent ? "Urgent" : "Normal"}</button>
              {t.estimatedMins && <span style={{ fontSize: 10, color: "#475569" }}>⏱ {t.estimatedMins}min</span>}
              {daysLate >= 1 && (
                <span style={{
                  fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 700,
                  background: daysLate >= 4 ? "#ef444420" : "#f9731620",
                  color: daysLate >= 4 ? "#ef4444" : "#f97316",
                }}>
                  {daysLate >= 4 ? "🔥" : "📅"} {daysLate} day{daysLate !== 1 ? "s" : ""} late
                </span>
              )}
              {t.isRecurring && t.frequency && (
                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "#06b6d415", color: "#06b6d4", fontWeight: 600 }}>
                  {t.frequency.charAt(0).toUpperCase() + t.frequency.slice(1)}
                </span>
              )}
              {(t.subTasks || []).length > 0 && (() => {
                const stDone = (t.subTasks || []).filter(s => s.done).length;
                const stTotal = (t.subTasks || []).length;
                return (
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 600,
                    background: stDone === stTotal ? "#10b98115" : "#f59e0b15",
                    color: stDone === stTotal ? "#10b981" : "#f59e0b",
                  }}>
                    {stDone}/{stTotal} {stDone === stTotal ? "✓" : "◉"}
                  </span>
                );
              })()}
              <button onClick={() => setAddingSubTask(v => !v)} style={{
                background: addingSubTask ? "#f59e0b15" : "none", border: "none",
                color: addingSubTask ? "#f59e0b" : "#334155", cursor: "pointer",
                fontFamily: "inherit", fontSize: 10, fontWeight: 600,
                padding: "2px 6px", borderRadius: 6, transition: "all 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.color="#f59e0b"}
              onMouseLeave={e => { if (!addingSubTask) e.currentTarget.style.color="#334155"; }}>
                + sub-task
              </button>
            </>
          ) : (
            <>
              {t.urgent && <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 700 }}>🔥 Urgent</span>}
              <span style={{ fontSize: 10, color: "#475569" }}>{t.when === "today" ? "📅 Today" : "🗓 Someday"}</span>
              {t.tag && <span style={{ fontSize: 10, fontWeight: 600, color: t.tag === "work" ? "#3b82f6" : t.tag === "personal" ? "#8b5cf6" : "#10b981" }}>
                {t.tag === "work" ? "💼" : t.tag === "personal" ? "🏠" : "🛒"}
              </span>}
              {t.doneAt && <span style={{ fontSize: 10, color: "#334155" }}>✓ {new Date(t.doneAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
              {/* Actual time: show comparison if both exist, else prompt for input */}
              {t.estimatedMins && t.actualMins ? (
                t.actualMins <= t.estimatedMins
                  ? <span style={{ fontSize: 10, fontWeight: 600, color: "#10b981" }}>✓ {t.actualMins}min</span>
                  : <span style={{ fontSize: 10, fontWeight: 600, color: "#f59e0b" }}>{t.actualMins}min <span style={{ fontWeight: 400 }}>(est. {t.estimatedMins})</span></span>
              ) : t.estimatedMins && !t.actualMins ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <input
                    type="number" min="1" max="999"
                    defaultValue={t.estimatedMins}
                    onClick={e => e.stopPropagation()}
                    onBlur={e => e.target.value && onUpdate(t.id, { actualMins: parseInt(e.target.value, 10) })}
                    style={{ width: 38, background: "#1e293b", border: "1px solid #334155", borderRadius: 4, padding: "1px 4px", color: "#94a3b8", fontSize: 10, fontFamily: "inherit", outline: "none", textAlign: "center" }}
                    title="How long did it take? (min)"
                  />
                  <span style={{ fontSize: 10, color: "#334155" }}>min?</span>
                </span>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Up/Down + AI + Delete */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, alignItems: "center" }}>
        {!t.done && onMove && (
          <>
            <button onClick={() => onMove(t.id, "up")} style={{
              background: "none", border: "none", color: "#334155", cursor: "pointer",
              fontSize: 12, lineHeight: 1, padding: "1px 4px",
            }} onMouseEnter={e => e.target.style.color="#f59e0b"}
               onMouseLeave={e => e.target.style.color="#334155"}>▲</button>
            <button onClick={() => onMove(t.id, "down")} style={{
              background: "none", border: "none", color: "#334155", cursor: "pointer",
              fontSize: 12, lineHeight: 1, padding: "1px 4px",
            }} onMouseEnter={e => e.target.style.color="#f59e0b"}
               onMouseLeave={e => e.target.style.color="#334155"}>▼</button>
          </>
        )}
        {!t.done && t.tag === "shopping" && (
          <button onClick={() => setChecklistOpen(o => !o)} title="Shopping list"
            style={{
              background: checklistOpen ? "#10b98122" : "none",
              border: "none", color: checklistOpen ? "#10b981" : "#64748b",
              cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "2px 4px",
              borderRadius: 4, transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color="#10b981"; }}
            onMouseLeave={e => { if (!checklistOpen) e.currentTarget.style.color="#64748b"; }}>
            ✚
          </button>
        )}
        {!t.done && (
          <button onClick={() => { setAiOpen(o => !o); if (!aiResponse && !aiOpen) askTaskAI(); }}
            title="Get AI help with this task"
            style={{
              background: aiOpen ? "#f59e0b22" : "none",
              border: "none", color: aiOpen ? "#f59e0b" : "#64748b",
              cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "2px 4px",
              borderRadius: 4, transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.color="#f59e0b"; }}
            onMouseLeave={e => { if (!aiOpen) e.currentTarget.style.color="#64748b"; }}>
            ✦
          </button>
        )}
        <button onClick={() => onDelete(t.id)} style={{
          background: "none", border: "none", color: "#475569", cursor: "pointer",
          fontSize: 16, lineHeight: 1, padding: "1px 4px",
        }}
        onMouseEnter={e => e.target.style.color="#ef4444"}
        onMouseLeave={e => e.target.style.color="#475569"}>×</button>
      </div>
    </div>

    {/* Sub-tasks panel */}
    {((t.subTasks || []).length > 0 || addingSubTask) && !t.done && (
      <div style={{ marginTop: 4, paddingLeft: 54, paddingRight: 8 }}>
        {(t.subTasks || []).map(s => (
          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
            <button onClick={() => toggleSubTask(s.id)} style={{
              width: 16, height: 16, borderRadius: 3, flexShrink: 0, padding: 0, cursor: "pointer",
              border: `2px solid ${s.done ? "#10b981" : "#334155"}`,
              background: s.done ? "#10b981" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {s.done && <svg width="8" height="8" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
            </button>
            <span style={{ flex: 1, fontSize: 12, color: s.done ? "#475569" : "#94a3b8", textDecoration: s.done ? "line-through" : "none" }}>{s.text}</span>
            <button onClick={() => deleteSubTask(s.id)} style={{ background: "none", border: "none", color: "#334155", cursor: "pointer", fontSize: 13, padding: "0 2px" }}
              onMouseEnter={e => e.target.style.color="#ef4444"} onMouseLeave={e => e.target.style.color="#334155"}>×</button>
          </div>
        ))}
        {addingSubTask && (
          <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input ref={subTaskInputRef} value={subTaskInput} onChange={e => setSubTaskInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") addSubTask(); if (e.key === "Escape") { setAddingSubTask(false); setSubTaskInput(""); } }}
              placeholder="Sub-task..." style={{ ...inp, flex: 1, fontSize: 12, padding: "5px 8px" }} />
            <button onClick={addSubTask} style={{ background: "#f59e0b", border: "none", borderRadius: 6, color: "#000", cursor: "pointer", padding: "5px 10px", fontSize: 13, fontFamily: "inherit" }}>+</button>
            <button onClick={() => { setAddingSubTask(false); setSubTaskInput(""); }}
              style={{ background: "#1e293b", border: "none", borderRadius: 6, color: "#64748b", cursor: "pointer", padding: "5px 8px", fontSize: 12, fontFamily: "inherit" }}>✕</button>
          </div>
        )}
        {allDonePrompt && (
          <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 6, background: "#10b98115", border: "1px solid #10b98133" }}>
            <div style={{ fontSize: 11, color: "#10b981", marginBottom: 6 }}>All sub-tasks done — mark the main task complete?</div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => { onToggle(t.id); setAllDonePrompt(false); }} style={{ ...btn("#10b981", "#000"), fontSize: 11, padding: "4px 12px" }}>Yes ✓</button>
              <button onClick={() => setAllDonePrompt(false)} style={{ background: "#1e293b", border: "none", borderRadius: 6, color: "#64748b", fontSize: 11, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit" }}>Not yet</button>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Shopping checklist panel */}
    {checklistOpen && t.tag === "shopping" && !t.done && (
      <div style={{
        marginTop: 6, padding: "10px 12px", borderRadius: 8,
        background: "#080f1e", border: "1px solid #10b98133",
        borderLeft: "3px solid #10b981",
      }}>
        <div style={{ fontSize: 10, color: "#10b981", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>
          🛒 SHOPPING LIST {(t.items || []).length > 0 && `· ${(t.items || []).filter(i => i.done).length}/${(t.items || []).length} done`}
        </div>
        {(t.items || []).map(it => (
          <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <button onClick={() => toggleItem(it.id)} style={{
              width: 16, height: 16, borderRadius: 3, flexShrink: 0, padding: 0, cursor: "pointer",
              border: `2px solid ${it.done ? "#10b981" : "#334155"}`,
              background: it.done ? "#10b981" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {it.done && <svg width="8" height="8" viewBox="0 0 10 10"><path d="M2 5L4.5 7.5L8 3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" /></svg>}
            </button>
            <span style={{ flex: 1, fontSize: 12, color: it.done ? "#475569" : "#cbd5e1", textDecoration: it.done ? "line-through" : "none" }}>{it.text}</span>
            <button onClick={() => deleteItem(it.id)} style={{ background: "none", border: "none", color: "#475569", cursor: "pointer", fontSize: 13, padding: "0 2px" }}
              onMouseEnter={e => e.target.style.color="#ef4444"} onMouseLeave={e => e.target.style.color="#475569"}>×</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input ref={checklistInputRef} value={checklistInput} onChange={e => setChecklistInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addItem()}
            placeholder="Add item..." style={{ ...inp, flex: 1, fontSize: 12, padding: "5px 8px" }} />
          <button onClick={addItem} style={{
            background: "#10b981", border: "none", borderRadius: 6, color: "#fff",
            cursor: "pointer", padding: "5px 10px", fontSize: 13, fontFamily: "inherit",
          }}>+</button>
        </div>
      </div>
    )}

    {/* AI help panel — expands inline */}
    {aiOpen && (
      <div style={{
        marginTop: 8, padding: "12px 14px", borderRadius: 8,
        background: "#080f1e", border: "1px solid #f59e0b33",
        borderLeft: "3px solid #f59e0b",
      }}>
        <div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>
          ✦ AI COACH
        </div>
        {aiLoading ? (
          <div style={{ fontSize: 12, color: "#475569" }}>Thinking about your task...</div>
        ) : (
          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {aiResponse}
          </div>
        )}
        {!aiLoading && aiResponse && (
          <button onClick={() => { setAiOpen(false); setAiResponse(""); }}
            style={{ marginTop: 8, background: "none", border: "none", color: "#334155", fontSize: 10, cursor: "pointer", fontFamily: "inherit" }}>
            ✕ Close
          </button>
        )}
      </div>
    )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SETTINGS VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function SettingsView({ notifPermission, setNotifPermission, userName, setUserName, userId, analysisPrefs, setAnalysisPrefs, userMode, setUserMode, userSchedule = {}, setUserSchedule, userTagline = "", setUserTagline, futureMePhoto = "", setFutureMePhoto, motivationPhotos = [], setMotivationPhotos }) {
  const saved = JSON.parse(localStorage.getItem('alicanb-reminders') || '{}');
  const [eveningTime, setEveningTime] = useState(saved.evening || "21:30");
  const [streakTime, setStreakTime] = useState(saved.streakTime || "20:00");
  const [eveningOn, setEveningOn] = useState(!!saved.evening);
  const [streakOn, setStreakOn] = useState(saved.streak !== false);
  const [nameInput, setNameInput] = useState(userName || "");
  const [taglineInput, setTaglineInput] = useState(userTagline || "");
  const [saved2, setSaved2] = useState(false);

  // Schedule fields
  const [wakeTime, setWakeTime] = useState(userSchedule.wakeTime || "04:00");
  const [workStart, setWorkStart] = useState(userSchedule.workStart || "08:00");
  const [workEnd, setWorkEnd] = useState(userSchedule.workEnd || "18:00");
  const [kidsEnd, setKidsEnd] = useState(userSchedule.kidsEnd || "20:30");
  const [sleepTime, setSleepTime] = useState(userSchedule.sleepTime || "22:00");
  const [exerciseDays, setExerciseDays] = useState(userSchedule.exerciseDays || "3x");
  const [fixedCommitments, setFixedCommitments] = useState(userSchedule.fixedCommitments || "");

  async function saveScheduleField(field, value) {
    const updated = { ...userSchedule, wakeTime, workStart, workEnd, kidsEnd, sleepTime, exerciseDays, fixedCommitments, [field]: value };
    setUserSchedule(updated);
    await save("user-schedule", updated, userId);
  }

  async function enableNotifications() {
    const granted = await requestNotificationPermission();
    setNotifPermission(granted ? 'granted' : 'denied');
  }

  // Downscale an uploaded image to keep the saved base64 small.
  function fileToBase64Resized(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const max = 400;
          const scale = Math.min(1, max / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Future Me photo
  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64Resized(file);
    setFutureMePhoto && setFutureMePhoto(base64);
    await save("future-me-photo", base64, userId);
  }

  async function removePhoto() {
    setFutureMePhoto && setFutureMePhoto("");
    await save("future-me-photo", "", userId);
  }

  // Motivation photos (kids / partner / anyone you're doing this for) — max 3
  function setMotivationLocal(updated) {
    setMotivationPhotos && setMotivationPhotos(updated);
  }
  async function persistMotivation(updated) {
    setMotivationLocal(updated);
    await save("motivation-photos", updated, userId);
  }
  function addMotivationSlot() {
    if (motivationPhotos.length >= 3) return;
    persistMotivation([...motivationPhotos, { id: uid(), photo: "", name: "", line: "" }]);
  }
  function removeMotivation(id) {
    persistMotivation(motivationPhotos.filter(m => m.id !== id));
  }
  async function handleMotivationUpload(id, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64Resized(file);
    persistMotivation(motivationPhotos.map(m => m.id === id ? { ...m, photo: base64 } : m));
  }
  // Update name/line locally on each keystroke; persist on blur to avoid re-saving base64 repeatedly.
  function setMotivationField(id, field, value) {
    setMotivationLocal(motivationPhotos.map(m => m.id === id ? { ...m, [field]: value } : m));
  }
  function saveMotivation() {
    save("motivation-photos", motivationPhotos, userId);
  }

  function saveSettings() {
    const reminders = {};
    if (eveningOn) reminders.evening = eveningTime;
    reminders.streak = streakOn;
    if (streakOn) reminders.streakTime = streakTime;
    localStorage.setItem('alicanb-reminders', JSON.stringify(reminders));
    if (nameInput.trim()) {
      setUserName(nameInput.trim());
      save("username", nameInput.trim(), userId);
    }
    setUserTagline(taglineInput);
    save("user-tagline", taglineInput, userId);
    setSaved2(true);
    setTimeout(() => setSaved2(false), 2000);
  }

  return (
    <div style={{ maxWidth: 600, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Settings</h2>
        <p style={{ margin: 0, color: "#475569", fontSize: 13 }}>Reminders, preferences, and account.</p>
      </div>

      {/* Notification permission */}
      {notifPermission !== 'granted' && (
        <div style={{ ...card, marginBottom: 16, borderColor: "#f59e0b44" }}>
          <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>🔔 ENABLE REMINDERS</div>
          <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>
            Allow notifications so alicanb can remind you for your evening ritual and protect your habit streaks.
          </p>
          {notifPermission === 'denied' ? (
            <p style={{ fontSize: 12, color: "#ef4444" }}>Notifications blocked. Please enable them in your browser settings.</p>
          ) : (
            <button onClick={enableNotifications} style={btn("#f59e0b", "#000")}>Enable Notifications</button>
          )}
        </div>
      )}

      {/* Evening ritual reminder */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>🌙 Evening Ritual Reminder</div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Reminds you to reflect on today and plan tomorrow</div>
          </div>
          <button onClick={() => setEveningOn(e => !e)} style={{
            background: eveningOn ? "#10b981" : "#1e293b",
            border: "none", borderRadius: 20, padding: "4px 14px",
            color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
          }}>{eveningOn ? "ON" : "OFF"}</button>
        </div>
        {eveningOn && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>Remind me at:</span>
            <input type="time" value={eveningTime} onChange={e => setEveningTime(e.target.value)}
              style={{ ...inp, width: "auto", colorScheme: "dark" }} />
          </div>
        )}
      </div>

      {/* Streak protection */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>🔥 Streak Protection</div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Alerts you when habit streaks are at risk</div>
          </div>
          <button onClick={() => setStreakOn(s => !s)} style={{
            background: streakOn ? "#ef4444" : "#1e293b",
            border: "none", borderRadius: 20, padding: "4px 14px",
            color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
          }}>{streakOn ? "ON" : "OFF"}</button>
        </div>
        {streakOn && (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#64748b" }}>Check at:</span>
            <input type="time" value={streakTime} onChange={e => setStreakTime(e.target.value)}
              style={{ ...inp, width: "auto", colorScheme: "dark" }} />
          </div>
        )}
      </div>

      {/* Name */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>YOUR NAME</div>
        <input value={nameInput} onChange={e => setNameInput(e.target.value)}
          placeholder="What should we call you?" style={inp} />
      </div>

      {/* Tagline */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>YOUR PERSONAL TAGLINE</div>
        <input value={taglineInput} onChange={e => setTaglineInput(e.target.value)}
          placeholder="Your personal motto or inspiration sentence..." style={inp} />
      </div>

      {/* Future Me */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #f59e0b" }}>
        <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>✦ FUTURE ME</div>
        <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 14px", lineHeight: 1.6 }}>
          Upload a photo of yourself. Every morning your future self will send you a message based on how you are living today.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {futureMePhoto && (
            <img src={futureMePhoto} alt="Future you" style={{
              width: 100, height: 100, borderRadius: "50%", objectFit: "cover",
              border: "2px solid #f59e0b", flexShrink: 0,
            }} />
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ ...btn("#f59e0b", "#000"), display: "inline-block", textAlign: "center" }}>
              📷 {futureMePhoto ? "Change Photo" : "Upload My Photo"}
              <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
            </label>
            {futureMePhoto && (
              <button onClick={removePhoto} style={{
                background: "none", border: "none", color: "#ef4444", fontSize: 12,
                cursor: "pointer", fontFamily: "inherit", textAlign: "left", padding: 0,
              }}>Remove photo</button>
            )}
          </div>
        </div>
      </div>

      {/* My Motivation */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #f472b6" }}>
        <div style={{ fontSize: 11, color: "#f472b6", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>♥ MY MOTIVATION</div>
        <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 14px", lineHeight: 1.6 }}>
          Add photos of people who motivate you — your kids, partner, or anyone whose future depends on your actions today.
        </p>
        {motivationPhotos.map(m => (
          <div key={m.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid #1e293b" }}>
            <label style={{ cursor: "pointer", flexShrink: 0 }}>
              {m.photo ? (
                <img src={m.photo} alt={m.name || "Motivation"} style={{
                  width: 64, height: 64, borderRadius: "50%", objectFit: "cover", border: "2px solid #f472b6", display: "block",
                }} />
              ) : (
                <div style={{
                  width: 64, height: 64, borderRadius: "50%", border: "2px dashed #f472b688",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#f472b688",
                }}>📷</div>
              )}
              <input type="file" accept="image/*" onChange={e => handleMotivationUpload(m.id, e)} style={{ display: "none" }} />
            </label>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              <input value={m.name || ""} onChange={e => setMotivationField(m.id, "name", e.target.value)} onBlur={saveMotivation}
                placeholder="Who is this? (e.g. David, Sara)" style={{ ...inp, fontSize: 12 }} />
              <input value={m.line || ""} onChange={e => setMotivationField(m.id, "line", e.target.value)} onBlur={saveMotivation}
                placeholder="What do they need from you?" style={{ ...inp, fontSize: 12 }} />
              <button onClick={() => removeMotivation(m.id)} style={{
                background: "none", border: "none", color: "#ef4444", fontSize: 11,
                cursor: "pointer", fontFamily: "inherit", textAlign: "left", padding: 0, marginTop: 2,
              }}>Remove</button>
            </div>
          </div>
        ))}
        {motivationPhotos.length < 3 && (
          <button onClick={addMotivationSlot} style={{ ...btn("#f472b6", "#000"), fontSize: 12, padding: "8px 16px" }}>
            + Add person ({motivationPhotos.length}/3)
          </button>
        )}
      </div>

      {/* AI Analysis Preferences */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #8b5cf6" }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 14 }}>✦ AI ANALYSIS PREFERENCES</div>

        {/* Tone */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>TONE</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { id: "tough", label: "Tough Coach", desc: "Direct, no softening" },
              { id: "balanced", label: "Balanced", desc: "Honest but constructive" },
              { id: "encouraging", label: "Encouraging", desc: "Focuses on wins" },
            ].map(opt => {
              const active = (analysisPrefs?.tone || "balanced") === opt.id;
              return (
                <button key={opt.id} onClick={async () => {
                  const updated = { ...analysisPrefs, tone: opt.id };
                  setAnalysisPrefs(updated);
                  await save("analysis-prefs", updated, userId);
                }} style={{
                  flex: 1, minWidth: 90, padding: "8px 10px", borderRadius: 8, border: `1px solid ${active ? "#8b5cf6" : "#1e293b"}`,
                  background: active ? "#8b5cf622" : "#1e293b", color: active ? "#a78bfa" : "#475569",
                  cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: active ? 700 : 500,
                  transition: "all 0.15s", textAlign: "left",
                }}>
                  <div>{opt.label}</div>
                  <div style={{ fontSize: 10, color: active ? "#7c3aed" : "#334155", marginTop: 2, fontWeight: 400 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Emphasis */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>EMPHASIS</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { id: "whole", label: "Whole Life", desc: "All 8 areas equally" },
              { id: "business", label: "Business & Financial", desc: "Work and money goals" },
              { id: "health", label: "Health & Habits", desc: "Energy, habits, body" },
              { id: "family", label: "Family & Personal", desc: "Kids, home, growth" },
            ].map(opt => {
              const active = (analysisPrefs?.emphasis || "whole") === opt.id;
              return (
                <button key={opt.id} onClick={async () => {
                  const updated = { ...analysisPrefs, emphasis: opt.id };
                  setAnalysisPrefs(updated);
                  await save("analysis-prefs", updated, userId);
                }} style={{
                  flex: 1, minWidth: 110, padding: "8px 10px", borderRadius: 8, border: `1px solid ${active ? "#8b5cf6" : "#1e293b"}`,
                  background: active ? "#8b5cf622" : "#1e293b", color: active ? "#a78bfa" : "#475569",
                  cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: active ? 700 : 500,
                  transition: "all 0.15s", textAlign: "left",
                }}>
                  <div>{opt.label}</div>
                  <div style={{ fontSize: 10, color: active ? "#7c3aed" : "#334155", marginTop: 2, fontWeight: 400 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Format */}
        <div>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>FORMAT</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { id: "full", label: "Full Analysis", desc: "Complete breakdown" },
              { id: "quick", label: "Quick Summary", desc: "5 bullets, 2 min read" },
              { id: "morning", label: "Morning Catch-up", desc: "Light review of yesterday" },
            ].map(opt => {
              const active = (analysisPrefs?.format || "full") === opt.id;
              return (
                <button key={opt.id} onClick={async () => {
                  const updated = { ...analysisPrefs, format: opt.id };
                  setAnalysisPrefs(updated);
                  await save("analysis-prefs", updated, userId);
                }} style={{
                  flex: 1, minWidth: 90, padding: "8px 10px", borderRadius: 8, border: `1px solid ${active ? "#8b5cf6" : "#1e293b"}`,
                  background: active ? "#8b5cf622" : "#1e293b", color: active ? "#a78bfa" : "#475569",
                  cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: active ? 700 : 500,
                  transition: "all 0.15s", textAlign: "left",
                }}>
                  <div>{opt.label}</div>
                  <div style={{ fontSize: 10, color: active ? "#7c3aed" : "#334155", marginTop: 2, fontWeight: 400 }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* User Mode */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #10b981" }}>
        <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 14 }}>YOUR CURRENT MODE</div>
        {[
          { id: "growth", icon: "🌱", title: "GROWTH MODE", desc: "Things are stable. I am focused on building better habits and making steady progress." },
          { id: "fight", icon: "⚔️", title: "FIGHT MODE", desc: "I am under pressure. I need to execute at my highest level and make every hour count." },
          { id: "support", icon: "🤝", title: "SUPPORT MODE", desc: "I am struggling. I need encouragement, gentle accountability, and help getting back on track." },
        ].map(m => {
          const active = (userMode || "growth") === m.id;
          return (
            <button key={m.id} onClick={async () => { setUserMode(m.id); await save("user-mode", m.id, userId); }} style={{
              width: "100%", marginBottom: 8, padding: "12px 14px", borderRadius: 10,
              border: `1px solid ${active ? "#10b981" : "#1e293b"}`,
              background: active ? "#10b98115" : "#1e293b",
              cursor: "pointer", fontFamily: "inherit", textAlign: "left", transition: "all 0.15s",
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: active ? "#10b981" : "#64748b", marginBottom: 4 }}>
                {m.icon} {m.title}
              </div>
              <div style={{ fontSize: 12, color: active ? "#6ee7b7" : "#475569", lineHeight: 1.5 }}>{m.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Daily Schedule */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #8b5cf6" }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 14 }}>🗓 MY DAILY SCHEDULE</div>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 14 }}>Used by the AI planner to build your schedule around your life.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[
            { label: "Wake up", state: wakeTime, set: setWakeTime, field: "wakeTime" },
            { label: "Work starts", state: workStart, set: setWorkStart, field: "workStart" },
            { label: "Home from work", state: workEnd, set: setWorkEnd, field: "workEnd" },
            { label: "Kids done / free time", state: kidsEnd, set: setKidsEnd, field: "kidsEnd" },
            { label: "Sleep by", state: sleepTime, set: setSleepTime, field: "sleepTime" },
          ].map(({ label, state, set, field }) => (
            <div key={field}>
              <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 4 }}>{label.toUpperCase()}</div>
              <input type="time" value={state} onChange={e => set(e.target.value)}
                onBlur={e => saveScheduleField(field, e.target.value)}
                style={{ ...inp, width: "100%", colorScheme: "dark", fontSize: 13 }} />
            </div>
          ))}
          <div>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 4 }}>EXERCISE</div>
            <select value={exerciseDays} onChange={e => { setExerciseDays(e.target.value); saveScheduleField("exerciseDays", e.target.value); }}
              style={{ ...inp, width: "100%", fontSize: 13 }}>
              {["2x", "3x", "4x", "5x", "daily"].map(v => <option key={v} value={v}>{v} per week</option>)}
            </select>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 4 }}>FIXED DAILY COMMITMENTS (optional)</div>
          <textarea
            value={fixedCommitments}
            onChange={e => setFixedCommitments(e.target.value)}
            onBlur={e => saveScheduleField("fixedCommitments", e.target.value)}
            placeholder={"e.g. school pickup at 15:30, prayer at 07:00"}
            rows={3}
            style={{ ...inp, resize: "none", fontFamily: "inherit", fontSize: 12 }}
          />
        </div>
      </div>

      {/* Save */}
      <button onClick={saveSettings} style={{ ...btn("#f59e0b", "#000"), width: "100%", padding: "12px", fontSize: 14 }}>
        {saved2 ? "✓ Saved!" : "Save Settings"}
      </button>

      {notifPermission === 'granted' && (
        <div style={{ fontSize: 11, color: "#334155", textAlign: "center", marginTop: 12 }}>
          Notifications enabled ✓ — reminders will fire when the app is open in your browser
        </div>
      )}
    </div>
  );
}

// ── Notifications ────────────────────────────────────────────────────────────
async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function checkAndFireReminders(habitLogs, habits, dayPlans, goals = []) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const reminders = JSON.parse(localStorage.getItem('alicanb-reminders') || '{}');
  const now = new Date();
  const timeStr = now.toTimeString().slice(0, 5); // "HH:MM"
  const todayK = now.toISOString().slice(0, 10);
  const lastFired = JSON.parse(localStorage.getItem('alicanb-last-fired') || '{}');

  // Evening ritual reminder
  if (reminders.evening && timeStr === reminders.evening) {
    const firedKey = `evening-${todayK}`;
    if (!lastFired[firedKey]) {
      const plan = dayPlans[todayK] || {};
      const ritualDone = plan.reflection && plan.reflection.length > 10;
      if (!ritualDone) {
        new Notification('alicanb — Evening Ritual', {
          body: 'Time to reflect on today and plan tomorrow. 5 minutes tonight = a better day tomorrow. 🌙',
          icon: '/icon-192.png',
          tag: 'evening-ritual',
        });
        lastFired[firedKey] = true;
        localStorage.setItem('alicanb-last-fired', JSON.stringify(lastFired));
      }
    }
  }

  // Stagnant goal nudge — Saturday 10:00
  if (timeStr === "10:00" && now.getDay() === 6) {
    const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const ninetyDaysFromNow = new Date(); ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);
    const wk = weekKey();
    const nudgeStorage = JSON.parse(localStorage.getItem('alicanb-nudges') || '{}');
    const nudgeGoals = goals.filter(g => {
      if (g.progress >= 100) return false;
      if (!g.deadline) return false;
      const deadlineDate = new Date(g.deadline + "T00:00:00");
      if (deadlineDate > ninetyDaysFromNow) return false;
      const recentEntries = (g.progressLog || []).filter(e => new Date(e.date) >= sevenDaysAgo);
      return recentEntries.length === 0;
    });
    nudgeGoals.forEach(goal => {
      const nudgeKey = `next-action-nudge-${goal.id}-${wk}`;
      if (!nudgeStorage[nudgeKey]) {
        new Notification('alicanb — Goal Needs Attention', {
          body: `◎ ${goal.text.slice(0, 60)} hasn't moved this week. What's your next step?`,
          icon: '/icon-192.png',
          tag: `next-action-${goal.id}`,
        });
        nudgeStorage[nudgeKey] = true;
      }
    });
    if (nudgeGoals.length > 0) localStorage.setItem('alicanb-nudges', JSON.stringify(nudgeStorage));
  }

  // Streak protection — fires at 8pm if habits not logged
  if (reminders.streak !== false) {
    const streakTime = reminders.streakTime || "20:00";
    if (timeStr === streakTime) {
      const firedKey = `streak-${todayK}`;
      if (!lastFired[firedKey]) {
        const atRiskHabits = habits.filter(h => {
          const streak = Object.keys(habitLogs[h.id] || {}).length;
          const doneToday = !!(habitLogs[h.id] || {})[todayK];
          return streak >= 3 && !doneToday;
        });
        if (atRiskHabits.length > 0) {
          const names = atRiskHabits.slice(0, 2).map(h => h.name).join(', ');
          new Notification('alicanb — Streak at Risk! 🔥', {
            body: `Don't break your streak! ${names} ${atRiskHabits.length > 2 ? `+${atRiskHabits.length - 2} more` : ''} still not done today.`,
            icon: '/icon-192.png',
            tag: 'streak-protection',
          });
          lastFired[firedKey] = true;
          localStorage.setItem('alicanb-last-fired', JSON.stringify(lastFired));
        }
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// WEEK
// ═══════════════════════════════════════════════════════════════════════════════
function weekKey() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  d.setDate(diff);
  return d.toISOString().slice(0, 10);
}

function WeekView({ weekPlans, setWeekPlans, userId }) {
  const key = weekKey();
  const plan = weekPlans[key] || {};

  async function update(patch) {
    const u = { ...weekPlans, [key]: { ...plan, ...patch } };
    setWeekPlans(u);
    await save("weekplans", u, userId);
  }

  const score = plan.score ?? 5;

  function scoreColor(v) {
    if (v >= 9) return "#10b981";
    if (v >= 7) return "#84cc16";
    if (v >= 5) return "#f59e0b";
    if (v >= 3) return "#f97316";
    return "#ef4444";
  }

  const weekStart = new Date(key + "T12:00:00");
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekLabel = weekStart.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " – " + weekEnd.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#f1f5f9" }}>Week</h2>
        <p style={{ margin: 0, color: "#f59e0b", fontSize: 13, fontStyle: "italic", fontWeight: 600 }}>{weekLabel}</p>
      </div>

      {/* Plan this week */}
      <div style={{ ...card, marginBottom: 12, borderLeft: "3px solid #3b82f6" }}>
        <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>📋 PLAN THIS WEEK</div>

        {[
          { field: "topThree", label: "3 MOST IMPORTANT THINGS THIS WEEK", placeholder: "1.\n2.\n3.", rows: 4 },
          { field: "goalsForward", label: "GOALS I AM PUSHING FORWARD THIS WEEK", placeholder: "Which goals get real progress this week?", rows: 3 },
          { field: "familyKids", label: "FAMILY & KIDS THIS WEEK", placeholder: "What do you want to do with the kids? Any special moments planned?", rows: 2 },
          { field: "mealPrep", label: "MEAL PREP PLAN FOR THE WEEK", placeholder: "What will you batch cook? What ingredients do you need?", rows: 2 },
          { field: "prepInAdvance", label: "ANYTHING TO PREPARE IN ADVANCE", placeholder: "Calls to schedule, materials to gather, decisions to make ahead...", rows: 2 },
        ].map(({ field, label, placeholder, rows }) => (
          <div key={field} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
            <textarea value={plan[field] || ""} onChange={e => update({ [field]: e.target.value })}
              placeholder={placeholder} rows={rows}
              style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
          </div>
        ))}
      </div>

      {/* Weekly reflection */}
      <div style={{ ...card, borderLeft: "3px solid #8b5cf6" }}>
        <div style={{ fontSize: 11, color: "#8b5cf6", fontWeight: 700, letterSpacing: "0.1em", marginBottom: 14 }}>🌙 WEEKLY REFLECTION</div>

        {[
          { field: "accomplished", label: "WHAT DID I ACTUALLY ACCOMPLISH?", placeholder: "Be honest. Not what you planned — what actually happened?", rows: 3 },
          { field: "didntHappen", label: "WHAT DIDN'T HAPPEN AND WHY?", placeholder: "What slipped? What did you avoid? What surprised you?", rows: 3 },
          { field: "timeTrap", label: "BIGGEST TIME TRAP THIS WEEK?", placeholder: "What ate your time without producing results?", rows: 2 },
          { field: "carryForward", label: "WHAT DO I CARRY FORWARD?", placeholder: "One insight, one commitment, one change for next week.", rows: 2 },
        ].map(({ field, label, placeholder, rows }) => (
          <div key={field} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
            <textarea value={plan[field] || ""} onChange={e => update({ [field]: e.target.value })}
              placeholder={placeholder} rows={rows}
              style={{ ...inp, resize: "none", fontFamily: "inherit" }} />
          </div>
        ))}

        {/* Honest score */}
        <div>
          <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 10 }}>HONEST SCORE FOR THIS WEEK</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(v => (
                <button key={v} onClick={() => update({ score: v })} style={{
                  width: 28, height: 28, borderRadius: 6, border: "none", cursor: "pointer",
                  fontFamily: "inherit", fontSize: 11, fontWeight: 700,
                  background: v <= score ? scoreColor(score) + "33" : "#1e293b",
                  color: v <= score ? scoreColor(score) : "#334155",
                  outline: v === score ? `2px solid ${scoreColor(score)}` : "none",
                  transition: "all 0.15s",
                }}>{v}</button>
              ))}
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(score), minWidth: 40 }}>
              {score}/10
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
            {score >= 9 ? "Outstanding week. You showed up fully." :
             score >= 7 ? "Solid week. Keep the momentum." :
             score >= 5 ? "Average week. One change could shift it." :
             score >= 3 ? "Tough week. What can you fix?" :
             "Hard week. Be honest about why."}
          </div>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { id: "Goals", icon: "◎" },
  { id: "Habits", icon: "🔄" },
  { id: "Day", icon: "☀" },
  { id: "Week", icon: "📅" },
  { id: "Tasks", icon: "✓" },
  { id: "Time Log", icon: "⏱" },
  { id: "Analyze", icon: "✦" },
  { id: "Settings", icon: "⚙" },
];

// Future-self phrase shown on hover for each tab's floating presence.
const TAB_FUTURE_PHRASES = {
  Goals: "Your future self is watching 👁",
  Habits: "Every habit builds future you 🔥",
  Week: "Future you is planned week by week 📅",
  Tasks: "Each task done is a gift to future you ✓",
  "Time Log": "Time logged = future you informed ⏱",
  Analyze: "Future you thanks your honesty ✦",
  Settings: "Configure the path to future you ⚙",
};

// Small circular avatar with a cartoon speech bubble below it — used in the nav bar.
// alwaysShow keeps the bubble permanently visible (Future Me); otherwise it shows on hover.
function NavAvatar({ src, size = 36, bubbleText, signature, border = "2px solid #f59e0b", alwaysShow = false }) {
  const [bubbleVisible, setBubbleVisible] = useState(false);
  if (!src) return null;
  const text = (bubbleText || "").trim();
  const show = alwaysShow || bubbleVisible;
  return (
    <div style={{ position: "relative", flexShrink: 0 }}
      onMouseEnter={() => setBubbleVisible(true)} onMouseLeave={() => setBubbleVisible(false)}>
      <img src={src} alt="avatar" style={{
        width: size, height: size, minWidth: size, borderRadius: "50%",
        objectFit: "cover", objectPosition: "center top", overflow: "hidden",
        border, display: "block", flexShrink: 0,
      }} />
      {(text || signature) && (
        <div style={{
          position: "absolute", top: size + 12, left: 0,
          background: "#f1f5f9", color: "#0f172a", borderRadius: 12,
          padding: "8px 12px", fontSize: 11, lineHeight: 1.5,
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)", maxWidth: 200, width: "max-content",
          zIndex: 100, opacity: show ? 1 : 0, transition: "opacity 0.2s ease",
          pointerEvents: "none",
        }}>
          {/* tail pointing up toward the photo */}
          <div style={{
            position: "absolute", top: -8, left: Math.max(8, size / 2 - 8),
            width: 0, height: 0,
            borderLeft: "8px solid transparent", borderRight: "8px solid transparent",
            borderBottom: "8px solid #f1f5f9",
          }} />
          {text}
          {signature && <div style={{ color: "#f59e0b", fontWeight: 700, marginTop: 4 }}>{signature}</div>}
        </div>
      )}
    </div>
  );
}

// Floating future-self presence pinned to the top-right of the content area.
function TabFuturePresence({ src, tab }) {
  const phrase = TAB_FUTURE_PHRASES[tab];
  if (!src || !phrase) return null;
  return (
    <div style={{ position: "fixed", top: 72, right: 16, zIndex: 50 }}>
      <img src={src} alt="Future you" style={{
        width: 44, height: 44, minWidth: 44, borderRadius: "50%",
        objectFit: "cover", objectPosition: "center top", overflow: "hidden",
        border: "2px solid #f59e0b", display: "block",
      }} />
      {/* cartoon speech bubble to the LEFT of the photo — always visible */}
      <div style={{
        position: "absolute", right: 52, top: "50%", transform: "translateY(-50%)",
        background: "#f1f5f9", color: "#0f172a", borderRadius: 12,
        padding: "8px 12px", fontSize: 11, lineHeight: 1.4,
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)", maxWidth: 180, width: "max-content",
        zIndex: 100, pointerEvents: "none",
      }}>
        {phrase}
        {/* tail pointing right toward the photo */}
        <div style={{
          position: "absolute", right: -8, top: "50%", transform: "translateY(-50%)",
          width: 0, height: 0,
          borderTop: "6px solid transparent", borderBottom: "6px solid transparent",
          borderLeft: "8px solid #f1f5f9",
        }} />
      </div>
    </div>
  );
}

export default function LifeOS({ session }) {
  const userId = session?.user?.id;
  const [tab, setTab] = useState("Day");
  const [goals, setGoals] = useState([]);
  const [dayPlans, setDayPlans] = useState({});
  const [timeLogs, setTimeLogs] = useState({});
  const [customActivities, setCustomActivities] = useState([]);
  const [userName, setUserName] = useState("");
  const [habits, setHabits] = useState([]);
  const [habitLogs, setHabitLogs] = useState({});
  const [tasks, setTasks] = useState([]);
  const [weekPlans, setWeekPlans] = useState({});
  const [analysisPrefs, setAnalysisPrefs] = useState({ tone: "balanced", emphasis: "whole", format: "full" });
  const [userQuestions, setUserQuestions] = useState([]);
  const [userMode, setUserMode] = useState("growth");
  const [userTagline, setUserTagline] = useState("");
  const [futureMePhoto, setFutureMePhoto] = useState("");
  const [motivationPhotos, setMotivationPhotos] = useState([]);
  const [userSchedule, setUserSchedule] = useState({});
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [ready, setReady] = useState(false);

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  // Check reminders every minute
  useEffect(() => {
    const interval = setInterval(() => {
      if (ready) checkAndFireReminders(habitLogs, habits, dayPlans, goals);
    }, 60000);
    return () => clearInterval(interval);
  }, [ready, habitLogs, habits, dayPlans, goals]);

  function getGreeting() {
    const h = new Date().getHours();
    const name = userName ? `, ${userName}` : "";
    if (h < 12) return `Good morning${name} ☀`;
    if (h < 17) return `Good afternoon${name} 🌤`;
    if (h < 21) return `Good evening${name} 🌆`;
    return `Good night${name} 🌙`;
  }

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      load("goals", [], userId),
      load("dayplans", {}, userId),
      load("timelogs", {}, userId),
      load("custom-activities", [], userId),
      load("username", "", userId),
      load("habits", [], userId),
      load("habitlogs", {}, userId),
      load("tasks", [], userId),
      load("weekplans", {}, userId),
      load("analysis-prefs", { tone: "balanced", emphasis: "whole", format: "full" }, userId),
      load("daily-questions", [], userId),
      load("user-mode", "growth", userId),
      load("recurring-tasks", [], userId),
      load("user-schedule", {}, userId),
      load("user-tagline", "", userId),
      load("future-me-photo", "", userId),
      load("motivation-photos", [], userId),
    ]).then(async ([g, d, t, ca, un, h, hl, tk, wp, ap, dq, um, rt, us, utl, fmp, mp]) => {
      setGoals(g); setDayPlans(d); setTimeLogs(t); setCustomActivities(ca);
      setUserName(un || ""); setHabits(h); setHabitLogs(hl); setWeekPlans(wp); setAnalysisPrefs(ap); setUserQuestions(dq); setUserMode(um || "growth");
      setUserSchedule(us || {});
      setUserTagline(utl || "");
      setFutureMePhoto(fmp || "");
      setMotivationPhotos(Array.isArray(mp) ? mp : []);
      setRecurringTasks(rt);
      const generatedTasks = await checkAndGenerateRecurringTasks(rt, tk, userId);
      setTasks(generatedTasks);
      setReady(true);
    });
  }, [userId]);

  return (
    <div style={{ minHeight: "100vh", background: "#020617", color: "#f1f5f9", fontFamily: "'IBM Plex Mono', 'Fira Code', monospace" }}>
      {/* Nav */}
      <div style={{ borderBottom: "1px solid #1e293b", padding: "0 12px", position: "sticky", top: 0, background: "#020617", zIndex: 10 }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          {/* Logo + greeting row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, paddingBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: "#94a3b8" }}>al</span>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#f59e0b" }}>i</span>
              <span style={{ fontWeight: 800, fontSize: 15, color: "#f1f5f9" }}>can</span>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#f59e0b" }}>b</span>
            </div>
            {(futureMePhoto || motivationPhotos.length > 0) && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, marginLeft: 8 }}>
                {futureMePhoto && <NavAvatar src={futureMePhoto} size={36} />}
                {motivationPhotos.filter(m => m.photo).map(m => (
                  <NavAvatar key={m.id} src={m.photo} size={30} border="2px solid #f59e0b88"
                    bubbleText={`${m.name || "Someone"}${m.line ? " — " + m.line : ""}`} />
                ))}
              </div>
            )}
            <div style={{ textAlign: "center", flex: 1, padding: "0 8px" }}>
              <div style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>{getGreeting()}</div>
              {userTagline && (
                <div style={{
                  fontSize: 10, color: "#f59e0b", fontStyle: "italic",
                  maxWidth: 400, margin: "2px auto 0",
                  whiteSpace: "normal", wordWrap: "break-word", lineHeight: 1.4,
                }}>
                  {userTagline}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {/* Notification bell */}
              <button onClick={async () => {
                if (notifPermission !== 'granted') {
                  const granted = await requestNotificationPermission();
                  setNotifPermission(granted ? 'granted' : 'denied');
                } else {
                  setTab('Settings');
                }
              }} title={notifPermission === 'granted' ? 'Reminder settings' : 'Enable reminders'}
              style={{
                background: "none", border: "1px solid #1e293b", borderRadius: 8,
                padding: "4px 8px", color: notifPermission === 'granted' ? "#f59e0b" : "#334155",
                cursor: "pointer", fontFamily: "inherit", fontSize: 13,
              }}>🔔</button>
              <button onClick={handleSignOut} style={{
                background: "none", border: "1px solid #1e293b", borderRadius: 8,
                padding: "4px 8px", color: "#475569", fontSize: 11,
                cursor: "pointer", fontFamily: "inherit",
              }} onMouseEnter={e => e.target.style.color="#ef4444"}
                 onMouseLeave={e => e.target.style.color="#475569"}>
                out
              </button>
            </div>
          </div>
          {/* Tabs row - full width, all visible */}
          <div style={{ display: "flex", gap: 0, paddingBottom: 4 }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex: 1, minWidth: 0,
                background: tab === t.id ? "#0f172a" : "transparent",
                border: "none", borderBottom: tab === t.id ? "2px solid #f59e0b" : "2px solid transparent",
                padding: "6px 2px",
                color: tab === t.id ? "#f59e0b" : "#94a3b8",
                fontSize: 10, fontWeight: tab === t.id ? 700 : 500,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                whiteSpace: "nowrap", textAlign: "center", overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                <span style={{ display: "block" }}>{t.icon}</span>
                <span style={{ fontSize: 9 }}>{t.id}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "24px 20px 80px" }}>
        {ready && <TabFuturePresence src={futureMePhoto} tab={tab} />}
        {!ready ? (
          <div style={{ textAlign: "center", color: "#1e293b", paddingTop: 80, fontSize: 13 }}>Loading...</div>
        ) : (
          <>
            {tab === "Goals" && <GoalsView goals={goals} setGoals={setGoals} userId={userId} dayPlans={dayPlans} setDayPlans={setDayPlans} tasks={tasks} setTasks={setTasks} />}
            {tab === "Habits" && <HabitsView habits={habits} setHabits={setHabits} habitLogs={habitLogs} setHabitLogs={setHabitLogs} goals={goals} userId={userId} />}
            {tab === "Day" && <TodayView goals={goals} dayPlans={dayPlans} setDayPlans={setDayPlans} userId={userId} userName={userName} setUserName={setUserName} tasks={tasks} setTasks={setTasks} userQuestions={userQuestions} setUserQuestions={setUserQuestions} userMode={userMode} setTab={setTab} habits={habits} habitLogs={habitLogs} setHabitLogs={setHabitLogs} userSchedule={userSchedule} userTagline={userTagline} futureMePhoto={futureMePhoto} />}
            {tab === "Week" && <WeekView weekPlans={weekPlans} setWeekPlans={setWeekPlans} userId={userId} />}
            {tab === "Time Log" && <TimeLogView timeLogs={timeLogs} setTimeLogs={setTimeLogs} customActivities={customActivities} setCustomActivities={setCustomActivities} userId={userId} />}
            {tab === "Tasks" && <TasksView tasks={tasks} setTasks={setTasks} userId={userId} recurringTasks={recurringTasks} setRecurringTasks={setRecurringTasks} />}
            {tab === "Analyze" && <AnalyzeView goals={goals} dayPlans={dayPlans} setDayPlans={setDayPlans} timeLogs={timeLogs} habits={habits} habitLogs={habitLogs} tasks={tasks} userId={userId} analysisPrefs={analysisPrefs} setTab={setTab} userQuestions={userQuestions} userMode={userMode} futureMePhoto={futureMePhoto} />}
            {tab === "Settings" && <SettingsView notifPermission={notifPermission} setNotifPermission={setNotifPermission} userName={userName} setUserName={setUserName} userId={userId} analysisPrefs={analysisPrefs} setAnalysisPrefs={setAnalysisPrefs} userMode={userMode} setUserMode={setUserMode} userSchedule={userSchedule} setUserSchedule={setUserSchedule} userTagline={userTagline} setUserTagline={setUserTagline} futureMePhoto={futureMePhoto} setFutureMePhoto={setFutureMePhoto} motivationPhotos={motivationPhotos} setMotivationPhotos={setMotivationPhotos} />}
          </>
        )}
      </div>
    </div>
  );
}
